/**
 * The layout pass: SwiftUI's propose / report / place protocol over the
 * renderer's element tree (docs/ops-protocol.md, "Layout engine semantics").
 *
 * `measure(node, proposal)` reports the size a node wants for a proposal and
 * is memoized per (node, proposal, container); `place(node, proposal, x, y)`
 * records the frame and places the children using the same memoized plans.
 * Context (font, line limit, text alignment, enclosing stack axis, list row)
 * flows down the tree like SwiftUI's environment and is a pure function of
 * the node's ancestors, so it is not part of the memo key; the container
 * (`containerRelativeFrame`) is, as a scroll view's viewport is its own
 * proposal and differs between the widths a stack tries it at.
 *
 * Phase 9: a `LayoutPass` lives across commits. The renderer tells it which
 * nodes changed (`invalidate`, `remove`); a changed node, its ancestors (their
 * sizes may change) and its descendants (their context may change) lose their
 * memo and their last placement. `place` returns at once for a node that was
 * placed before with the same proposal at the same origin and was not
 * invalidated: its whole subtree's frames, chrome and text are still those of
 * the last pass. `run` reports the ids it touched so the DOM pass only visits
 * those.
 */

import { FONT_FAMILIES, defaultFont, makeFont, resolveFont, textHeight, textStyleSize } from './typography';
import { wrapRuns, wrapText, type TextRunInput } from './text';
import { formatPickerDate, formatPickerTime, sliderFraction } from './controls';
import { videoSize } from './video';
import {
  CHART_LEGEND_GAP,
  CHART_LEGEND_GLYPH,
  annotationFrame,
  chartSize,
  layoutChart,
  legendGlyphSize,
  unitRect,
  type ChartAxisInput,
  type ChartGeometry,
  type ChartItemPosition,
  type ChartLegendInput,
} from './chart';
import type {
  EdgeInsets,
  LayoutChildSizes,
  LayoutEnvironment,
  LayoutNode,
  LayoutRecord,
  LayoutResult,
  Proposal,
  Rect,
  ResolvedFont,
  Size,
  TextAlignment,
  TextLayout,
  TextMeasurer,
} from './types';

type Axis = 'horizontal' | 'vertical';

export interface Context {
  font: ResolvedFont;
  lineLimit: number | null;
  textAlignment: TextAlignment;
  /** The enclosing stack's axis (through `styled` wrappers), for spacer/divider. */
  stackAxis: Axis | null;
  /** True for a list row element (through `styled` wrappers): navlinks reserve a chevron. */
  listRow: boolean;
  /**
   * Height of the bar covering the bottom of the screen above the subtree
   * (a `tabview`'s bar plus the bottom safe area), 0 outside tabs. Scrolling
   * views that reach the screen bottom inset their content by it. Inside a
   * `navscreen` with a bottom toolbar or search field, the room they take
   * (up from `bottomEdge`).
   */
  bottomBar: number;
  /** Where the bar `bottomBar` measures ends: the screen's bottom unless set (a `navscreen`'s bottom). */
  bottomEdge?: number | undefined;
  /**
   * iOS 26: inside a `navscreen` whose content is not itself a list or scroll
   * view, its bar (the band from the screen's top to `top`, `x` to `x + width`):
   * a vertical list or scroll view whose top edge touches it across the
   * screen runs up under it (`scrollEdgeGrowth`).
   */
  barAbove?: { top: number; height: number; x: number; width: number } | undefined;
  /** Phase 6 `imageScale`: symbol images measure this fraction of the line height (1 by default). */
  imageScale: number;
  /** Phase 7: true inside the sidebar column of a `navsplit` (a selected `navlink` row drops its chevron there). */
  splitSidebar: boolean;
  /** Inside either column of a `navsplit` (on a Mac the window's unified title bar spans every column). */
  splitColumn?: boolean;
  /** Inside a leading or trailing item of a navigation bar (a segmented picker there is the bar's own capsule). */
  navBarItem?: boolean;
  /** Inside an item of a bottom toolbar (a bordered or prominent button there takes the bar's sizes). */
  bottomBarItem?: boolean;
  /** Rows of a `sidebar`-style list (iPadOS): its links have no chevron. */
  sidebarList?: boolean;
  /**
   * Inside a list row (the whole subtree): a `Label` lays out in iOS's icon
   * column, its icon centred 28 from the card's edge and its title 40 past
   * the content edge, so titles and separators line up across rows whatever
   * the symbols' widths, and with the edit-mode controls.
   */
  labelColumn?: boolean;
  /**
   * The Mac idiom, inside a list row's whole subtree: symbols measure the
   * font's point size (not its leading) and a `Label`'s title sits 6 after
   * its icon, as the Mac app draws them.
   */
  listCell?: boolean;
  /** A row of a Mac's grouped form (macOS 26): a toggle is a switch at the trailing end, a slider's and a text field's label lead their control, a link has its chevron. */
  groupedRow?: boolean;
  /**
   * The Mac idiom: a tab view's toolbar row at the top of the window. The
   * screens of a tab's navigation stack draw their bar as that row: no
   * title (the tab picker is there), the back button and the items at the
   * row's ends.
   */
  tabToolbar?: Rect;
  /**
   * The Mac idiom: the navigation stack that fills a sheet. Its root screen
   * draws the sheet's 40 title bar (the title leading, 13 bold) and puts
   * its text bar items (a cancel or confirm action) in the sheet's bottom
   * bar, `actions`, when the sheet has one.
   */
  macSheet?: { actions: Rect | null };
  /**
   * The Mac idiom: a row of a form in the columns style. A control whose
   * label went to the label column (a stepper's, a date picker's, a
   * picker's) draws without it, at the row's leading edge.
   */
  formColumn?: boolean;
  /**
   * Phase 8: the y (screen coordinates) where content clears the bars above
   * it: the top safe area at the root, the bottom of a `navscreen`'s bar (and
   * large title / search field) inside one. `.ignoresSafeArea` grows up to 0
   * from here.
   */
  safeTop: number;
  /**
   * The inset a sheet gives the bar of the navigation stack that fills it
   * (its grabber row, or iOS 26's room above a sheet's bar), as the status
   * bar does at the root. Only the stack's own screens see it.
   */
  barInset?: number;
  /**
   * The room a split view's sidebar toggle takes at the leading edge of the
   * bar row of the column it sits in (that column's stack's screens shift
   * their back button and leading items past it).
   */
  barLeading?: number;
  /**
   * The room a Mac split view's sidebar toggle takes at the trailing end of
   * the sidebar's bar row (the toggle sits at the panel's trailing edge):
   * the sidebar's screens end their trailing items before it.
   */
  barTrailing?: number;
  /**
   * A Mac split view whose sidebar could not fit its own bar items after
   * the window buttons: the detail column's screens show the overflow menu
   * (`»`, chrome `overflow`) of them at the trailing end of their bar, as
   * the Mac puts them in its toolbar's overflow.
   */
  barOverflow?: boolean;
  /**
   * `.environment(\.defaultMinListRowHeight, _)` set above (a `styled`
   * node's `defaultMinListRowHeight`): the lists below space their rows at
   * least this tall unless they set their own `minRowHeight`.
   */
  minListRowHeight?: number;
  /**
   * Phase 12: the size of the nearest container (`containerRelativeFrame`):
   * the screen at the root, a scroll view's viewport inside one.
   */
  container: Size;
  /**
   * The lower bound of the enclosing `.lineLimit(2...4)` range (`lineLimitMin`):
   * a vertical text field is at least this many lines tall. Absent for 1.
   */
  minLines?: number;
  /** `.scrollDisabled(true)` above: a `texteditor` is as tall as its lines. */
  scrollDisabled?: boolean;
}

// Constants from the spec.
const DEFAULT_SPACING = 8;
const DEFAULT_SPACER_MIN = 8;
/** Phase 5: the square an `image` whose catalog name is unknown measures. */
const UNKNOWN_IMAGE_SIDE = 44;
const SHAPE_DEFAULT = 10;
// iOS 26 metrics (measured in the iOS 26.2 simulator, Examples/Fidelity): a
// bordered button is 34 tall (50 at `.controlSize(.large)`), a switch 63 × 28.
const BORDERED_PAD_V = 6;
const BORDERED_PAD_H = 12;
const BORDERED_MIN_HEIGHT = 34;
/**
 * A bordered button's padding, least height and (smaller sizes) label text
 * style per `controlSize` (props); `regular` is the constants above.
 */
const BORDERED_SIZES: Record<string, { padV: number; padH: number; minHeight: number; textStyle?: string }> = {
  mini: { padV: 3, padH: 8, minHeight: 24, textStyle: 'footnote' },
  small: { padV: 4, padH: 10, minHeight: 28, textStyle: 'subheadline' },
  large: { padV: 14, padH: 20, minHeight: 50 },
  extraLarge: { padV: 18, padH: 24, minHeight: 58 },
};
const SWITCH_WIDTH = 63;
const SWITCH_HEIGHT = 28;
const TOGGLE_GAP = 8;
const TEXTFIELD_DEFAULT_WIDTH = 200;
const TEXTFIELD_HEIGHT_ROUNDED = 34;
const TEXTFIELD_HEIGHT_PLAIN = 22;
/** A vertical `roundedBorder` field's text inset: 7 + the 1px border across, (34 - 22) / 2 down. */
const TEXTFIELD_ROUNDED_INSET = { horizontal: 8, vertical: 6 };
const NAV_BAR_HEIGHT = 44;
// iOS 26: under an inline title the content starts 10 below the bar's 44
// row (measured in the iOS 26.2 simulator); in a sheet it does not.
const NAV_BAR_INLINE_BOTTOM = 10;
// iOS 26: the large title's row is 62 (52 before).
const LARGE_TITLE_HEIGHT = 62;
// iOS 26: `.largeTitle` toolbar items take the title's place 10 below the
// bar's 44 row, in a row exactly their height: the content starts right
// under them (measured in the iOS 26 simulator, Native's LargeTitleTests).
const LARGE_TITLE_ITEM_TOP = 10;
const SHEET_GRABBER_ROW = 20;
const SHEET_GRABBER: Size = { width: 36, height: 5 };
const SHEET_GRABBER_TOP = 8;
// iOS 26: a sheet at a partial detent (medium, a height or a fraction)
// floats 8 from the screen's sides and 15 above its bottom; the medium card
// is 433 tall. Its grabber shows only when it has more than one detent; its
// content then starts 14 below the card's top.
const SHEET_FLOAT_INSET_H = 8;
const SHEET_FLOAT_INSET_BOTTOM = 15;
const SHEET_MEDIUM_HEIGHT = 433;
// iOS 26: without a grabber a sheet's content (and a navigation bar's row)
// starts 16 below its top.
const SHEET_CONTENT_TOP = 16;
// iPadOS: in a regular width a sheet is a form sheet, a card centered on the
// screen at UIKit's form-sheet size (clamped to the screen less a margin),
// whatever its detents; a full-screen cover stays full screen.
const FORM_SHEET: Size = { width: 540, height: 620 };
const FORM_SHEET_MARGIN = 20;
const LIST_INSET = 16;
const LIST_CELL_PADDING = 16;
// iOS 26 (measured in the iOS 26.2 simulator, Examples/Fidelity, in thirds
// of a point): rows are at least 52 tall with 15 above and below their text;
// a section's header is a sentence-case headline-sized line that starts 8
// into the 35.67 gap between cards and ends 10 above its card; a footer is
// 7.33 below its card.
const LIST_ROW_VPAD = 15;
// A row holding just a toggle or a stepper has 10 above and below it (so
// both come out 52 tall), a segmented picker 14.5 (61).
const LIST_ROW_CONTROL_VPAD = 10;
const LIST_ROW_SEGMENTED_VPAD = 14.5;
const LIST_ROW_MIN_HEIGHT = 52;
// iPadOS sidebar rows (`sidebar` style): at least 44 tall, 11 above and
// below their text.
const LIST_SIDEBAR_ROW_MIN_HEIGHT = 44;
const LIST_SIDEBAR_ROW_VPAD = 11;
const LIST_TOP = 18;
const LIST_BOTTOM = 20;
const LIST_HEADER_LIFT = 8;
const LIST_HEADER_GAP = 10;
const LIST_FOOTER_GAP = 22 / 3;
const LIST_SECTION_GAP = 107 / 3;
// Below a section's footer the gap to the next section is 11 shorter.
const LIST_SECTION_GAP_AFTER_FOOTER = 74 / 3;
const LIST_CHEVRON_WIDTH = 20;
/**
 * A `disclosuregroup` among a list's rows is an outline (`outlineRows`
 * below): each level's rows start this much further in than the level
 * above. SwiftUI's value, as yet unmeasured against the simulator.
 */
const LIST_OUTLINE_INDENT = 20;
// Phase 4.
// iOS 26: the tab bar is a 62-tall glass capsule floating 21 above the bottom
// edge (8 when the tab view ends above the screen bottom), its items in equal
// slots inside a 4 inset. It is as wide as its tabs (268/3 each, measured in
// the 26.2 simulator: two tabs make a 186.67 capsule), centered, and at most
// the width less 21 either side (four tabs fill that on an iPhone).
const TAB_BAR_HEIGHT = 62;
const TAB_BAR_ITEM_WIDTH = 268 / 3;
const TAB_BAR_MARGIN_H = 21;
const TAB_BAR_MARGIN_BOTTOM = 21;
const TAB_BAR_MARGIN_BOTTOM_INSET = 8;
const TAB_BAR_ITEM_INSET = 4;
const PICKER_SEGMENTED_HEIGHT = 32;
const PICKER_SEGMENT_PAD = 20;
const PICKER_SEGMENT_FONT_SIZE = 13;
// iOS 26, a segmented picker in a navigation bar item (measured in the
// simulator, Examples/Fidelity `topbarsegmented`, with the item's shared
// glass hidden): the control is a 44-tall glass capsule itself, no track;
// the selected segment a 36-tall gray capsule inset 4 (no shadow); labels
// 15 regular; segments of equal width, the widest label and 21 either side.
// The capsule keeps 4 clear of the bar's other capsules beyond their usual
// 12 (it sits 16 from the next one).
const BAR_SEGMENTED_FONT_SIZE = 15;
const BAR_SEGMENT_PAD = 42;
const BAR_SEGMENTED_INSET = 4;
const BAR_SEGMENTED_MARGIN = 4;
const PICKER_MENU_ROW_HEIGHT = 44;
const PICKER_MENU_HEIGHT = 34;
/** `chevron.up.chevron.down` glyph width plus the gap to the value text. */
const PICKER_MENU_CHEVRON_WIDTH = 20;
const PICKER_MENU_GAP = 12;
// Phase 6.
const GAUGE_BAR_HEIGHT = 4;
const GAUGE_LABEL_GAP = 4;
/** A ProgressView's label to its bar, and its bar to its current value label. */
const PROGRESS_LABEL_GAP = 8;
const PROGRESS_VALUE_GAP = 6;
const GAUGE_DEFAULT_WIDTH = 100;
/** A circular gauge's side (`accessoryCircular` / `accessoryCircularCapacity`). */
const GAUGE_RING_SIZE = 58;
/**
 * iOS 26 (Examples/Fidelity's `spacing` screen): what a circular gauge
 * proposes the view in its middle, in a 24pt semibold font (Backyard Birds'
 * water drop is a 24pt symbol there); an `accessoryCircular` gauge's label
 * sits in the gap at the bottom of its arc, in the box below.
 */
const GAUGE_RING_CENTER: Size = { width: 40.67, height: 29 };
const GAUGE_RING_FONT = { size: 24, weight: 'semibold' };
const GAUGE_ARC_LABEL: Size = { width: 36, height: 17.67 };
const GRID_ITEM_DEFAULT_MIN = 10;
// Phase 6 (docs/phase6/chrome.md).
const PICKER_INLINE_ROW_HEIGHT = 44;
/** Checkmark glyph (20) plus its gap to the option title. */
const PICKER_INLINE_CHECK_WIDTH = 28;
/** Space between a list row's content and its trailing badge. */
const BADGE_GAP = 8;
const NAV_ITEM_SPACING = 16;
const NAV_ITEM_MARGIN = 16;
// iOS 26: the back button is a 44pt glass circle 16 from the leading edge,
// and the bar's leading and trailing items sit in glass capsules 44 tall,
// each item in a slot: a symbol the 44 circle, anything else (text, a
// label) its width and 12 either side, at least 44. Adjacent items of one
// placement share a capsule (a run; a `ToolbarSpacer` of that placement ends
// one), their slots 12 apart and the capsule a point past the end slots; the
// capsules, the back button and the More button sit 12 apart (measured in
// the iOS 26 simulator, Examples/Fidelity `topbar*`).
const NAV_GLASS_SIZE = 44;
const NAV_GLASS_PAD_H = 12;
const NAV_GROUP_SPACING = 12;
const NAV_RUN_GAP = 12;
const NAV_RUN_PAD = 1;
// The Mac idiom (docs/notes/macos.md; `LayoutEnvironment.platform` is
// `macos`), measured on macOS 26: the window's title bar stands in for the
// navigation bar, with no background or rule of its own (the content's
// color runs up under it). A 52 toolbar holds the back button, the bar items
// and the title, leading-aligned; a window with nothing in its bar but the
// title has the compact 32 one, the title leading-aligned after the window
// buttons. There are no large titles. The items are Liquid Glass, as on iOS
// 26 but smaller: 36 tall, centered in the bar; an icon a 36 slot,
// adjacent items in one capsule (the back button in the leading one), a
// text item a capsule of its own at its width and 10 either side;
// capsules 8 apart and 8 from the window's edge. On the screen's own window
// (the top of the screen, when the safe area carries a title bar) the three
// window buttons take the first 96 of the toolbar; the title is 12 after
// whatever is before it.
const MAC_TITLE_BAR_UNIFIED = 52;
const MAC_TITLE_BAR_PLAIN = 32;
const MAC_BAR_ITEM_SIZE = 36;
const MAC_BAR_ITEM_PAD_H = 10;
const MAC_BAR_RUN_GAP = 0;
/** A capsule of several slots: 3 past its end slots. */
const MAC_BAR_RUN_PAD = 3;
/** Between capsules, and between the last one and the window's edge. */
const MAC_BAR_GROUP_GAP = 8;
/** The window buttons' room: the first item's slot starts 96 in; a title is 12 after the last thing before it. */
const MAC_WINDOW_BUTTONS_ROOM = 96;
const MAC_TITLE_GAP = 12;
/** A compact title bar's title: 84 in, after the window buttons (9 in, 14 across, 9 apart) and 15. */
const MAC_COMPACT_TITLE_X = 84;
/** A Mac bar's trailing items end 8 before the window's edge. */
const MAC_BAR_MARGIN_TRAILING = 8;
// A Mac tab view's bar (macOS 26) is a glass capsule centered in the
// window's 52 toolbar: 36 tall, 8 from the top, its tabs equal segments (the
// widest title and 11 either side) inside a 4 inset, the selected one a 24
// gray pill (6 from the capsule's top and bottom).
const MAC_TAB_ROW_HEIGHT = 52;
const MAC_TAB_BAR_HEIGHT = 36;
/** A tab picker's segment: the widest title (13 regular) and 11 either side. */
const MAC_TAB_ITEM_PAD_H = 11;
const MAC_TAB_ITEM_INSET = 4;
const MAC_TAB_ITEM_HEIGHT = 24;
const MAC_TAB_BAR_MARGIN_H = 20;
// Mac controls (macOS 14, a first pass): a bordered button is 22 tall with 10
// either side of its label, and every `automatic` button outside a list row
// and a bar is bordered; a checkbox is 14 square, its label 6 after it, and a
// toggle in a list row (a grouped form's) is the small switch, 26 × 15; a
// pop-up button is 22 tall (24 in a row), its value in a bordered button
// with 8 either side.
// macOS 26: a push button is a capsule, 24 tall with 13 either side of its
// label (a large one 28, an extra large one 36), on a translucent gray (styles.css).
const MAC_BORDERED_SIZES: Record<string, { padV: number; padH: number; minHeight: number; textStyle?: string }> = {
  regular: { padV: 4, padH: 13, minHeight: 24 },
  mini: { padV: 1, padH: 7, minHeight: 16, textStyle: 'caption' },
  small: { padV: 2, padH: 10, minHeight: 20, textStyle: 'subheadline' },
  large: { padV: 6, padH: 16, minHeight: 28 },
  extraLarge: { padV: 10, padH: 20, minHeight: 36 },
};
const MAC_CHECKBOX_SIZE = 14;
const MAC_CHECKBOX_GAP = 6;
const MAC_PICKER_MENU_HEIGHT = 24;
// A Mac's segmented control is 22 tall with 13-point regular text, its
// label (which iOS drops) 10 before it.
const MAC_SEGMENTED_HEIGHT = 24;
/** A segment's room beside its label (macOS 26: the widest label's, every segment). */
const MAC_SEGMENT_PAD = 18;
const MAC_SEGMENTED_FONT_SIZE = 13;
const MAC_SEGMENTED_LABEL_GAP = 10;
const MAC_PICKER_MENU_ROW_HEIGHT = 24;
/** A pop-up's room beyond its widest option: 13 before it, the 8 chevrons 13 after it and 9 from the end. */
const MAC_PICKER_POPUP_EXTRA = 43;
// Mac lists (measured in the Mac app, docs/notes/macos.md): no cards, the
// rows full width with 16 beside their content, at least 24 tall (28 in a
// sidebar) with 4 (6) above and below their content, a bordered button's 3;
// every row has a separator under it. A section's header (11 bold) starts
// 7 below the top, 5 into the 22 between sections after the first; a rule
// across the whole list sits 8 under it and the rows 10 under the rule. A
// footer (11) sits 8 under the last row, with a rule 5 under it. A
// sidebar's rows inset 10; no chevrons. The sidebar column is 220 wide.
const MAC_LIST_ROW_MIN_HEIGHT = 24;
const MAC_LIST_SIDEBAR_ROW_MIN_HEIGHT = 32;
const MAC_LIST_ROW_VPAD = 4;
const MAC_LIST_SIDEBAR_ROW_VPAD = 6;
const MAC_LIST_BUTTON_ROW_VPAD = 3;
const MAC_LIST_CELL_PADDING = 16;
/** A sidebar's rows (macOS 26): their content 12 in from the selection's edges, a Label's title 10 after its icon, one line. */
const MAC_LIST_SIDEBAR_CELL_PADDING = 12;
/** A sidebar's sections: 13 apart, a header 2 under the top (4 under rows), 4 in from the selection's edge and 4 over its rows, no rule. */
const MAC_LIST_SIDEBAR_SECTION_GAP = 13;
const MAC_LIST_SIDEBAR_HEADER_TOP = 2;
const MAC_LIST_SIDEBAR_HEADER_GAP = 4;
const MAC_LIST_SIDEBAR_HEADER_INSET = 4;
const MAC_LIST_SIDEBAR_BADGE_FONT_SIZE = 11;
const MAC_LIST_SIDEBAR_LABEL_GAP = 10;
/** A Mac list row's Label: the icon (a symbol at the font's point size) 6 before the title. */
const MAC_LIST_LABEL_GAP = 6;
const MAC_LIST_INSET_SIDEBAR = 10;
/** A Mac list's first row starts 10 under the top (the Showcase on macOS 26); a header at the top sits 4 higher (Fidelity's). */
const MAC_LIST_TOP = 10;
const MAC_LIST_FIRST_HEADER_LIFT = 4;
const MAC_LIST_BOTTOM = 8;
const MAC_LIST_SECTION_GAP = 22;
const MAC_LIST_HEADER_TOP = 4;
/** Under a Mac header: 8 to its rule and the rule; the first section's rows start 10 further down. */
const MAC_LIST_HEADER_GAP = 9;
const MAC_LIST_FIRST_ROWS_GAP = 10;
const MAC_LIST_FOOTER_GAP = 8;
// A Mac's Form (measured in the Mac app): the columns style, with no
// insets of its own and centered vertically in the room it gets. The labels
// (13) right-aligned in a column as wide as the widest, from the leading
// edge; the controls in a column 10 after it that runs to the trailing
// edge; a section header and a footer (13 regular, the label color) in the
// controls column; 8 under a header, rows 7 apart, sections touching, a
// header 4 under the rows before it (right under a footer), a footer 2
// under the last row.
const MAC_FORM_LABEL_GAP = 10;
const MAC_FORM_HEADER_GAP = 8;
const MAC_FORM_HEADER_TOP = 4;
const MAC_FORM_ROW_GAP = 7;
const MAC_FORM_FOOTER_GAP = 2;
// A Mac's grouped form (`.formStyle(.grouped)`, macOS 26): rounded cards
// (11) at most 704 wide, centered, at least 20 from the sides; 36 rows (a
// button's 44) with their content 10 in, a 1 separator between rows 10 in
// from either side; a header (13 semibold) 10 over its card, 20 below the
// top; sections 30 apart; a footer 10 under its card.
const MAC_GROUPED_MAX_WIDTH = 704;
const MAC_GROUPED_MARGIN = 20;
const MAC_GROUPED_CELL_PADDING = 10;
const MAC_GROUPED_ROW_HEIGHT = 36;
const MAC_GROUPED_ROW_VPAD = 7;
const MAC_GROUPED_BUTTON_ROW_VPAD = 11;
const MAC_GROUPED_SEPARATOR = 1;
const MAC_GROUPED_TOP = 20;
const MAC_GROUPED_HEADER_GAP = 10;
const MAC_GROUPED_SECTION_GAP = 30;
const MAC_GROUPED_FOOTER_GAP = 10;
/** In a grouped row: the switch, 36 × 18; a slider's track the trailing 55% of the row; a link's chevron 6 wide, 2 in from the content's edge. */
const MAC_GROUPED_SWITCH_WIDTH = 36;
const MAC_GROUPED_SWITCH_HEIGHT = 18;
const MAC_GROUPED_SLIDER_FRACTION = 0.55;
const MAC_GROUPED_CHEVRON_WIDTH = 8;
const MAC_GROUPED_LABEL_GAP = 10;
/** The row kinds whose label goes in a Mac form's label column (a stack with one is shifted to it). */
const MAC_FORM_LABELED_KINDS = ['textfield', 'picker', 'stepper', 'datepicker', 'slider'];
/** Under a Mac footer: 5 to its rule, the rule. */
const MAC_LIST_FOOTER_BOTTOM = 6;
// A Mac's split view (macOS 26): the sidebar is a floating panel, 140 wide,
// 8 from the window's leading, top and bottom edges, rounded 22, on the
// toolbar's glass fill with its own top 44 the window's color (the window
// buttons and, 6 from its trailing edge, the sidebar toggle, a bare 36
// slot); the detail column starts at its trailing edge. Its title is not
// shown. A list in it is a sidebar: 32 rows inset 10 in the panel, the
// selected one a gray rounded fill; a search field over it, 28 tall, 9 under
// the bar and 10 above the rows. A screen not at the window's leading edge
// starts its toolbar 10 in.
const MAC_NAVSPLIT_SIDEBAR_WIDTH = 140;
/** The width of the strip astride the sidebar panel's trailing edge that drags the sidebar's width. */
const MAC_NAVSPLIT_DIVIDER_GRIP = 8;
const MAC_NAVSPLIT_SIDEBAR_INSET = 8;
const MAC_NAVSPLIT_TOGGLE_INSET = 6;
/**
 * A Mac sidebar's own bar items (macOS 26, measured in Examples/Fidelity
 * `macfolders`, a 320 sidebar): plain symbols with no glass, each in a slot
 * at least 36 wide (its width and 6 either side when wider), 10 apart, the
 * last 12 before the collapse button. Those that would reach the window
 * buttons go into an overflow menu (`»`), as the Mac puts them in its
 * toolbar's overflow.
 */
const MAC_SIDEBAR_ITEM_PAD_H = 6;
const MAC_SIDEBAR_ITEM_GAP = 10;
const MAC_SIDEBAR_TOGGLE_GAP = 12;
/** A Mac bar menu's indicator (SwiftUI's `.menuIndicator` on a toolbar menu): a chevron 6 wide, 7 after the label. */
const MAC_BAR_MENU_INDICATOR = 13;
const MAC_SIDEBAR_SEARCH_ABOVE = 9;
const MAC_SIDEBAR_SEARCH_HEIGHT = 28;
const MAC_SIDEBAR_SEARCH_BELOW = 10;
const MAC_SIDEBAR_SEARCH_MARGIN_H = 10;
const MAC_BAR_LEADING_MARGIN = 10;
// A Mac's search field is in the toolbar, at its trailing end (macOS 26): a
// 36 glass capsule like the bar's items, 325 wide (less when the bar is
// narrow), 8 from the edge; elsewhere a field is 22 tall. A sheet is a
// window-modal panel that drops from the title bar, 480 × 400 at most,
// centered, with no grabber.
const MAC_SEARCH_FIELD_WIDTH = 325;
const MAC_SEARCH_FIELD_HEIGHT = 22;
// A Mac's sheet (measured in the Mac app, macOS 26): a 470 panel centered in
// the window, its 48 title bar (the title 13 bold, 48 in) over the content at
// its ideal height, and a 60 bar at the bottom with the text actions (cancel,
// confirm) as push buttons at its trailing end, 16 in and 8 apart; at least
// 40 clear of the window's edges.
const MAC_SHEET_WIDTH = 470;
const MAC_SHEET_TITLE_BAR = 48;
const MAC_SHEET_TITLE_X = 48;
const MAC_SHEET_ACTIONS_BAR = 60;
const MAC_SHEET_ACTIONS_INSET = 16;
const MAC_SHEET_ACTIONS_GAP = 8;
const MAC_SHEET_MARGIN = 40;
// A Mac's text field is 22 tall whatever its style; a stepper's control is
// the 13 × 22 pair of arrows (up over down) 4 after the label; an alert is
// a 260 panel, its title and message centered 16 in, its buttons 22-tall
// push buttons 16 in, side by side 12 apart for two, stacked 8 apart otherwise.
const MAC_TEXTFIELD_HEIGHT = 22;
const MAC_STEPPER_WIDTH = 13;
const MAC_STEPPER_HEIGHT = 22;
const MAC_STEPPER_GAP = 4;
// A Mac's slider is a 4 track with a 20 round knob in a 20 row; a compact
// date picker's date and time are 22-tall bordered fields 6 apart with 6
// beside their text; a menu outside a bar or a list row is a pull-down
// button, 22 tall with 10 beside its label and the chevron's 16 after it.
const MAC_SLIDER_HEIGHT = 20;
const MAC_SLIDER_TRACK_HEIGHT = 4;
const MAC_SLIDER_THUMB = 20;
const MAC_DATEPICKER_HEIGHT = 22;
const MAC_DATEPICKER_CAPSULE_PAD_H = 6;
const MAC_DATEPICKER_CAPSULE_GAP = 6;
const MAC_PULLDOWN_HEIGHT = 22;
const MAC_PULLDOWN_PAD_H = 10;
const MAC_PULLDOWN_CHEVRON = 16;

/**
 * The children an item draws in the bar: a menu or context menu draws its
 * label (the first child) and lists the rest in its popover, so its items
 * (which a lazy menu only builds when it opens) do not make it wider or a
 * text item.
 */
function drawnChildren(node: LayoutNode): LayoutNode[] {
  return node.kind === 'menu' || node.kind === 'contextmenu' ? node.children.slice(0, 1) : node.children;
}

/** Whether any text is laid out in an item (a bottom bar pads text items as text). */
function hasText(item: LayoutNode): boolean {
  return item.kind === 'text' || drawnChildren(item).some(hasText);
}

/** A bar item that is only a symbol (an `image`, or a button, menu or link around one). */
function isSymbolItem(item: LayoutNode): boolean {
  const inner = unwrapStyled(item);
  if (inner.kind === 'image') return true;
  const drawn = drawnChildren(inner);
  return drawn.length === 1 && isSymbolItem(drawn[0] as LayoutNode);
}

/** A bar item's slot in its glass capsule (`glass` square for a symbol, else its width and `pad` either side, at least `glass`). */
function navSlot(item: LayoutNode, width: number, glass = NAV_GLASS_SIZE, pad = NAV_GLASS_PAD_H): number {
  if (isSegmentedItem(item)) return width + 2 * BAR_SEGMENTED_MARGIN;
  if (isSymbolItem(item) && width <= glass) return glass;
  return Math.max(glass, width + 2 * pad);
}
// iOS 26 (measured in the 26.2 simulator, Examples/Fidelity `bottombar*`): a
// bottom toolbar's items float on Liquid Glass in a 48 row 28 above the
// screen bottom (as the search field does) and 28 in from the sides.
// Consecutive items share a capsule: an icon takes a 50 slot, text its width
// and 18 either side, 6 between slots; a lone icon is a 48 circle. An item
// with no text (symbols, a symbol and a chevron, a custom view) is an icon
// at any width: at least 50, else its width and 8 either side, so a little
// more width gives it a little more room (not text's 18 a side). Alone in its
// group it is a circle only if it fits an icon's slot; wider, a capsule.
const BOTTOM_GLASS_HEIGHT = 48;
/** A `keyboard` toolbar's bar (the input accessory view): 44 across the screen, directly above the keyboard, its items 16 in. */
/** The keyboard bar (iOS 26's Notes): a 48 glass capsule, 10 in from the screen's sides, 10 above the keyboard. */
const KEYBOARD_BAR_HEIGHT = 48;
const KEYBOARD_BAR_MARGIN_H = 10;
const KEYBOARD_BAR_GAP = 10;
const BOTTOM_GLASS_MARGIN_H = 28;
const BOTTOM_ICON_SLOT = 50;
const BOTTOM_TEXT_PAD_H = 18;
const BOTTOM_ICON_PAD_H = 8;
const BOTTOM_ITEM_GAP = 6;
/** A fixed `ToolbarSpacer` between two bottom groups. */
const BOTTOM_FIXED_SPACER = 8;
// A bordered button in a bottom bar stays in its capsule, its slot 2 wider
// and its fill the slot less 10 (5 a side), 34 tall; a prominent or glass
// one leaves the capsule for a 48 circle of its own, 12 from the next
// capsule (measured in the iOS 26.2 simulator, Native BarButtonStyleTests).
const BOTTOM_BORDERED_GROWTH = 2;
const BOTTOM_BORDERED_INSET = 10;
const BOTTOM_BORDERED_HEIGHT = 34;
const BOTTOM_GROUP_GAP = 12;
// A `.toggleStyle(.button)` toggle in a bottom bar takes a slot 24 wider
// than a button's, its pill (drawn while on) the slot less 8 and 50 tall,
// on or off; its capsule is 60 tall about the same center (iOS 26.2,
// Native ToolbarToggleTests).
const BOTTOM_TOGGLE_GROWTH = 24;
const BOTTOM_TOGGLE_INSET = 8;
const BOTTOM_TOGGLE_HEIGHT = 50;
const BOTTOM_TOGGLE_GLASS_HEIGHT = 60;

/** A toggle drawn as a button (`.toggleStyle(.button)`). */
function isButtonToggle(node: LayoutNode): boolean {
  return node.kind === 'toggle' && str(node.props['style']) === 'button';
}

// A toggle of the automatic style in a bar is no switch: it takes a plain
// button's slot, and while on fills a pill under its label, 10 wider on
// each side and 36 tall, with the accent (iOS 26.2, Native
// ToolbarToggleTests: the pen's 42×36, the bar 230 wide on or off).
const BAR_TOGGLE_PAD_H = 10;
const BAR_TOGGLE_HEIGHT = 36;

/** A toggle the bar draws as its own (any style but `button` and `switch`). */
function isBarToggle(node: LayoutNode, ctx: Context): boolean {
  if (node.kind !== 'toggle' || !(ctx.bottomBarItem || ctx.navBarItem)) return false;
  const style = str(node.props['style']);
  return style !== 'button' && style !== 'switch';
}

/** A bottom bar item's slot in its capsule, from what it draws. */
function bottomSlot(node: LayoutNode, size: Size): number {
  if (!hasText(node)) return Math.max(BOTTOM_ICON_SLOT, size.width + 2 * BOTTOM_ICON_PAD_H);
  return size.width < NAV_GLASS_SIZE ? BOTTOM_ICON_SLOT : size.width + 2 * BOTTOM_TEXT_PAD_H;
}

/** How a bottom bar item's button is styled: bordered, prominent (or glass), a button toggle, a bar's own toggle, or none of them. */
function bottomBarStyle(item: LayoutNode): 'bordered' | 'prominent' | 'toggle' | 'barToggle' | null {
  const inner = unwrapStyled(item);
  if (isButtonToggle(inner)) return 'toggle';
  if (inner.kind === 'toggle' && str(inner.props['style']) !== 'switch') return 'barToggle';
  if (inner.kind !== 'button') return null;
  const style = str(inner.props['style']);
  if (style === 'bordered') return 'bordered';
  return style === 'borderedProminent' || style === 'glass' ? 'prominent' : null;
}
const SEARCH_FIELD_HEIGHT = 36;
const SEARCH_FIELD_MARGIN_H = 16;
// iOS 26: a navigation stack's search field is a 48-tall glass capsule
// floating 28 from the sides (measured in the simulator: x 28.67…364.33 on the
// Fidelity `search` screen and Food Truck's Donuts) and 28 above the screen
// bottom (8 above a view that ends higher); the content stops above it.
const SEARCH_BOTTOM_HEIGHT = 48;
const SEARCH_BOTTOM_MARGIN_H = 28;
const SEARCH_BOTTOM_MARGIN = 28;
const SEARCH_BOTTOM_MARGIN_INSET = 8;
const SEARCH_FIELD_DEFAULT_WIDTH = 200;
// iOS 26 (measured in the Simulator, Backyard Birds): in a tab view the
// search field sits under the title instead, a 44-tall capsule 16 from the
// sides, 1 below the title row (y 166 under a large title), the content 15
// below it: a 60 row.
const SEARCH_TOP_HEIGHT = 44;
const SEARCH_TOP_ABOVE = 1;
const SEARCH_TOP_BELOW = 15;
const SEARCH_TOP_ROW = SEARCH_TOP_ABOVE + SEARCH_TOP_HEIGHT + SEARCH_TOP_BELOW;
const IMAGE_SCALES: Readonly<Record<string, number>> = { small: 0.8, medium: 1, large: 1.25 };
// Phase 7 (docs/ops-protocol.md, "NavigationSplitView").
const NAVSPLIT_SIDEBAR_WIDTH = 320;
/** From this root width a window is regular (an iPad, a desktop browser), as `@swiftbrowser/react`'s size class. */
const REGULAR_WIDTH = 700;
/** Whether a screen is the regular size class (an iPad, a desktop window): a `sidebar` list then draws as one. */
export const isRegularWidth = (screen: { width: number }): boolean => screen.width >= REGULAR_WIDTH;
/**
 * From this root height a window is the regular vertical size class: below
 * it, as an iPhone on its side (at most 440 tall, less under Safari's bars),
 * it is compact, and UIKit shows no large titles. An iPad is regular either
 * way (744 tall at the least).
 */
const REGULAR_HEIGHT = 500;
/** The sidebar never takes more than this share of the split view's width. */
const NAVSPLIT_SIDEBAR_MAX_FRACTION = 0.4;
const NAVSPLIT_SEPARATOR_WIDTH = 1;
/** `.inspector`: the trailing column's default width, and at most this share of the width. */
const INSPECTOR_WIDTH = 320;
const INSPECTOR_MAX_FRACTION = 0.5;
const EPSILON = 1e-6;
// Phase 8 (docs/ops-protocol.md, "Phase 8: interaction": alerts and controls).
/** The segmented −/+ control of a `stepper`, trailing. */
const STEPPER_WIDTH = 94;
const STEPPER_HEIGHT = 32;
const STEPPER_GAP = 8;
/** A `slider` is 31 high (its label row, when present, sits above). */
// iOS 26: a 6pt track and a 38 × 24 pill knob in a 31 row (a form row
// holding one is 61: 31 + 2 × 15).
const SLIDER_HEIGHT = 31;
const SLIDER_TRACK_HEIGHT = 6;
const SLIDER_THUMB = 38;
const SLIDER_THUMB_HEIGHT = 24;
const SLIDER_DEFAULT_WIDTH = 200;
/** Gap between a min / max value label and the track. */
const SLIDER_LABEL_GAP = 8;
/** A compact `datepicker` is 34 high; its capsules pad the text by 11 and sit 8 apart. */
const DATEPICKER_HEIGHT = 34;
const DATEPICKER_CAPSULE_PAD_H = 11;
const DATEPICKER_CAPSULE_GAP = 8;
const DATEPICKER_GAP = 8;
/** A `texteditor` fills the proposal; 100 high when proposed nil. */
const TEXTEDITOR_DEFAULT_HEIGHT = 100;
/** UITextView's insets around a `texteditor`'s lines (`textContainerInset`, `lineFragmentPadding`; styles.css). */
const TEXTEDITOR_INSET = { horizontal: 5, vertical: 8 };
/**
 * `alert` style (iOS 26): a 320 wide card centered in the safe area, its
 * title and message leading-aligned 30 in, its buttons 46 tall capsules 16
 * in from the card's sides, side by side 8 apart for two, stacked 8 apart
 * otherwise. `ALERT_METRICS` gathers them for `placeAlert`; `MAC_ALERT_METRICS`
 * is the Mac's panel (MAC_* above).
 */
const ALERT_WIDTH = 320;
const ALERT_PAD_TOP = 21;
const ALERT_PAD_H = 30;
const ALERT_BUTTON_INSET = 16;
const ALERT_BUTTONS_GAP = 20;
const ALERT_PAD_BOTTOM = 16;
const ALERT_BUTTON_SPACING = 8;
/** Gap between the alert title and its message. */
const ALERT_TITLE_GAP = 6;
const ALERT_BUTTON_HEIGHT = 46;
/** An alert's `textfield` children: 36 tall rows inset as the buttons are, 8 apart, 16 below the message. */
const ALERT_FIELD_HEIGHT = 36;
const ALERT_FIELD_SPACING = 8;
const ALERT_FIELDS_GAP = 16;
interface AlertMetrics {
  width: number;
  padTop: number;
  padH: number;
  buttonInset: number;
  buttonsGap: number;
  padBottom: number;
  buttonSpacing: number;
  rowSpacing: number;
  titleGap: number;
  buttonHeight: number;
  fieldHeight: number;
  fieldSpacing: number;
  fieldsGap: number;
  textAlignment: 'leading' | 'center';
}
const ALERT_METRICS: AlertMetrics = {
  width: ALERT_WIDTH,
  padTop: ALERT_PAD_TOP,
  padH: ALERT_PAD_H,
  buttonInset: ALERT_BUTTON_INSET,
  buttonsGap: ALERT_BUTTONS_GAP,
  padBottom: ALERT_PAD_BOTTOM,
  buttonSpacing: ALERT_BUTTON_SPACING,
  rowSpacing: ALERT_BUTTON_SPACING,
  titleGap: ALERT_TITLE_GAP,
  buttonHeight: ALERT_BUTTON_HEIGHT,
  fieldHeight: ALERT_FIELD_HEIGHT,
  fieldSpacing: ALERT_FIELD_SPACING,
  fieldsGap: ALERT_FIELDS_GAP,
  textAlignment: 'leading',
};
// Measured in the Mac app (macOS 26): no app icon; the title (13 bold) 20
// under the top and 22 in, the message (13 regular) 10 under it, both
// leading-aligned; 28-tall capsule buttons 16 under the message, 16 in and
// 16 over the bottom, two side by side 8 apart (stacked 4 apart).
const MAC_ALERT_METRICS: AlertMetrics = {
  width: 260,
  padTop: 20,
  padH: 22,
  buttonInset: 16,
  buttonsGap: 16,
  padBottom: 16,
  buttonSpacing: 4,
  rowSpacing: 8,
  titleGap: 10,
  buttonHeight: 28,
  fieldHeight: 22,
  fieldSpacing: 8,
  fieldsGap: 12,
  textAlignment: 'leading',
};
/** Gap between an action sheet's title and its message. */
const DIALOG_TITLE_GAP = 4;
/** An action sheet's button labels are inset this much from its rows. */
const DIALOG_LABEL_INSET = 16;
/** `dialog` style (action sheet): cards inset 8 from the screen edges and the bottom safe area. */
const DIALOG_MARGIN = 8;
const DIALOG_BUTTON_HEIGHT = 57;
const DIALOG_HEADER_PAD_V = 14;
const DIALOG_HEADER_PAD_H = 16;
/** Gap between the dialog's button card and its cancel card. */
const DIALOG_CARD_GAP = 8;
const DIALOG_BUTTON_FONT_SIZE = 20;
const SAFE_AREA_EDGES = ['top', 'leading', 'bottom', 'trailing'] as const;

// ---------------------------------------------------------------------------
// Prop readers (props are untrusted JSON)
// ---------------------------------------------------------------------------

function obj(value: unknown): Record<string, unknown> | null {
  return typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : null;
}
function num(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}
function str(value: unknown): string | null {
  return typeof value === 'string' ? value : null;
}
/** A frame dimension: a number or the string "infinity". */
function dim(value: unknown): number | null {
  if (value === 'infinity') return Infinity;
  return num(value);
}

function styleOf(node: LayoutNode): Record<string, unknown> {
  return obj(node.props['style']) ?? {};
}

function readInsets(value: unknown): EdgeInsets | null {
  const o = obj(value);
  if (!o) return null;
  return {
    top: num(o['top']) ?? 0,
    leading: num(o['leading']) ?? 0,
    bottom: num(o['bottom']) ?? 0,
    trailing: num(o['trailing']) ?? 0,
  };
}

interface FrameSpec {
  width: number | null;
  height: number | null;
  minWidth: number | null;
  maxWidth: number | null;
  minHeight: number | null;
  maxHeight: number | null;
  alignment: string;
}

function readFrame(value: unknown): FrameSpec | null {
  const o = obj(value);
  if (!o) return null;
  return {
    width: num(o['width']),
    height: num(o['height']),
    minWidth: dim(o['minWidth']),
    maxWidth: dim(o['maxWidth']),
    minHeight: dim(o['minHeight']),
    maxHeight: dim(o['maxHeight']),
    alignment: str(o['alignment']) ?? 'center',
  };
}

/** Phase 12 `containerRelativeFrame` hint: `a × container + b` on the marked axes. */
interface ContainerRelativeFrameSpec {
  horizontal: boolean;
  vertical: boolean;
  a: number;
  b: number;
  alignment: string;
}

function readContainerRelativeFrame(value: unknown): ContainerRelativeFrameSpec | null {
  const o = obj(value);
  if (!o) return null;
  const horizontal = o['horizontal'] === true;
  const vertical = o['vertical'] === true;
  if (!horizontal && !vertical) return null;
  return { horizontal, vertical, a: num(o['a']) ?? 1, b: num(o['b']) ?? 0, alignment: str(o['alignment']) ?? 'center' };
}

interface AspectRatioSpec {
  /** The ratio (width ÷ height), or null for the child's own ideal ratio. */
  ratio: number | null;
  fill: boolean;
}

function readAspectRatio(value: unknown): AspectRatioSpec | null {
  const o = obj(value);
  if (!o) return null;
  const ratio = num(o['ratio']);
  return { ratio: ratio !== null && ratio > 0 ? ratio : null, fill: o['contentMode'] === 'fill' };
}

/** `layoutPriority` of a stack child, read through its `styled` wrappers (outermost wins). */
function layoutPriorityOf(node: LayoutNode): number {
  let current: LayoutNode | undefined = node;
  while (current && current.kind === 'styled') {
    const priority = num(styleOf(current)['layoutPriority']);
    if (priority !== null) return priority;
    current = current.children.length === 1 ? current.children[0] : undefined;
  }
  return 0;
}

/**
 * A flexible frame's length on one axis, as SwiftUI resolves it: a minimum
 * raises the child's length (up to the proposal) to at least `min`, and a
 * maximum takes the proposal (or the child's length, if longer) up to `max`.
 * So `.frame(minWidth: 25)` around a narrow badge is 25 wide, not the width
 * it was offered, while `.frame(maxWidth: .infinity)` fills the proposal. A
 * nil proposal is the child's own length.
 */
function flexibleFrameLength(proposed: number | null, child: number, min: number | null, max: number | null): number {
  const offered = proposed ?? child;
  let length = child;
  if (min !== null) length = Math.max(min, Math.min(length, offered));
  if (max !== null) length = Math.min(max, Math.max(length, offered));
  return length;
}

/** Whether `id` is the node or one of its descendants (a screen asking whether the focused text input is its own). */
function subtreeHas(node: LayoutNode, id: number): boolean {
  if (node.id === id) return true;
  for (const child of node.children) if (subtreeHas(child, id)) return true;
  return false;
}

/** Whether a root (through its `styled` wrappers) carries `ignoresKeyboard`: it keeps the keyboardless height. */
function ignoresKeyboard(node: LayoutNode): boolean {
  let current = node;
  while (current.kind === 'styled') {
    if (styleOf(current)['ignoresKeyboard'] === true) return true;
    if (current.children.length !== 1) return false;
    current = current.children[0] as LayoutNode;
  }
  return false;
}

/** The element under a chain of `styled` wrappers. */
/** A segmented picker as a navigation bar item (under its styled wrappers): it is a capsule of its own. */
function isSegmentedItem(item: LayoutNode): boolean {
  const inner = unwrapStyled(item);
  return inner.kind === 'picker' && str(inner.props['style']) === 'segmented';
}

function unwrapStyled(node: LayoutNode): LayoutNode {
  let current = node;
  while (current.kind === 'styled' && current.children.length === 1) {
    current = current.children[0] as LayoutNode;
  }
  return current;
}

/** Root kinds laid out over the whole screen: their bars reach into the safe areas. */
const SCREEN_ROOT_KINDS = new Set(['navstack', 'tabview', 'navsplit', 'inspector', 'alert']);

/**
 * A root that fills the screen: one of `SCREEN_ROOT_KINDS`, also when an
 * app's root modifiers wrap it in modifiers that leave its size alone or in
 * a one-child `ZStack` (Backyard Birds' shop modifier wraps its `TabView`
 * so); on iOS its bars reach the screen's edges all the same.
 */
function fillsScreen(node: LayoutNode): boolean {
  let current = node;
  for (;;) {
    if (SCREEN_ROOT_KINDS.has(current.kind)) return true;
    if (current.children.length !== 1) return false;
    if (current.kind === 'styled') {
      const style = styleOf(current);
      const sized = ['frame', 'containerRelativeFrame', 'padding', 'aspectRatio', 'fixedSize'].some((key) => style[key] !== undefined && style[key] !== null);
      if (sized) return false;
    } else if (current.kind !== 'zstack') {
      return false;
    }
    current = current.children[0] as LayoutNode;
  }
}

/** A `{x, y}` pair of finite numbers. */
function readPoint(value: unknown): { x: number; y: number } | null {
  const o = obj(value);
  if (!o) return null;
  const x = num(o['x']);
  const y = num(o['y']);
  return x !== null && y !== null ? { x, y } : null;
}

/** A `{width, height}` proposal from props (`"infinity"` → Infinity, null → null). */
function readProposal(value: unknown): Proposal | null {
  const o = obj(value);
  if (!o) return null;
  const axis = (v: unknown): number | null | undefined => {
    if (v === null) return null;
    if (v === 'infinity') return Infinity;
    const n = num(v);
    return n === null ? undefined : n;
  };
  const width = axis(o['width']);
  const height = axis(o['height']);
  if (width === undefined || height === undefined) return null;
  return { width, height };
}

/** Whether two proposals agree on both axes (within the 2-decimal rounding Swift applies). */
function sameProposal(a: Proposal, b: Proposal | null): boolean {
  if (!b) return false;
  const same = (x: number | null, y: number | null): boolean =>
    x === y || (x !== null && y !== null && (x === Infinity ? y === Infinity : Math.abs(x - y) < 0.01));
  return same(a.width, b.width) && same(a.height, b.height);
}

/** A `{width, height}` whose dimensions may be `"infinity"`. */
function readFlexSize(value: unknown): Size | null {
  const o = obj(value);
  if (!o) return null;
  const axis = (v: unknown): number | null => (v === 'infinity' ? Infinity : num(v));
  const width = axis(o['width']);
  const height = axis(o['height']);
  return width !== null && height !== null ? { width: Math.max(0, width), height: Math.max(0, height) } : null;
}

/** A custom layout's `{min, ideal, max}` sizes from its props. */
function readLayoutSizes(value: unknown): { min: Size; ideal: Size; max: Size; atWidth: Size | null } | null {
  const o = obj(value);
  if (!o) return null;
  const min = readFlexSize(o['min']);
  const ideal = readFlexSize(o['ideal']);
  const max = readFlexSize(o['max']);
  return min && ideal && max ? { min, ideal, max, atWidth: readFlexSize(o['atWidth']) } : null;
}

/** A `{width, height}` pair of finite, non-negative numbers. */
function readSize(value: unknown): Size | null {
  const o = obj(value);
  if (!o) return null;
  const width = num(o['width']);
  const height = num(o['height']);
  return width !== null && height !== null ? { width: Math.max(0, width), height: Math.max(0, height) } : null;
}

/** A `{x, y, width, height}` rect of finite numbers. */
function readRect(value: unknown): Rect | null {
  const point = readPoint(value);
  const size = readSize(value);
  return point && size ? { ...point, ...size } : null;
}

/** A style key of a grid cell, read through its `styled` wrappers (outermost wins). */
function cellStyle(node: LayoutNode, key: string): unknown {
  let current: LayoutNode | undefined = node;
  while (current && current.kind === 'styled') {
    const value = styleOf(current)[key];
    if (value !== undefined && value !== null) return value;
    current = current.children.length === 1 ? current.children[0] : undefined;
  }
  return undefined;
}

/**
 * A split view column's `.navigationSplitViewColumnWidth(min:ideal:max:)`:
 * the `splitColumnWidth` hint (`{ min?, ideal, max? }`) on the column's
 * content, read through its `styled` wrappers, its `navstack` and the root
 * screen of that stack (an app puts the modifier on the sidebar's list).
 */
function splitColumnWidthOf(column: LayoutNode): { min?: number; ideal: number; max?: number } | null {
  let current: LayoutNode | undefined = column;
  while (current) {
    const hint = cellStyle(current, 'splitColumnWidth');
    if (hint && typeof hint === 'object') {
      const spec = hint as Record<string, unknown>;
      const ideal = num(spec['ideal']);
      if (ideal === null) return null;
      const min = num(spec['min']);
      const max = num(spec['max']);
      return { ...(min !== null ? { min } : {}), ideal, ...(max !== null ? { max } : {}) };
    }
    const inner = unwrapStyled(current);
    if (inner.kind === 'navstack') current = inner.children[0] as LayoutNode | undefined;
    else if (inner.kind === 'navscreen') current = inner.children.find((child) => child.kind !== 'toolbar' && child.kind !== 'searchfield');
    else return null;
  }
  return null;
}

/** The default vertical padding above and below a list row's content. */
function listRowVPad(row: LayoutNode, sidebar: boolean, mac: boolean): number {
  if (mac) return row.kind === 'button' ? MAC_LIST_BUTTON_ROW_VPAD : sidebar ? MAC_LIST_SIDEBAR_ROW_VPAD : MAC_LIST_ROW_VPAD;
  switch (row.kind) {
    case 'picker':
      return str(row.props['style']) === 'segmented' ? LIST_ROW_SEGMENTED_VPAD : 0;
    case 'toggle':
      return isButtonToggle(row) ? (sidebar ? LIST_SIDEBAR_ROW_VPAD : LIST_ROW_VPAD) : LIST_ROW_CONTROL_VPAD;
    case 'stepper':
      return LIST_ROW_CONTROL_VPAD;
    default:
      return sidebar ? LIST_SIDEBAR_ROW_VPAD : LIST_ROW_VPAD;
  }
}

/** `gridCellColumns` of a grid cell (at least 1). */
function gridSpanOf(node: LayoutNode): number {
  const span = num(cellStyle(node, 'gridCellColumns'));
  return span !== null && span >= 1 ? Math.floor(span) : 1;
}

/** `gridCellAnchor` of a grid cell as [horizontal, vertical] unit anchors. */
function gridAnchorOf(node: LayoutNode): [number, number] | null {
  const point = readPoint(cellStyle(node, 'gridCellAnchor'));
  return point ? [point.x, point.y] : null;
}

/** `gridColumnAlignment` of a grid cell as a horizontal unit anchor. */
function gridColumnAnchorOf(node: LayoutNode): number | null {
  switch (str(cellStyle(node, 'gridColumnAlignment'))) {
    case 'leading':
      return 0;
    case 'center':
      return 0.5;
    case 'trailing':
      return 1;
    default:
      return null;
  }
}

/** Vertical anchor of a `gridrow` alignment (null → the grid's). */
function rowAnchor(alignment: string | null, gridAlignment: string): number {
  switch (alignment) {
    case 'top':
    case 'firstTextBaseline':
      return 0;
    case 'center':
      return 0.5;
    case 'bottom':
    case 'lastTextBaseline':
      return 1;
    default:
      return alignmentAnchors(gridAlignment)[1];
  }
}

interface GridItemSpec {
  kind: 'fixed' | 'flexible' | 'adaptive';
  value: number;
  minimum: number;
  maximum: number;
  spacing: number;
  alignment: string | null;
}

/** `columns` of a `lazyvgrid` or `rows` of a `lazyhgrid` (untrusted JSON); an empty list is one flexible track. */
function readGridItems(value: unknown): GridItemSpec[] {
  const items: GridItemSpec[] = [];
  if (Array.isArray(value)) {
    for (const raw of value) {
      const o = obj(raw);
      if (!o) continue;
      const size = obj(o['size']) ?? {};
      const kind = str(size['kind']);
      const minimum = Math.max(0, num(size['minimum']) ?? GRID_ITEM_DEFAULT_MIN);
      const maximum = Math.max(minimum, dim(size['maximum']) ?? Infinity);
      items.push({
        kind: kind === 'fixed' ? 'fixed' : kind === 'adaptive' ? 'adaptive' : 'flexible',
        value: Math.max(0, num(size['value']) ?? 0),
        minimum,
        maximum,
        spacing: num(o['spacing']) ?? DEFAULT_SPACING,
        alignment: str(o['alignment']),
      });
    }
  }
  if (items.length === 0) {
    items.push({
      kind: 'flexible',
      value: 0,
      minimum: GRID_ITEM_DEFAULT_MIN,
      maximum: Infinity,
      spacing: DEFAULT_SPACING,
      alignment: null,
    });
  }
  return items;
}

interface RowHints {
  /** `.badge(_:)` text (non-empty), or null. */
  badge: string | null;
  /** `.listRowInsets(_:)`, or null for the defaults. */
  insets: EdgeInsets | null;
  /** `.listRowSeparator(.hidden, edges:)`: the edges whose separator the row hides. */
  separatorHidden: { top: boolean; bottom: boolean } | null;
}

/**
 * Phase 6 list row hints, read through the row's `styled` chain (the
 * outermost wins, like `layoutPriority`): `badge` and `listRowInsets`.
 */
function rowHintsOf(node: LayoutNode): RowHints {
  const hints: RowHints = { badge: null, insets: null, separatorHidden: null };
  let current: LayoutNode | undefined = node;
  // `NavigationLink(value:) { OrderRow().badge(n) }`: the hints sit on the
  // link's label, which is the row's content. Phase 8 wrappers (`listrow`,
  // `swipeactions`, `contextmenu`, `gesture`) are looked through the same way.
  while (current) {
    if (current.kind === 'styled') {
      const style = styleOf(current);
      if (hints.badge === null) {
        const badge = str(style['badge']);
        if (badge !== null && badge.length > 0) hints.badge = badge;
      }
      if (hints.insets === null) hints.insets = readInsets(style['listRowInsets']);
      const separator = style['listRowSeparator'];
      if (hints.separatorHidden === null && typeof separator === 'object' && separator !== null) {
        const edges = separator as Record<string, unknown>;
        hints.separatorHidden = { top: edges['top'] === true, bottom: edges['bottom'] === true };
      }
      current = current.children.length === 1 ? current.children[0] : undefined;
    } else if (current.kind === 'navlink' && current.children.length === 1) {
      current = current.children[0];
    } else if (ROW_WRAPPERS.has(current.kind) && current.children.length >= 1) {
      current = current.children[0];
    } else {
      break;
    }
  }
  return hints;
}

/**
 * `.listRowBackground(_:)`: the ShapeStyle a list row's cell is painted with
 * instead of the card's (`Color.clear` lets the list's background show), read
 * through the row's `styled` chain and wrappers as `rowHintsOf` reads the
 * other hints; undefined when the row has none.
 */
export function listRowBackgroundOf(node: LayoutNode): unknown {
  let current: LayoutNode | undefined = node;
  while (current) {
    if (current.kind === 'styled') {
      const background = styleOf(current)['listRowBackground'];
      if (background !== undefined && background !== null) return background;
      current = current.children.length === 1 ? current.children[0] : undefined;
    } else if ((current.kind === 'navlink' && current.children.length === 1) || (ROW_WRAPPERS.has(current.kind) && current.children.length >= 1)) {
      current = current.children[0];
    } else {
      return undefined;
    }
  }
  return undefined;
}

/** Phase 8 wrappers whose first child is the row content they stand for. */
const ROW_WRAPPERS: ReadonlySet<string> = new Set(['listrow', 'swipeactions', 'contextmenu', 'gesture', 'tapgesture', 'draggable', 'dropdestination']);

/**
 * Phase 8: the `listrow` a list row is (or wraps, through `styled` and the
 * other wrappers), or null for a plain row. Edit mode shifts such rows.
 */
function listRowOf(node: LayoutNode): LayoutNode | null {
  let current: LayoutNode | undefined = node;
  while (current) {
    if (current.kind === 'listrow') return current;
    if (current.kind === 'styled' || ROW_WRAPPERS.has(current.kind)) current = current.children[0];
    else return null;
  }
  return null;
}

/** A list's row as `outlineRows` names it: the view, how deep it sits, and the `disclosuregroup` it is the label of. */
interface RowSource {
  node: LayoutNode;
  depth: number;
  group: LayoutNode | null;
}

/**
 * The rows a section's children make. A `disclosuregroup` among them is an
 * outline, as SwiftUI lays one out in a `List`: its label (its first child)
 * is a row, with the group's chevron at the trailing edge, and while it is
 * expanded each of its content children is a row under it, a level deeper
 * (`LIST_OUTLINE_INDENT` further in), a nested group among them opening the
 * same way. Collapsed, its content is not laid out.
 */
function outlineRows(nodes: readonly LayoutNode[], depth: number, out: RowSource[]): RowSource[] {
  for (const row of nodes) {
    if (row.kind === 'disclosuregroup') {
      const [label, ...content] = row.children;
      if (label) out.push({ node: label, depth, group: row });
      if (row.props['expanded'] === true) outlineRows(content, depth + 1, out);
    } else {
      out.push({ node: row, depth, group: null });
    }
  }
  return out;
}

/** A list row's `Label`: where its icon is centred, and where its title starts, past the row content's leading edge (iOS). */
const LIST_LABEL_ICON_CENTER = 12;
const LIST_LABEL_TITLE_X = 40;

/**
 * A `Label` in its default style: an hstack of the marked icon (`labelIcon`
 * on its `styled` wrapper) then the title. In a list row it takes iOS's icon
 * column instead of the stack's spacing.
 */
function labelColumnOf(node: LayoutNode): { icon: LayoutNode; title: LayoutNode } | null {
  if (node.kind !== 'hstack' || node.children.length !== 2) return null;
  const [icon, title] = node.children as [LayoutNode, LayoutNode];
  const style = icon.kind === 'styled' ? (icon.props['style'] as Record<string, unknown> | undefined) : undefined;
  if (style?.['labelIcon'] !== true) return null;
  return { icon, title };
}

/** Phase 8 edit mode: the width of a `listrow`'s leading control (the selection circle / minus) and of a movable row's trailing grip. */
export const LIST_EDIT_CONTROL_WIDTH = 44;
/**
 * Edit mode: how far a control sits in from the card's edge (its 22 pt glyph
 * is centred 28 from the edge, as iOS draws it) and by how much the content
 * shifts right of the leading control, or narrows for the trailing grip.
 */
export const LIST_EDIT_CONTROL_INSET = 6;
export const LIST_EDIT_CONTROL_SHIFT = 40;

/** `font` with every metric multiplied by `scale` (for `imageScale`d symbols). */
function scaledFont(font: ResolvedFont, scale: number): ResolvedFont {
  if (scale === 1) return font;
  const size = font.size * scale;
  const lineHeight = font.lineHeight * scale;
  const px = (v: number): string => `${Math.round(v * 100) / 100}px`;
  return { ...font, size, lineHeight, leading: font.leading * scale, css: `${font.weight} ${px(size)}/${px(lineHeight)} ${FONT_FAMILIES[font.family]}` };
}

// ---------------------------------------------------------------------------
// Geometry helpers
// ---------------------------------------------------------------------------

function clamp(value: number, min: number | null, max: number | null): number {
  let v = value;
  if (max !== null) v = Math.min(v, max);
  if (min !== null) v = Math.max(v, min);
  return v;
}

function finite(value: number | null): value is number {
  return value !== null && Number.isFinite(value);
}

/** Subtract a length from a proposal axis (null and Infinity pass through). */
function shrink(value: number | null, by: number): number | null {
  if (!finite(value)) return value;
  return Math.max(0, value - by);
}

function axisOf(size: Size, axis: Axis): number {
  return axis === 'horizontal' ? size.width : size.height;
}
function crossOf(size: Size, axis: Axis): number {
  return axis === 'horizontal' ? size.height : size.width;
}
function makeProposal(axis: Axis, along: number | null, cross: number | null): Proposal {
  return axis === 'horizontal' ? { width: along, height: cross } : { width: cross, height: along };
}

/** Horizontal (0/0.5/1) and vertical (0/0.5/1) anchors for a SwiftUI alignment. */
function alignmentAnchors(alignment: string): [number, number] {
  switch (alignment) {
    case 'leading':
      return [0, 0.5];
    case 'trailing':
      return [1, 0.5];
    case 'top':
      return [0.5, 0];
    case 'bottom':
      return [0.5, 1];
    case 'topLeading':
      return [0, 0];
    case 'topTrailing':
      return [1, 0];
    case 'bottomLeading':
      return [0, 1];
    case 'bottomTrailing':
      return [1, 1];
    default:
      return [0.5, 0.5];
  }
}

function alignIn(container: Size, child: Size, alignment: string): { dx: number; dy: number } {
  const [ax, ay] = alignmentAnchors(alignment);
  return {
    dx: safeOffset(container.width, child.width, ax),
    dy: safeOffset(container.height, child.height, ay),
  };
}

/** The guide names a pair of anchors aligns by: `leading`/`center`/`trailing` and `top`/`center`/`bottom`. */
function guideNames([ax, ay]: [number, number]): [string, string] {
  return [ax === 0 ? 'leading' : ax === 1 ? 'trailing' : 'center', ay === 0 ? 'top' : ay === 1 ? 'bottom' : 'center'];
}

/**
 * The guide `name` on `axis` a view moved with `.alignmentGuide`, as
 * `[a, b, c]` (the guide is `a + b·width + c·height`): set on the view or on
 * a `styled` wrapper inside it (the modifiers between change nothing here).
 */
export function explicitGuide(node: LayoutNode, axis: 'horizontal' | 'vertical', name: string): [number, number, number] | null {
  let current: LayoutNode | undefined = node;
  for (let depth = 0; current && current.kind === 'styled' && depth < 32; depth++) {
    const guide = (current.props['style'] as { alignmentGuide?: { axis?: string; name?: string; value?: unknown } } | undefined)?.alignmentGuide;
    if (guide && guide.axis === axis && guide.name === name && Array.isArray(guide.value) && guide.value.length === 3) {
      const [a, b, c] = guide.value.map((v) => (typeof v === 'number' && Number.isFinite(v) ? v : 0)) as [number, number, number];
      return [a, b, c];
    }
    current = current.children.length === 1 ? current.children[0] : undefined;
  }
  return null;
}

function guideValue([a, b, c]: [number, number, number], size: Size): number {
  return a + b * size.width + c * size.height;
}

/**
 * `alignIn` for a child that may have moved its guides (`.alignmentGuide`):
 * on an axis where it did, its guide meets the container's guide for the
 * same alignment (0, the middle or the end); elsewhere it aligns as before.
 */
function alignChild(container: Size, child: Size, alignment: string, node: LayoutNode): { dx: number; dy: number } {
  const anchors = alignmentAnchors(alignment);
  const [horizontal, vertical] = guideNames(anchors);
  const offsets = alignIn(container, child, alignment);
  const gx = explicitGuide(node, 'horizontal', horizontal);
  const gy = explicitGuide(node, 'vertical', vertical);
  if (gx && Number.isFinite(container.width)) offsets.dx = container.width * anchors[0] - guideValue(gx, child);
  if (gy && Number.isFinite(container.height)) offsets.dy = container.height * anchors[1] - guideValue(gy, child);
  return offsets;
}

/** A `Spacer` (a lazy stack's too): a stack sizes it after its other children. */
function isSpacer(node: LayoutNode): boolean {
  return node.kind === 'spacer' || node.kind === 'lazyspacer';
}

function safeOffset(container: number, child: number, anchor: number): number {
  if (!Number.isFinite(container) || !Number.isFinite(child)) return 0;
  return (container - child) * anchor;
}

const ZERO: Size = { width: 0, height: 0 };
const NIL_PROPOSAL: Proposal = { width: null, height: null };
const MIN_PROPOSAL: Proposal = { width: 0, height: 0 };
const MAX_PROPOSAL: Proposal = { width: Infinity, height: Infinity };

function square(side: number): Size {
  return { width: side, height: side };
}

function proposalKey(p: Proposal): string {
  return `${p.width === null ? 'n' : p.width}|${p.height === null ? 'n' : p.height}`;
}

/**
 * The memo key of a plan or placement: the proposal and the container
 * (`containerRelativeFrame`) it was made under. The container is the screen
 * everywhere but inside a scroll view, whose viewport is its proposal: a
 * stack measuring the scroll view at zero, at its ideal and at its final
 * width makes three containers, and a page sized by the container must not
 * keep the size the first measurement gave it.
 */
function planKey(ctx: Context, p: Proposal): string {
  return `${proposalKey(p)}@${ctx.container.width}x${ctx.container.height}`;
}

/**
 * Snap a frame to whole CSS pixels: both edges floor (with an epsilon for
 * float noise), so nested and adjacent frames never overlap by a fraction and
 * integer sizes survive fractional origins.
 */
function snapRect(r: Rect): Rect {
  const x = Math.floor(fin(r.x) + EPSILON);
  const y = Math.floor(fin(r.y) + EPSILON);
  const right = Math.floor(fin(r.x) + fin(r.width) + EPSILON);
  const bottom = Math.floor(fin(r.y) + fin(r.height) + EPSILON);
  return { x, y, width: Math.max(0, right - x), height: Math.max(0, bottom - y) };
}
function fin(v: number): number {
  return Number.isFinite(v) ? v : 0;
}

// ---------------------------------------------------------------------------
// Plans (memoized per node + proposal)
// ---------------------------------------------------------------------------

interface StackPlan {
  size: Size;
  childSizes: Size[];
  childProposals: Proposal[];
  /** The space after each child but the last (see `stackGaps`). */
  gaps: number[];
}

interface StyledPlan {
  size: Size;
  /** The child's reported size (0×0 without a child). */
  childSize: Size;
  childProposal: Proposal;
  /** Rect the child is aligned in, relative to the styled frame. */
  contentBox: Rect;
  alignment: string;
  /** Phase 6 `.position(x:y:)`: the child's center inside the content box, and its ideal size. */
  position: { x: number; y: number; ideal: Size } | null;
}

interface TextPlan {
  size: Size;
  layout: TextLayout;
}

// Phase 8 controls.
interface SliderPlan {
  size: Size;
  /** The `label` child, hidden (iOS draws no slider label). */
  label: LayoutNode | null;
  min: LayoutNode | null;
  max: LayoutNode | null;
  minSize: Size;
  maxSize: Size;
  cctx: Context;
}

interface DatePickerPlan {
  size: Size;
  /** Null when there is no label child or `labelsHidden` is set. */
  label: LayoutNode | null;
  labelProposal: Proposal;
  labelSize: Size;
  /** Capsule widths (text + 2 × 11), 0 for an absent component. */
  dateWidth: number;
  timeWidth: number;
  capsulesWidth: number;
  cctx: Context;
}

interface ListRowPlan {
  node: LayoutNode;
  proposal: Proposal;
  /** Content size reported by the row element. */
  contentSize: Size;
  /** Full-width row rect, relative to the list origin at scroll 0. */
  row: Rect;
  /** Where the row element is placed. */
  content: Rect;
  /** Phase 6 `.badge`: trailing text in the row (rect relative to the list origin). */
  badge: { rect: Rect; layout: TextLayout } | null;
  /**
   * Phase 8 edit mode: the `listrow` the row is, its 44px leading control
   * rect (a selectable or deletable row) and its 44px trailing grip rect (a
   * movable row), relative to the list origin.
   */
  edit: { listRow: LayoutNode; control: Rect | null; reorder: Rect | null } | null;
  /** `.listRowSeparator(.hidden)`: the row's separator edges left out. */
  separatorHidden?: { top: boolean; bottom: boolean } | null;
  /**
   * An outline's row (`outlineRows`): how deep it sits, and the
   * `disclosuregroup` it is the label of, when it is one (the group's
   * chevron and toggle go over this row).
   */
  outline?: { depth: number; group: LayoutNode | null } | null;
}

/** A section's view header or footer (`sectionheader` / `sectionfooter`) as the list places it. */
interface ListSupplementPlan {
  node: LayoutNode;
  ctx: Context;
  proposal: Proposal;
  /** Relative to the list origin at scroll 0. */
  rect: Rect;
}

interface ListGroupPlan {
  section: LayoutNode | null;
  frame: Rect;
  header: { rect: Rect; layout: TextLayout } | null;
  footer: { rect: Rect; layout: TextLayout } | null;
  headerView: ListSupplementPlan | null;
  footerView: ListSupplementPlan | null;
  card: Rect;
  rows: ListRowPlan[];
  /** Each `disclosuregroup` among the rows, with the rect its rows span (its label's, then its content's while expanded). */
  outlines: { group: LayoutNode; frame: Rect }[];
}

/** A Mac form's label column entry: text the page draws (`layout`), or a view (`node`) placed at the rect. */
interface FormLabelPlan {
  /** Relative to the list origin at scroll 0; a view's rect is its own size, at the column's trailing edge. */
  rect: Rect;
  layout: TextLayout | null;
  node: LayoutNode | null;
  ctx: Context;
}

interface ListPlan {
  size: Size;
  contentSize: Size;
  groups: ListGroupPlan[];
  /** The Mac idiom's columns form: its label column's entries. */
  labels?: FormLabelPlan[];
  columns?: boolean;
  /** The columns form's controls column: where it starts (relative to the list origin) and its width. */
  column?: { x: number; width: number };
}

interface ScrollPlan {
  size: Size;
  content: LayoutNode | null;
  /** The content's context: the scroll view's, with the viewport as the container. */
  cctx: Context;
  contentProposal: Proposal;
  contentSize: Size;
}

interface PickerPlan {
  size: Size;
  style: 'segmented' | 'menu' | 'inline';
  font: ResolvedFont;
  options: string[];
  selected: number;
  /** Menu style: the label's and the value's (text + chevron) widths. */
  labelWidth: number;
  valueWidth: number;
  /** Inline style: whether a label row precedes the options. */
  labelRow: boolean;
  /** Segmented style in a navigation bar item: the bar's glass capsule, its segments inset. */
  bar?: boolean;
}

// Phase 6 plans.

interface GridCellPlan {
  node: LayoutNode;
  column: number;
  span: number;
  /** The cell box width: the spanned columns plus the spacing between them. */
  width: number;
  size: Size;
  /** Full-span cell of a non-`gridrow` child (measured with the grid width). */
  full: boolean;
}

interface GridRowPlan {
  /** The `gridrow` element, or null for a full-span child. */
  node: LayoutNode | null;
  cells: GridCellPlan[];
  y: number;
  height: number;
  alignment: string | null;
}

interface GridPlan {
  size: Size;
  columns: number[];
  columnX: number[];
  /** Horizontal unit anchor per column from a cell's `gridColumnAlignment` (null → the grid's). */
  columnAnchors: (number | null)[];
  rows: GridRowPlan[];
}

interface LazyColumnPlan {
  x: number;
  width: number;
  alignment: string | null;
}

interface LazyGridPlan {
  size: Size;
  columns: LazyColumnPlan[];
  rows: { y: number; height: number; cells: { node: LayoutNode; column: number; size: Size }[] }[];
}

interface GaugePlan {
  size: Size;
  label: LayoutNode | null;
  current: LayoutNode | null;
  labelSize: Size;
  currentSize: Size;
  /** Height of the label row (0 without labels). */
  labelRow: number;
  labelCtx: Context;
  /** A ProgressView's label context (the gauge's own font, not the caption). */
  labelOwnCtx?: Context;
  /** A circular gauge: the view in the middle of the ring (the current value label, else the label) and its size. */
  ring?: { center: LayoutNode | null; centerSize: Size; ctx: Context; arcLabel: LayoutNode | null };
}

/** Phase 7: one `chartitem` child of a chart, measured at its ideal size. */
interface ChartItemPlan {
  node: LayoutNode;
  role: string;
  size: Size;
  /** Frame relative to the chart's origin. */
  frame: Rect;
}

interface ChartPlan {
  size: Size;
  geometry: ChartGeometry;
  /** The axis / legend font (`caption`). */
  font: ResolvedFont;
  items: ChartItemPlan[];
  /** Text labels in `geometry.xLabels` / `geometry.yLabels` order. */
  xLabelTexts: string[];
  yLabelTexts: string[];
  childCtx: Context;
}

/** `options` of a picker as strings (untrusted JSON). */
function readOptions(node: LayoutNode): string[] {
  const raw = node.props['options'];
  return Array.isArray(raw) ? raw.map((o) => (typeof o === 'string' ? o : String(o))) : [];
}

/** `selected` clamped to `0 ..< count` (0 when there is nothing to select). */
function readSelected(node: LayoutNode, count: number): number {
  const raw = num(node.props['selected']) ?? 0;
  return count === 0 ? 0 : Math.min(count - 1, Math.max(0, Math.floor(raw)));
}

// ---------------------------------------------------------------------------
// The pass
// ---------------------------------------------------------------------------

export class LayoutPass {
  readonly frames = new Map<number, Rect>();
  readonly text = new Map<number, TextLayout>();
  readonly fonts = new Map<number, ResolvedFont>();
  readonly contentSizes = new Map<number, Size>();
  readonly chrome = new Map<number, Record<string, Rect>>();
  readonly chromeText = new Map<number, Record<string, TextLayout>>();
  readonly hidden = new Set<number>();
  readonly layouts = new Map<number, LayoutRecord>();
  /** Previous screen title per navscreen node, for the back button label. */
  private readonly previousTitles = new Map<LayoutNode, string>();
  /**
   * A navigation screen's root list or vertical scroll view starts at the top
   * of the screen and scrolls under its bar (`bar` tall): its content starts
   * below the bar and the large title row that scrolls with it, if any.
   * `nested`: a scroll view deeper in the screen's content that touches the
   * bar (`scrollEdgeGrowth`), rather than the screen's own content.
   */
  /** A scroll view under its screen's bar: the bar, the large title that scrolls with it and a search row under the title (`search`). */
  private readonly scrollInsets = new WeakMap<LayoutNode, { bar: number; largeTitle: Rect | null; search?: number; nested?: boolean }>();

  private readonly memo = new Map<LayoutNode, Map<string, unknown>>();
  private readonly implicit = new Map<LayoutNode, LayoutNode>();
  private implicitIds = -1;
  /** The Mac sidebar just laid out could not fit its bar items (its detail shows them in its `»`). */
  private sidebarOverflowed = false;
  private readonly rootContext: Context;
  /** Phase 9: where (and for what proposal) every node was last placed. */
  private readonly placed = new Map<LayoutNode, { key: string; x: number; y: number }>();
  /** Covered screens to lay out anyway (`uncover`): one a back swipe is about to show. */
  private readonly uncovered = new Set<LayoutNode>();
  /** Phase 9: the ids each node hid when it was last placed (menu items, tabs, ...). */
  private readonly hiddenBy = new Map<LayoutNode, number[]>();
  /** Phase 9: ids placed or hidden in the current `run`; what the DOM pass must visit. */
  private touched = new Set<number>();
  /** Phase 9: the run that last wrote each node's chrome (a node's own place drops older entries). */
  private readonly chromeRun = new Map<number, number>();
  private runId = 0;
  /** Phase 9: how many nodes `run` placed from scratch and skipped (for tests and profiling). */
  stats = { placed: 0, skipped: 0 };

  constructor(
    private readonly env: LayoutEnvironment,
    private readonly measurer: TextMeasurer,
    /** Phase 9: the parent of a node in the renderer's tree (for `invalidate`); null at the root. */
    private readonly parentOf: (node: LayoutNode) => LayoutNode | null = () => null,
  ) {
    this.rootContext = {
      font: defaultFont(env),
      lineLimit: null,
      textAlignment: 'leading',
      stackAxis: null,
      listRow: false,
      bottomBar: 0,
      imageScale: 1,
      splitSidebar: false,
      safeTop: env.safeArea.top,
      container: { width: env.screen.width, height: env.screen.height },
    };
  }

  /** The on-screen keyboard's height over the screen's bottom (device mode, a field being edited); 0 while it is down. */
  private get keyboard(): number {
    const keyboard = this.env.keyboard;
    return typeof keyboard === 'number' && Number.isFinite(keyboard) && keyboard > 0 ? keyboard : 0;
  }

  run(roots: readonly LayoutNode[]): LayoutResult {
    this.runId += 1;
    this.touched = new Set();
    this.stats = { placed: 0, skipped: 0 };
    const { screen, safeArea } = this.env;
    const ctx = this.rootContext;
    for (const root of roots) {
      if (fillsScreen(root)) {
        // These ignore the safe areas: the tab bar and navigation bars
        // extend into them and the content is inset instead (a `navsplit`
        // hands the horizontal insets to its columns, see `placeNavSplit`;
        // an `inspector` its trailing one to its panel).
        // Phase 8: an `alert` is a full-screen layer too (scrim + card).
        this.place(root, ctx, { width: screen.width, height: screen.height }, 0, 0);
      } else if (root.kind === 'sheet' && this.sheetIsForm(root)) {
        const rect = this.formSheetRect(root, ctx);
        this.place(root, ctx, { width: rect.width, height: rect.height }, rect.x, rect.y);
      } else if (root.kind === 'sheet') {
        const floats = this.sheetFloats(root);
        const insetH = floats ? SHEET_FLOAT_INSET_H : 0;
        const insetBottom = floats ? SHEET_FLOAT_INSET_BOTTOM : 0;
        const proposal: Proposal = { width: screen.width - 2 * insetH, height: null };
        const size = this.measure(root, ctx, proposal);
        this.place(root, ctx, proposal, insetH, screen.height - insetBottom - size.height);
      } else {
        // The keyboard (device mode) is a bottom inset too, unless the root
        // ignores it (`.ignoresSafeArea(.keyboard)`: the screen keeps its height).
        const keyboard = ignoresKeyboard(root) ? 0 : this.keyboard;
        const safe: Size = {
          width: Math.max(0, screen.width - safeArea.leading - safeArea.trailing),
          height: Math.max(0, screen.height - safeArea.top - Math.max(safeArea.bottom, keyboard)),
        };
        if (this.env.contentHeight) {
          // An island: the root takes its content's height and sits at the top.
          const proposal: Proposal = { width: safe.width, height: null };
          const size = this.measure(root, ctx, proposal);
          const { dx } = alignIn(safe, size, 'center');
          this.place(root, ctx, proposal, safeArea.leading + dx, safeArea.top);
          continue;
        }
        const proposal: Proposal = { width: safe.width, height: safe.height };
        const size = this.measure(root, ctx, proposal);
        const { dx, dy } = alignIn(safe, size, 'center');
        this.place(root, ctx, proposal, safeArea.leading + dx, safeArea.top + dy);
      }
    }
    return this.finish();
  }

  /**
   * A `host` element (src/hostContent.ts): the page's DOM, measured by the
   * host measurer. `fill` takes the proposal and the content's own size on an
   * unconstrained axis; `content` takes the content's ideal width capped by
   * the proposal and its height at that width, like text. Without a measurer
   * (headless) it is a 10×10 placeholder, like a shape with no proposal.
   */
  private measureHost(node: LayoutNode, p: Proposal): Size {
    const props = node.props as { sizing?: string; content?: string };
    const bounded = (value: number | null): number | null => (value !== null && Number.isFinite(value) ? value : null);
    const width = bounded(p.width);
    const height = bounded(p.height);
    const measure = this.measurer.measureHost?.bind(this.measurer);
    if (!measure || typeof props.content !== 'string') {
      return { width: width ?? SHAPE_DEFAULT, height: height ?? SHAPE_DEFAULT };
    }
    if (props.sizing === 'fill') {
      if (width !== null && height !== null) return { width, height };
      const natural = measure(props.content, width);
      return { width: width ?? natural.width, height: height ?? natural.height };
    }
    const ideal = measure(props.content, null);
    if (width === null || ideal.width <= width) return ideal;
    return { width, height: measure(props.content, width).height };
  }

  private finish(): LayoutResult {
    // Only what this run produced needs snapping; the rest was snapped when
    // it was produced.
    for (const id of this.touched) {
      const rect = this.frames.get(id);
      if (rect) this.frames.set(id, snapRect(rect));
      const rects = this.chrome.get(id);
      if (rects) {
        const snapped: Record<string, Rect> = {};
        for (const key of Object.keys(rects)) snapped[key] = snapRect(rects[key] as Rect);
        this.chrome.set(id, snapped);
      }
      const size = this.contentSizes.get(id);
      if (size) {
        this.contentSizes.set(id, {
          width: Math.round(fin(size.width) + EPSILON),
          height: Math.round(fin(size.height) + EPSILON),
        });
      }
    }
    return {
      frames: this.frames,
      text: this.text,
      fonts: this.fonts,
      contentSizes: this.contentSizes,
      chrome: this.chrome,
      chromeText: this.chromeText,
      hidden: this.hidden,
      layouts: this.layouts,
      changed: this.touched,
    };
  }

  // -------------------------------------------------------------------------
  // Phase 9: invalidation across runs
  // -------------------------------------------------------------------------

  /**
   * `node`'s props changed (or it gained / lost a child): it, its ancestors
   * and its descendants are measured and placed again on the next run.
   */
  invalidate(node: LayoutNode): void {
    this.invalidateUp(node);
    this.invalidateDown(node);
  }

  /** `node` (or a descendant) changed in a way that can change its size. */
  invalidateUp(node: LayoutNode): void {
    for (let current: LayoutNode | null = node; current; current = this.parentOf(current)) {
      this.forgetPlan(current);
    }
  }

  /** `node` moved under a new ancestor or its context may have changed: its whole subtree. */
  invalidateDown(node: LayoutNode): void {
    this.forgetPlan(node);
    for (const child of node.children) this.invalidateDown(child);
  }

  /**
   * A covered screen is about to show without a pop (a back swipe drags
   * the top screen aside): lay it out on the next run even though it is
   * covered, in case the stack resized or its content changed meanwhile.
   */
  uncover(screen: LayoutNode): void {
    this.uncovered.add(screen);
    // The run must reach the stack: its ancestors' plans go (not the
    // screen's own, so one nothing changed under is still skipped as placed).
    for (let current = this.parentOf(screen); current; current = this.parentOf(current)) this.forgetPlan(current);
  }

  /** `node` left the tree: drop everything its subtree produced. */
  remove(node: LayoutNode): void {
    this.forgetPlan(node);
    this.uncovered.delete(node);
    if (node.id >= 0) {
      this.frames.delete(node.id);
      this.text.delete(node.id);
      this.fonts.delete(node.id);
      this.contentSizes.delete(node.id);
      this.chrome.delete(node.id);
      this.chromeRun.delete(node.id);
      this.chromeText.delete(node.id);
      this.layouts.delete(node.id);
      this.hidden.delete(node.id);
    }
    this.previousTitles.delete(node);
    for (const child of node.children) this.remove(child);
  }

  private forgetPlan(node: LayoutNode): void {
    this.memo.delete(node);
    this.placed.delete(node);
    const implicit = this.implicit.get(node);
    if (implicit) {
      this.memo.delete(implicit);
      this.placed.delete(implicit);
      this.implicit.delete(node);
    }
  }

  /**
   * `child` is not shown by `node` this time (a menu item, an unselected tab,
   * an unchosen `viewthatfits` candidate). Tabs keep their frame; anything
   * else loses its subtree's frames, which would otherwise outlive it.
   */
  private hide(child: LayoutNode, by: LayoutNode): void {
    if (child.id < 0) return;
    this.hidden.add(child.id);
    this.touched.add(child.id);
    const list = this.hiddenBy.get(by);
    if (list) list.push(child.id);
    else this.hiddenBy.set(by, [child.id]);
    if (child.kind !== 'tab') this.dropFrames(child);
  }

  private dropFrames(node: LayoutNode): void {
    this.placed.delete(node);
    if (node.id >= 0 && this.frames.delete(node.id)) {
      this.text.delete(node.id);
      this.chrome.delete(node.id);
      this.chromeText.delete(node.id);
      this.layouts.delete(node.id);
      this.touched.add(node.id);
    }
    for (const child of node.children) this.dropFrames(child);
  }

  // -------------------------------------------------------------------------
  // Memoization and context
  // -------------------------------------------------------------------------

  private plan<T>(node: LayoutNode, ctx: Context, proposal: Proposal, compute: () => T): T {
    let byProposal = this.memo.get(node);
    if (!byProposal) {
      byProposal = new Map();
      this.memo.set(node, byProposal);
    }
    const key = planKey(ctx, proposal);
    const cached = byProposal.get(key);
    if (cached !== undefined) return cached as T;
    const value = compute();
    byProposal.set(key, value);
    return value;
  }

  /** Context for the children of `node`. */
  private childContext(node: LayoutNode, ctx: Context): Context {
    switch (node.kind) {
      case 'styled': {
        const style = styleOf(node);
        let next = ctx;
        const font = resolveFont(style['font'], ctx.font, this.env);
        if (font !== ctx.font) next = { ...next, font };
        if ('lineLimit' in style) {
          const limit = num(style['lineLimit']);
          const min = num(style['lineLimitMin']);
          next = { ...next, lineLimit: limit !== null && limit >= 1 ? Math.floor(limit) : null };
          // A new limit replaces the whole range, lower bound included.
          if (min !== null && min > 1) next.minLines = Math.floor(min);
          else delete next.minLines;
        }
        const alignment = str(style['multilineTextAlignment']);
        if (alignment === 'leading' || alignment === 'center' || alignment === 'trailing') {
          next = { ...next, textAlignment: alignment };
        }
        const imageScale = str(style['imageScale']);
        if (imageScale !== null) next = { ...next, imageScale: IMAGE_SCALES[imageScale] ?? 1 };
        if (typeof style['scrollDisabled'] === 'boolean') next = { ...next, scrollDisabled: style['scrollDisabled'] };
        const minListRowHeight = num(style['defaultMinListRowHeight']);
        if (minListRowHeight !== null && minListRowHeight >= 0) next = { ...next, minListRowHeight };
        // `.ignoresSafeArea` on the bottom: the content inside has no bottom
        // safe area, so a scroll view in it reaching the screen's bottom is
        // not inset by the bar there (a photo viewer's, under a bottom toolbar).
        const ignored = style['ignoresSafeArea'];
        if (ignored === 'all' || (Array.isArray(ignored) && ignored.includes('bottom'))) {
          if (next.bottomBar !== 0 || next.bottomEdge !== undefined) next = { ...next, bottomBar: 0, bottomEdge: undefined };
        }
        return next;
      }
      case 'vstack':
      case 'disclosuregroup':
        return { ...ctx, stackAxis: 'vertical', listRow: false };
      case 'hstack':
        return { ...ctx, stackAxis: 'horizontal', listRow: false };
      case 'list':
      case 'scrollview':
      case 'sheet': {
        // A bar above is the outermost scroll view's to run under; what a
        // scroll view or a sheet holds is clear of it.
        if (ctx.barAbove === undefined && ctx.stackAxis === null && !ctx.listRow) return ctx;
        return { ...ctx, barAbove: undefined, stackAxis: null, listRow: false };
      }
      case 'toolbar': {
        const placement = this.barPlacement(node);
        const navBarItem = placement === 'leading' || placement === 'trailing';
        const bottomBarItem = placement === 'bottom';
        if (ctx.stackAxis === null && !ctx.listRow && ctx.navBarItem === navBarItem && ctx.bottomBarItem === bottomBarItem) return ctx;
        return { ...ctx, stackAxis: null, listRow: false, navBarItem, bottomBarItem };
      }
      case 'gesture':
      case 'swipeactions':
      case 'contextmenu':
      case 'listrow':
      case 'draggable':
      case 'dropdestination':
        // Phase 8 wrappers are transparent: a `navlink` under them is still
        // the list row (its chevron, its selected fill).
        return ctx.stackAxis === null ? ctx : { ...ctx, stackAxis: null };
      default:
        if (ctx.stackAxis === null && !ctx.listRow) return ctx;
        return { ...ctx, stackAxis: null, listRow: false };
    }
  }

  /**
   * A container's single content child: the child itself, or an implicit
   * stack when a TupleView produced several children (vertical by default,
   * like SwiftUI's implicit VStack).
   */
  private contentOf(node: LayoutNode, axis: Axis = 'vertical', children: LayoutNode[] = node.children): LayoutNode | null {
    if (children.length === 0) return null;
    if (children.length === 1) return children[0] as LayoutNode;
    let implicit = this.implicit.get(node);
    if (!implicit) {
      implicit = {
        id: this.implicitIds--,
        kind: axis === 'vertical' ? 'vstack' : 'hstack',
        props: { spacing: null, alignment: 'center' },
        children,
      };
      this.implicit.set(node, implicit);
    }
    return implicit;
  }

  /**
   * Phase 6: a `navscreen`'s children are `[content, ...extras]`, the extras
   * being `toolbar`s and a `searchfield`. The content is whatever is not an
   * extra (an implicit vstack when there are several such children).
   */
  private navScreenParts(node: LayoutNode): { content: LayoutNode | null; toolbars: LayoutNode[]; search: LayoutNode | null } {
    const toolbars: LayoutNode[] = [];
    let search: LayoutNode | null = null;
    const rest: LayoutNode[] = [];
    for (const child of node.children) {
      if (child.kind === 'toolbar') toolbars.push(child);
      else if (child.kind === 'searchfield') search = search ?? child;
      else rest.push(child);
    }
    return { content: this.contentOf(node, 'vertical', rest), toolbars, search };
  }

  private setFrame(node: LayoutNode, x: number, y: number, size: Size): void {
    if (node.id < 0) return;
    this.frames.set(node.id, { x, y, width: size.width, height: size.height });
    this.touched.add(node.id);
  }

  private setChrome(node: LayoutNode, rects: Record<string, Rect>): void {
    if (node.id < 0) return;
    this.chrome.set(node.id, rects);
    this.chromeRun.set(node.id, this.runId);
    this.touched.add(node.id);
  }

  // -------------------------------------------------------------------------
  // Measure
  // -------------------------------------------------------------------------

  measure(node: LayoutNode, ctx: Context, p: Proposal): Size {
    switch (node.kind) {
      case 'text':
        return this.textPlan(node, ctx, p).size;
      case 'image':
        return this.measureImage(node, ctx, p);
      case 'spacer':
        return this.measureSpacer(node, ctx, p);
      case 'lazyspacer':
        // Lazy rows not built yet: no space (the page builds them as it nears).
        return ZERO;
      case 'divider':
        if (ctx.stackAxis === 'horizontal') return { width: 1, height: finite(p.height) ? p.height : 0 };
        return { width: finite(p.width) ? p.width : 0, height: 1 };
      case 'vstack':
      case 'disclosuregroup':
        return this.stackPlan(node, ctx, p, 'vertical').size;
      case 'hstack': {
        const label = ctx.labelColumn ? labelColumnOf(node) : null;
        if (label) return this.labelColumnPlan(label, this.childContext(node, ctx), p).size;
        return this.stackPlan(node, ctx, p, 'horizontal').size;
      }
      case 'zstack':
        return this.measureZStack(node, ctx, p);
      case 'styled':
        return this.styledPlan(node, ctx, p).size;
      case 'button':
        return this.measureButton(node, ctx, p);
      case 'toggle':
        return this.measureToggle(node, ctx, p);
      case 'textfield': {
        if (node.props['axis'] === 'vertical') return this.measureMultilineField(node, ctx, p);
        const style = str(node.props['style']);
        return {
          width: finite(p.width) ? p.width : TEXTFIELD_DEFAULT_WIDTH,
          // A Mac's field is 22 tall in every style.
          height: this.env.platform === 'macos' ? MAC_TEXTFIELD_HEIGHT : style === 'plain' ? TEXTFIELD_HEIGHT_PLAIN : TEXTFIELD_HEIGHT_ROUNDED,
        };
      }
      case 'shape':
        if (node.props['shape'] === 'circle') {
          // SwiftUI's `Circle` is the largest square in the proposal (so in a
          // stack it gives the leftover width to its siblings).
          // An open axis counts as SHAPE_DEFAULT, as for the other shapes.
          return square(Math.min(p.width ?? SHAPE_DEFAULT, p.height ?? SHAPE_DEFAULT));
        }
        return { width: p.width ?? SHAPE_DEFAULT, height: p.height ?? SHAPE_DEFAULT };
      case 'color':
      case 'canvas':
        return { width: p.width ?? SHAPE_DEFAULT, height: p.height ?? SHAPE_DEFAULT };
      case 'video':
        // Fills the proposal; an open axis follows the video's aspect ratio.
        return videoSize(node.props, p);
      case 'scrollview':
        return this.scrollPlan(node, ctx, p).size;
      case 'list':
        return this.listPlan(node, ctx, p).size;
      case 'navlink':
        return this.measureNavLink(node, ctx, p);
      case 'navstack':
      case 'navscreen':
      case 'tabview':
      case 'tab':
      case 'navsplit':
      case 'inspector':
      case 'inspectorpanel':
        return {
          width: p.width ?? this.env.screen.width,
          height: p.height ?? this.env.screen.height,
        };
      case 'viewthatfits':
        return this.viewThatFitsPlan(node, ctx, p).size;
      case 'picker':
        return this.pickerPlan(node, ctx, p).size;
      case 'host':
        return this.measureHost(node, p);
      case 'registered':
      case 'geometry':
        // GeometryReader: the proposal, 10 on unconstrained axes.
        return { width: p.width ?? SHAPE_DEFAULT, height: p.height ?? SHAPE_DEFAULT };
      case 'sheet':
        if (this.sheetIsForm(node)) {
          const rect = this.formSheetRect(node, ctx);
          return { width: p.width ?? rect.width, height: rect.height };
        }
        return { width: p.width ?? this.env.screen.width, height: this.sheetHeight(node) };
      // Phase 8 controls.
      case 'alert':
        return { width: p.width ?? this.env.screen.width, height: p.height ?? this.env.screen.height };
      case 'stepper':
        return this.measureStepper(node, ctx, p);
      case 'slider':
        return this.sliderPlan(node, ctx, p).size;
      case 'datepicker':
        return this.datePickerPlan(node, ctx, p).size;
      case 'texteditor':
        // Not scrolling, as tall as its lines; else a textarea filling the
        // proposal (flexible on an infinite axis, like a shape).
        if (ctx.scrollDisabled) return this.measureFittingEditor(node, ctx, p);
        return { width: p.width ?? TEXTFIELD_DEFAULT_WIDTH, height: p.height ?? TEXTEDITOR_DEFAULT_HEIGHT };
      case 'layout':
        return this.measureLayout(node, ctx, p);
      case 'grid':
        return this.gridPlan(node, ctx, p).size;
      case 'lazyvgrid':
      case 'lazyhgrid':
        return this.lazyGridPlan(node, ctx, p).size;
      case 'gauge':
        return this.gaugePlan(node, ctx, p).size;
      case 'chart':
        return this.chartPlan(node, ctx, p).size;
      case 'decorated':
      case 'chartitem': {
        // Phase 7 `chartitem`: its one view's size (a chart measures it at its ideal size).
        const content = node.children[0];
        return content ? this.measure(content, this.childContext(node, ctx), p) : ZERO;
      }
      case 'menu': {
        // Phase 6: the label (the first child) is the only laid-out child.
        // The Mac idiom: outside a bar and a list row the menu is a pull-down
        // button around its label.
        const label = node.children[0];
        if (this.menuIsPullDown(ctx)) {
          const inner: Proposal = { width: shrink(p.width, 2 * MAC_PULLDOWN_PAD_H + MAC_PULLDOWN_CHEVRON), height: shrink(p.height, 0) };
          const labelSize = label ? this.measure(label, this.childContext(node, ctx), inner) : ZERO;
          return { width: labelSize.width + 2 * MAC_PULLDOWN_PAD_H + MAC_PULLDOWN_CHEVRON, height: Math.max(MAC_PULLDOWN_HEIGHT, labelSize.height) };
        }
        if (this.menuHasIndicator(ctx)) {
          // A Mac toolbar menu: its label, then the indicator's chevron.
          const labelSize = label ? this.measure(label, this.childContext(node, ctx), { width: shrink(p.width, MAC_BAR_MENU_INDICATOR), height: p.height }) : ZERO;
          return { width: labelSize.width + MAC_BAR_MENU_INDICATOR, height: labelSize.height };
        }
        return label ? this.measure(label, this.childContext(node, ctx), p) : ZERO;
      }
      case 'searchfield':
        // Phase 6: inside a navscreen the bar places it; elsewhere a 36pt field (22 on a Mac).
        return { width: finite(p.width) ? p.width : SEARCH_FIELD_DEFAULT_WIDTH, height: this.env.platform === 'macos' ? MAC_SEARCH_FIELD_HEIGHT : SEARCH_FIELD_HEIGHT };
      case 'swipeactions':
      case 'contextmenu': {
        // Phase 8: the content (the first child) alone; the actions / items
        // are hidden (the renderer shows them behind the row / in a popover).
        // A `swipeactions` that is a list row spans the row: the whole row
        // swipes and its actions sit at the row's trailing edge.
        const content = node.children[0];
        const contentSize = content ? this.measure(content, this.childContext(node, ctx), p) : ZERO;
        if (node.kind === 'swipeactions' && ctx.listRow && finite(p.width)) {
          return { width: p.width, height: contentSize.height };
        }
        return contentSize;
      }
      case 'gesture':
      case 'listrow':
      case 'tapgesture':
      case 'draggable':
      case 'dropdestination':
      default: {
        // `tapgesture` (its child's size), `toolbar` outside a navscreen,
        // `section` outside a list and unknown kinds: lay the children out as a vstack.
        const content = this.contentOf(node);
        return content ? this.measure(content, this.childContext(node, ctx), p) : ZERO;
      }
    }
  }

  /**
   * A vertical `textfield`: the proposed width (200 when unconstrained) and
   * as many lines as its text (or, empty, its placeholder) wraps to in the
   * context font, between the range's `minLines` and `lineLimit`.
   */
  private measureMultilineField(node: LayoutNode, ctx: Context, p: Proposal): Size {
    const inset = str(node.props['style']) === 'roundedBorder' ? TEXTFIELD_ROUNDED_INSET : { horizontal: 0, vertical: 0 };
    const width = finite(p.width) ? p.width : TEXTFIELD_DEFAULT_WIDTH;
    const text = str(node.props['text']) || str(node.props['placeholder']) || '';
    const wrapped = wrapText(text, ctx.font, Math.max(1, width - 2 * inset.horizontal), null, this.measurer);
    const min = Math.max(1, ctx.minLines ?? 1);
    const max = Math.max(min, ctx.lineLimit ?? Infinity);
    const lines = Math.min(Math.max(wrapped.lines.length, min), max);
    return { width, height: textHeight(lines, ctx.font) + 2 * inset.vertical };
  }

  /**
   * A `texteditor` under `.scrollDisabled(true)`: the proposed width (200
   * when unconstrained) and its lines, a leading apart (as the DOM spaces
   * them), inside UITextView's insets; one line when empty.
   *
   * Every line is the context font's leading tall, whatever its runs' fonts
   * (`richTextPlan`'s own per-line heights, a line height then a leading
   * per line, with a larger run's font making its line taller, are not
   * used): the DOM gives the editor one line height, which every run's span
   * inherits (richEditor.ts `layOutRichEditor`), so its lines are evenly
   * spaced and the box is as tall as the DOM draws it.
   */
  private measureFittingEditor(node: LayoutNode, ctx: Context, p: Proposal): Size {
    const width = finite(p.width) ? p.width : TEXTFIELD_DEFAULT_WIDTH;
    const runs = node.props['runs'];
    const lines = Array.isArray(runs)
      ? this.editorRunsPlan(node, ctx, p, runs, width).layout.lines.length
      : wrapText(str(node.props['text']) ?? '', ctx.font, Math.max(1, width - 2 * TEXTEDITOR_INSET.horizontal), null, this.measurer).lines.length;
    return { width, height: Math.max(1, lines) * ctx.font.leading + 2 * TEXTEDITOR_INSET.vertical };
  }

  /**
   * A rich `texteditor`'s runs wrapped inside the insets of a `width` wide
   * editor, with no line limit (an editor shows every line). Memoized under
   * the editor's proposal like a text's plan: the measurement of a fitting
   * editor and the layout the DOM draws (`place`) wrap the runs once per
   * layout pass, where they used to wrap them twice.
   */
  private editorRunsPlan(node: LayoutNode, ctx: Context, p: Proposal, runs: unknown[], width: number): TextPlan {
    return this.plan(node, ctx, p, () => {
      const inner = Math.max(1, width - 2 * TEXTEDITOR_INSET.horizontal);
      return this.richTextPlan(runs, { ...ctx, lineLimit: null }, { width: inner, height: null });
    });
  }

  private textPlan(node: LayoutNode, ctx: Context, p: Proposal): TextPlan {
    return this.plan(node, ctx, p, () => {
      const runs = node.props['runs'];
      if (Array.isArray(runs)) return this.richTextPlan(runs, ctx, p);
      const text = str(node.props['text']) ?? '';
      const wrapped = wrapText(text, ctx.font, p.width, ctx.lineLimit, this.measurer);
      return {
        size: { width: wrapped.width, height: textHeight(wrapped.lines.length, ctx.font) },
        layout: {
          font: ctx.font,
          lines: wrapped.lines,
          alignment: ctx.textAlignment,
          truncated: wrapped.truncated,
        },
      };
    });
  }

  /**
   * Rich text: each run's font (the text's font, changed by the run's), its
   * kerning, and an inline image's size; wrapped as one paragraph flow.
   */
  private richTextPlan(runs: unknown[], ctx: Context, p: Proposal): TextPlan {
    const inputs: TextRunInput[] = runs.map((value) => {
      const run = obj(value) ?? {};
      let font = resolveFont(run['font'], ctx.font, this.env);
      if (run['italic'] === true) font = { ...font, css: `italic ${font.css}` };
      const kerning = num(run['kerning']) ?? 0;
      const image = obj(run['image']);
      if (image) return { image: this.inlineImageSize(image, font), font, kerning };
      return { text: str(run['text']) ?? '', font, kerning };
    });
    const wrapped = wrapRuns(inputs, ctx.font, p.width, ctx.lineLimit, this.measurer);
    const lines = wrapped.lines.map((line) => line.map((fragment) => fragment.text ?? '\uFFFC').join(''));
    return {
      size: { width: wrapped.width, height: wrapped.heights.reduce((total, height) => total + height, 0) },
      layout: {
        font: ctx.font,
        lines,
        alignment: ctx.textAlignment,
        truncated: wrapped.truncated,
        rich: { lines: wrapped.lines, heights: wrapped.heights, fonts: inputs.map((input) => input.font) },
      },
    };
  }

  /** An image in a text: a bitmap or remote image at its size, a symbol its font's symbol square. */
  private inlineImageSize(props: Record<string, unknown>, font: ResolvedFont): Size {
    if (typeof props['url'] === 'string' || typeof props['bitmap'] === 'object') {
      return { width: num(props['width']) ?? 0, height: num(props['height']) ?? 0 };
    }
    const name = str(props['name']);
    if (name === null) return square(font.leading);
    const asset = this.env.assets?.images[name];
    if (!asset) return square(UNKNOWN_IMAGE_SIDE);
    if (asset.kind === 'symbol') return square(font.leading);
    return { width: num(asset.width) ?? 0, height: num(asset.height) ?? 0 };
  }

  private measureSpacer(node: LayoutNode, ctx: Context, p: Proposal): Size {
    const min = num(node.props['minLength']);
    if (ctx.stackAxis === null) {
      // Outside a stack: fills both axes, minimum 0.
      return { width: finite(p.width) ? p.width : 0, height: finite(p.height) ? p.height : 0 };
    }
    const minLength = min ?? DEFAULT_SPACER_MIN;
    const along = p[ctx.stackAxis === 'horizontal' ? 'width' : 'height'];
    let length: number;
    if (along === null) length = minLength;
    else if (along === Infinity) length = Infinity;
    else length = Math.max(minLength, along);
    return ctx.stackAxis === 'horizontal' ? { width: length, height: 0 } : { width: 0, height: length };
  }

  private measureZStack(node: LayoutNode, ctx: Context, p: Proposal): Size {
    const cctx = this.childContext(node, ctx);
    let width = 0;
    let height = 0;
    for (const child of node.children) {
      const size = this.measure(child, cctx, p);
      width = Math.max(width, size.width);
      height = Math.max(height, size.height);
    }
    return { width, height };
  }

  /**
   * A link row with a badge: iOS draws the count before the chevron
   * (`Inbox  12 ›`), the chevron at the row's trailing edge. The row was laid
   * out with the badge's room at the trailing edge, after the link's
   * chevron; the two trade places.
   */
  private badgeBeforeChevron(rowNode: LayoutNode): void {
    const rowChrome = rowNode.id >= 0 ? this.chrome.get(rowNode.id) : undefined;
    const badge = rowChrome?.['badge'];
    if (!badge) return;
    // Through the row's wrappers (a context menu's content is its first child).
    const wrappers = new Set(['styled', 'listrow', 'contextmenu', 'swipeactions', 'gesture']);
    let link: LayoutNode | undefined = rowNode;
    while (link && link.kind !== 'navlink') link = wrappers.has(link.kind) ? link.children[0] : undefined;
    const linkChrome = link && link.id >= 0 ? this.chrome.get(link.id) : undefined;
    const chevron = linkChrome?.['chevron'];
    if (!chevron || chevron.x > badge.x) return;
    const end = badge.x + badge.width;
    linkChrome['chevron'] = { ...chevron, x: end - chevron.width };
    rowChrome['badge'] = { ...badge, x: end - chevron.width - badge.width };
  }

  private buttonIsBordered(node: LayoutNode, ctx: Context): boolean {
    const style = str(node.props['style']);
    if (style === 'bordered' || style === 'borderedProminent' || style === 'glass') return true;
    // The Mac idiom: a button of the automatic style is a push button too
    // (in a list row as well), except as a bar item's content.
    return this.env.platform === 'macos' && (style === null || style === 'automatic') && !ctx.navBarItem && !ctx.bottomBarItem;
  }

  private measureButton(node: LayoutNode, ctx: Context, p: Proposal): Size {
    const label = this.contentOf(node);
    if (!this.buttonIsBordered(node, ctx)) return label ? this.measure(label, this.childContext(node, ctx), p) : ZERO;
    const cctx = this.borderedLabelContext(node, ctx);
    if (ctx.bottomBarItem) {
      // In a bottom bar (measured in the iOS 26.2 simulator, Native
      // BarButtonStyleTests): a prominent or glass button is its own 48
      // circle; a bordered one fills its slot less 10, 34 tall.
      if (bottomBarStyle(node) === 'prominent') return { width: BOTTOM_GLASS_HEIGHT, height: BOTTOM_GLASS_HEIGHT };
      const size = label ? this.measure(label, cctx, { width: null, height: null }) : ZERO;
      return { width: bottomSlot(label ?? node, size) + BOTTOM_BORDERED_GROWTH - BOTTOM_BORDERED_INSET, height: BOTTOM_BORDERED_HEIGHT };
    }
    const { padV, padH, minHeight } = this.borderedMetrics(node);
    const inner: Proposal = {
      width: shrink(p.width, 2 * padH),
      height: shrink(p.height, 2 * padV),
    };
    const size = label ? this.measure(label, cctx, inner) : ZERO;
    return {
      width: size.width + 2 * padH,
      height: Math.max(minHeight, size.height + 2 * padV),
    };
  }

  private borderedMetrics(node: LayoutNode): { padV: number; padH: number; minHeight: number; textStyle?: string } {
    const controlSize = str(node.props['controlSize']) ?? 'regular';
    if (this.env.platform === 'macos') return MAC_BORDERED_SIZES[controlSize] ?? (MAC_BORDERED_SIZES['regular'] as { padV: number; padH: number; minHeight: number });
    return BORDERED_SIZES[controlSize] ?? { padV: BORDERED_PAD_V, padH: BORDERED_PAD_H, minHeight: BORDERED_MIN_HEIGHT };
  }

  /** A bordered button's label context: a small or mini one sets a smaller text style. */
  private borderedLabelContext(node: LayoutNode, ctx: Context): Context {
    const cctx = this.childContext(node, ctx);
    const textStyle = this.borderedMetrics(node).textStyle;
    return textStyle ? { ...cctx, font: resolveFont({ textStyle }, cctx.font, this.env) } : cctx;
  }

  /**
   * A toggle's control: iOS's switch at the trailing edge; on a Mac a
   * checkbox before the label, or the small switch in a list row (a grouped
   * form's), at the trailing edge.
   */
  private switchMetrics(ctx: Context): { width: number; height: number; gap: number; checkbox: boolean } {
    if (this.env.platform !== 'macos') return { width: SWITCH_WIDTH, height: SWITCH_HEIGHT, gap: TOGGLE_GAP, checkbox: false };
    // A grouped form's toggle is a switch at the row's trailing end (macOS 26).
    if (ctx.groupedRow) return { width: MAC_GROUPED_SWITCH_WIDTH, height: MAC_GROUPED_SWITCH_HEIGHT, gap: MAC_GROUPED_LABEL_GAP, checkbox: false };
    // A Mac's toggle is a checkbox before its label, in a list row too.
    return { width: MAC_CHECKBOX_SIZE, height: MAC_CHECKBOX_SIZE, gap: MAC_CHECKBOX_GAP, checkbox: true };
  }

  private toggleLabelProposal(ctx: Context, p: Proposal): Proposal {
    const control = this.switchMetrics(ctx);
    return { width: shrink(p.width, control.width + control.gap), height: p.height };
  }

  private measureToggle(node: LayoutNode, ctx: Context, p: Proposal): Size {
    const label = this.contentOf(node);
    if (isBarToggle(node, ctx)) {
      const size = label ? this.measure(label, this.childContext(node, ctx), { width: null, height: null }) : ZERO;
      return { width: size.width + 2 * BAR_TOGGLE_PAD_H, height: BAR_TOGGLE_HEIGHT };
    }
    if (isButtonToggle(node)) {
      if (ctx.bottomBarItem) {
        const size = label ? this.measure(label, this.childContext(node, ctx), { width: null, height: null }) : ZERO;
        return { width: bottomSlot(label ?? node, size) + BOTTOM_TOGGLE_GROWTH - BOTTOM_TOGGLE_INSET, height: BOTTOM_TOGGLE_HEIGHT };
      }
      // sized as a bordered button, on or off
      const m = this.borderedMetrics(node);
      const inner: Proposal = { width: shrink(p.width, 2 * m.padH), height: shrink(p.height, 2 * m.padV) };
      const size = label ? this.measure(label, this.childContext(node, ctx), inner) : ZERO;
      return { width: size.width + 2 * m.padH, height: Math.max(m.minHeight, size.height + 2 * m.padV) };
    }
    const control = this.switchMetrics(ctx);
    const labelSize = label
      ? this.measure(label, this.childContext(node, ctx), this.toggleLabelProposal(ctx, p))
      : ZERO;
    return {
      width: finite(p.width) ? p.width : labelSize.width + control.gap + control.width,
      height: Math.max(labelSize.height, control.height),
    };
  }

  private navLinkLabelProposal(ctx: Context, p: Proposal): Proposal {
    // A Mac's rows have no chevron.
    return ctx.listRow && this.env.platform !== 'macos' ? { width: shrink(p.width, LIST_CHEVRON_WIDTH), height: p.height } : p;
  }

  private measureNavLink(node: LayoutNode, ctx: Context, p: Proposal): Size {
    const label = this.contentOf(node);
    const labelSize = label
      ? this.measure(label, this.childContext(node, ctx), this.navLinkLabelProposal(ctx, p))
      : ZERO;
    if (ctx.listRow && finite(p.width)) return { width: p.width, height: labelSize.height };
    return labelSize;
  }

  private sheetHeight(node: LayoutNode): number {
    const detents = node.props['detents'];
    const first = Array.isArray(detents) ? str(detents[0]) : null;
    const { screen, safeArea } = this.env;
    // iOS 26: a `large` sheet's top is the safe area's (measured in the
    // iOS 26 simulator); earlier versions left 10 more above it.
    const large = screen.height - safeArea.top;
    if (first === 'medium') return Math.min(SHEET_MEDIUM_HEIGHT, large);
    // `.height(h)` / `.fraction(f)` detents, at most as tall as `large`.
    if (first?.startsWith('height:')) return Math.min(large, Number(first.slice(7)) || large);
    if (first?.startsWith('fraction:')) return Math.min(large, screen.height * (Number(first.slice(9)) || 1));
    // Phase 8: `full` (`.fullScreenCover`) covers the whole screen.
    if (first === 'full') return screen.height;
    return large;
  }

  /** Phase 8: a sheet whose first detent is `full` has no grabber row. */
  private sheetIsFull(node: LayoutNode): boolean {
    const detents = node.props['detents'];
    return Array.isArray(detents) && str(detents[0]) === 'full';
  }

  /** iPadOS: a sheet in a regular width (not a full-screen cover) is a centered form sheet; on a Mac a window-modal panel. */
  private sheetIsForm(node: LayoutNode): boolean {
    return (this.env.screen.width >= REGULAR_WIDTH || this.env.platform === 'macos') && !this.sheetIsFull(node);
  }

  /**
   * The form sheet's card: UIKit's size, clamped to the safe area less a
   * margin, centered in it. On a Mac the panel is 470 wide, as tall as its
   * bars and its content's ideal height, centered in the window.
   */
  private formSheetRect(node: LayoutNode, ctx: Context): Rect {
    const { screen, safeArea } = this.env;
    if (this.env.platform === 'macos') {
      const width = Math.max(0, Math.min(MAC_SHEET_WIDTH, screen.width - 2 * MAC_SHEET_MARGIN));
      const height = Math.max(0, Math.min(this.macSheetHeight(node, ctx, width), screen.height - 2 * MAC_SHEET_MARGIN));
      return { x: Math.round((screen.width - width) / 2), y: Math.round((screen.height - height) / 2), width, height };
    }
    const top = safeArea.top + FORM_SHEET_MARGIN;
    const room = { width: screen.width - 2 * FORM_SHEET_MARGIN, height: screen.height - top - safeArea.bottom - FORM_SHEET_MARGIN };
    const width = Math.max(0, Math.min(FORM_SHEET.width, room.width));
    const height = Math.max(0, Math.min(FORM_SHEET.height, room.height));
    return { x: Math.round((screen.width - width) / 2), y: Math.round(top + (room.height - height) / 2), width, height };
  }

  /**
   * Where a `toolbar` is drawn: its placement, except that a Mac has no
   * bottom bar (SwiftUI's `.bottomBar` is iOS's): its items join the
   * toolbar's trailing ones, in order, as the native renderer places them
   * (`.automatic` there).
   */
  private barPlacement(toolbar: LayoutNode): string | null {
    const placement = str(toolbar.props['placement']);
    return placement === 'bottom' && this.env.platform === 'macos' ? 'trailing' : placement;
  }

  /** The toolbars of a Mac sheet's root screen that go in its bottom bar: those whose items are all text buttons (a cancel or confirm action). */
  private macSheetActionToolbars(screen: LayoutNode): LayoutNode[] {
    return this.navScreenParts(screen).toolbars.filter((toolbar) => {
      const placement = str(toolbar.props['placement']);
      if ((placement !== 'leading' && placement !== 'trailing') || str(toolbar.props['spacer']) !== null) return false;
      return toolbar.children.length > 0 && toolbar.children.every((item) => !isSymbolItem(item) && unwrapStyled(item).kind === 'button');
    });
  }

  /** A Mac sheet's root screen, when its content is a navigation stack. */
  private macSheetScreen(node: LayoutNode): LayoutNode | null {
    const child = this.contentOf(node);
    if (!child) return null;
    const stack = unwrapStyled(child);
    if (stack.kind !== 'navstack') return null;
    return stack.children[stack.children.length - 1] ?? null;
  }

  /** A Mac sheet's height: its title bar, its content's ideal height at the sheet's width, and its actions bar if it has one. */
  private macSheetHeight(node: LayoutNode, ctx: Context, width: number): number {
    const screen = this.macSheetScreen(node);
    const child = this.contentOf(node);
    if (!screen) return child ? this.measure(child, this.childContext(node, ctx), { width, height: null }).height : 0;
    const content = this.navScreenParts(screen).content;
    // The content's context as it is laid out: through the `styled` wrappers around the stack (a
    // `defaultMinListRowHeight` set on it, or inherited from the presenter), the stack and its screen.
    let inner = this.childContext(node, ctx);
    let current = child!;
    while (current.kind === 'styled' && current.children.length === 1) {
      inner = this.childContext(current, inner);
      current = current.children[0] as LayoutNode;
    }
    inner = this.childContext(screen, this.childContext(current, inner));
    const ideal = content ? this.measure(content, inner, { width, height: null }).height : 0;
    return MAC_SHEET_TITLE_BAR + ideal + (this.macSheetActionToolbars(screen).length > 0 ? MAC_SHEET_ACTIONS_BAR : 0);
  }

  /** iOS 26: a sheet whose first detent is a partial one floats inset from the screen edges. */
  private sheetFloats(node: LayoutNode): boolean {
    if (this.sheetIsForm(node)) return false;
    const detents = node.props['detents'];
    const first = Array.isArray(detents) ? str(detents[0]) : null;
    return first === 'medium' || first?.startsWith('height:') === true || first?.startsWith('fraction:') === true;
  }

  /** iOS 26 shows the grabber only for a sheet with more than one detent. */
  private sheetHasGrabber(node: LayoutNode): boolean {
    if (this.sheetIsForm(node)) return false;
    const detents = node.props['detents'];
    return Array.isArray(detents) && detents.length > 1 && !this.sheetIsFull(node);
  }

  // -------------------------------------------------------------------------
  // Picker (Phase 4)
  // -------------------------------------------------------------------------

  /**
   * `segmented`: 32 high; the width is the proposal, or every segment label
   * plus 20 when none is given. `menu`: 44 high in a list row, 34 elsewhere;
   * the width is the proposal, or label + gap + value + chevron. `inline`
   * (Phase 6): one 44 row per option, plus a label row when the label is
   * non-empty; the width is the proposal, or the widest row.
   */
  private pickerPlan(node: LayoutNode, ctx: Context, p: Proposal): PickerPlan {
    return this.plan(node, ctx, p, () => {
      const options = readOptions(node);
      const selected = readSelected(node, options.length);
      const style = str(node.props['style']);
      if (style === 'inline') {
        const font = ctx.font;
        const label = str(node.props['label']) ?? '';
        const labelRow = label.length > 0;
        let natural = labelRow ? this.measurer.measureWidth(label, font) : 0;
        for (const option of options) {
          natural = Math.max(natural, this.measurer.measureWidth(option, font) + PICKER_INLINE_CHECK_WIDTH);
        }
        const rows = options.length + (labelRow ? 1 : 0);
        return {
          size: { width: finite(p.width) ? p.width : natural, height: rows * PICKER_INLINE_ROW_HEIGHT },
          style: 'inline',
          font,
          options,
          selected,
          labelWidth: 0,
          valueWidth: 0,
          labelRow,
        };
      }
      if (style === 'segmented' && ctx.navBarItem) {
        const font = makeFont('system', BAR_SEGMENTED_FONT_SIZE, 400);
        let widest = 0;
        for (const option of options) widest = Math.max(widest, this.measurer.measureWidth(option, font));
        const natural = options.length * (widest + BAR_SEGMENT_PAD) + 2 * BAR_SEGMENTED_INSET;
        return {
          size: { width: finite(p.width) ? p.width : natural, height: NAV_GLASS_SIZE },
          style: 'segmented',
          font,
          options,
          selected,
          labelWidth: 0,
          valueWidth: 0,
          labelRow: false,
          bar: true,
        };
      }
      if (style === 'segmented') {
        const mac = this.env.platform === 'macos';
        const font = mac ? makeFont('system', MAC_SEGMENTED_FONT_SIZE, 400) : makeFont('system', PICKER_SEGMENT_FONT_SIZE, 600);
        let natural = 0;
        let widest = 0;
        for (const option of options) {
          const optionWidth = this.measurer.measureWidth(option, font);
          natural += optionWidth + PICKER_SEGMENT_PAD;
          widest = Math.max(widest, optionWidth);
        }
        // A Mac's segments are equal, the widest label and 18 (macOS 26).
        if (mac) natural = options.length * Math.ceil(widest + MAC_SEGMENT_PAD);
        // A Mac shows the picker's label before the control (in a form's row it is in the label column).
        const labelText = mac && !ctx.formColumn ? (str(node.props['label']) ?? '') : '';
        const labelWidth = labelText.length > 0 && !ctx.navBarItem ? this.measurer.measureWidth(labelText, ctx.font) + MAC_SEGMENTED_LABEL_GAP : 0;
        // On a Mac it is as wide as its segments; elsewhere it fills the width offered.
        const width = mac
          ? Math.min(natural + labelWidth, finite(p.width) ? p.width : Infinity)
          : finite(p.width) ? p.width : natural + labelWidth;
        return {
          size: { width, height: mac ? MAC_SEGMENTED_HEIGHT : PICKER_SEGMENTED_HEIGHT },
          style: 'segmented',
          font,
          options,
          selected,
          labelWidth,
          valueWidth: 0,
          labelRow: false,
        };
      }
      const font = ctx.font;
      // In a Mac form's row the label is in the label column and the pop-up spans the controls column.
      const labelWidth = ctx.formColumn ? 0 : this.measurer.measureWidth(str(node.props['label']) ?? '', font);
      const value = options[selected] ?? '';
      const mac = this.env.platform === 'macos';
      // The Mac's pop-up button holds the value and the chevrons in a bordered button.
      const popup = mac && (!ctx.listRow || ctx.formColumn === true);
      // macOS 26: a pop-up is as wide as its widest option, 13 before the label and the chevrons 9 from its end.
      const popupWidth = (): number => Math.ceil(Math.max(...options.map((option) => this.measurer.measureWidth(option, font)), 0) + MAC_PICKER_POPUP_EXTRA);
      const valueWidth = popup ? popupWidth() : this.measurer.measureWidth(value, font) + PICKER_MENU_CHEVRON_WIDTH;
      const natural = labelWidth + PICKER_MENU_GAP + valueWidth;
      return {
        size: {
          // In a Mac columns form's row: the pop-up alone, at its width.
          width: ctx.formColumn ? Math.min(valueWidth, finite(p.width) ? p.width : Infinity) : finite(p.width) ? p.width : natural,
          height: ctx.listRow && !ctx.formColumn ? (mac ? MAC_PICKER_MENU_ROW_HEIGHT : PICKER_MENU_ROW_HEIGHT) : mac ? MAC_PICKER_MENU_HEIGHT : PICKER_MENU_HEIGHT,
        },
        style: 'menu',
        font,
        options,
        selected,
        labelWidth,
        valueWidth,
        labelRow: false,
      };
    });
  }

  private placePicker(node: LayoutNode, ctx: Context, p: Proposal, x: number, y: number, size: Size): void {
    const plan = this.pickerPlan(node, ctx, p);
    // Inside a list the row chrome was set before placing; keep it.
    const existing = node.id >= 0 ? this.chrome.get(node.id) : undefined;
    const chrome: Record<string, Rect> = { ...existing };
    if (plan.style === 'segmented') {
      const n = Math.max(1, plan.options.length);
      // In a bar the segments sit inside the capsule, inset all round; `bar` (the
      // control's frame) tells the page to draw it as the bar draws it.
      const inset = plan.bar ? BAR_SEGMENTED_INSET : 0;
      if (plan.bar) chrome['bar'] = { x, y, width: size.width, height: size.height };
      else delete chrome['bar'];
      // The Mac's label before the control (`label`, `track` the control's own frame).
      const labelWidth = Math.min(plan.labelWidth, size.width);
      if (labelWidth > 0) {
        chrome['label'] = { x, y, width: labelWidth - MAC_SEGMENTED_LABEL_GAP, height: size.height };
        chrome['track'] = { x: x + labelWidth, y, width: Math.max(0, size.width - labelWidth), height: size.height };
      } else {
        delete chrome['label'];
        delete chrome['track'];
      }
      const trackX = x + labelWidth;
      const segmentWidth = Math.max(0, size.width - labelWidth - 2 * inset) / n;
      for (let i = 0; i < plan.options.length; i++) {
        chrome[`segment${i}`] = { x: trackX + inset + i * segmentWidth, y: y + inset, width: segmentWidth, height: Math.max(0, size.height - 2 * inset) };
      }
    } else if (plan.style === 'inline') {
      // Rows span the list row when there is one (the hit area and the
      // separators match the other rows), else the control's frame.
      const row = existing?.['row'];
      const rowX = row ? row.x : x;
      const rowWidth = row ? row.width : size.width;
      let rowY = y;
      if (plan.labelRow) {
        chrome['label'] = { x: rowX, y: rowY, width: rowWidth, height: PICKER_INLINE_ROW_HEIGHT };
        rowY += PICKER_INLINE_ROW_HEIGHT;
      }
      for (let i = 0; i < plan.options.length; i++) {
        chrome[`option${i}`] = { x: rowX, y: rowY, width: rowWidth, height: PICKER_INLINE_ROW_HEIGHT };
        rowY += PICKER_INLINE_ROW_HEIGHT;
      }
    } else {
      // Too little room for both: as an HStack shares it, the narrower of
      // the two is offered half first and keeps what it needs; the other
      // takes the rest and truncates (a long value, rather than its label).
      const room = Math.max(0, size.width - PICKER_MENU_GAP);
      let labelWidth = plan.labelWidth;
      let valueWidth = plan.valueWidth;
      // In a Mac form's controls column the pop-up spans the column (its label is in the label column).
      if (!ctx.formColumn && labelWidth + valueWidth > room) {
        if (labelWidth <= valueWidth) {
          labelWidth = Math.min(labelWidth, room / 2);
          valueWidth = room - labelWidth;
        } else {
          valueWidth = Math.min(valueWidth, room / 2);
          labelWidth = room - valueWidth;
        }
      }
      valueWidth = Math.min(valueWidth, size.width);
      chrome['label'] = { x, y, width: labelWidth, height: size.height };
      chrome['value'] = { x: x + size.width - valueWidth, y, width: valueWidth, height: size.height };
      // The page draws the Mac's pop-up button around the value (the engine padded it).
      if (this.env.platform === 'macos' && (!ctx.listRow || ctx.formColumn === true)) chrome['popup'] = chrome['value'];
      else delete chrome['popup'];
    }
    this.setChrome(node, chrome);
    if (node.id >= 0) this.fonts.set(node.id, plan.font);
  }

  // -------------------------------------------------------------------------
  // Stacks
  // -------------------------------------------------------------------------

  private stackPlan(node: LayoutNode, ctx: Context, p: Proposal, axis: Axis): StackPlan {
    return this.plan(node, ctx, p, () => {
      const cctx = this.childContext(node, ctx);
      return this.layoutStack(this.stackChildren(node), cctx, p, axis, this.stackGaps(node, cctx, axis));
    });
  }

  /**
   * The children a stack lays out: all of them, but a collapsed
   * `disclosuregroup`'s label alone (the first child; its content is in
   * the tree whatever the state, as the native renderer wants it).
   */
  private stackChildren(node: LayoutNode): readonly LayoutNode[] {
    return node.kind === 'disclosuregroup' && node.props['expanded'] !== true ? node.children.slice(0, 1) : node.children;
  }

  /**
   * The space between a stack's children: its `spacing`, else the system's.
   * A horizontal stack's default is 8; in a vertical one iOS 26 spaces text
   * by its font (measured in the simulator, Examples/Fidelity's `spacing`
   * screen; `textEdge` finds text at an edge). Between two texts the gap is
   * the lower font's leading less its line height, plus a pixel (about 2 for
   * the text styles); a view above text sits 0.409em − 1.24 above its line
   * (caption 3.67, body 5.71, largeTitle 12.67); text above a view is
   * 0.6em + 1.13 above it (caption 8.33, body 11.33); other views are 8 apart.
   */
  private stackGaps(node: LayoutNode, cctx: Context, axis: Axis): number[] {
    const children = this.stackChildren(node);
    const n = children.length;
    if (n < 2) return [];
    // A disclosure group's content sits 8 under its label, as SwiftUI's does.
    const spacing = num(node.props['spacing']) ?? (node.kind === 'disclosuregroup' ? DEFAULT_SPACING : null);
    // A Mac list row's Label (no icon column there): the title 6 after the icon.
    if (spacing === null && axis === 'horizontal' && cctx.listCell === true && labelColumnOf(node)) return [cctx.sidebarList ? MAC_LIST_SIDEBAR_LABEL_GAP : MAC_LIST_LABEL_GAP];
    if (spacing !== null || axis === 'horizontal') return new Array<number>(n - 1).fill(spacing ?? DEFAULT_SPACING);
    const gaps: number[] = [];
    for (let i = 0; i < n - 1; i++) {
      const above = this.textEdge(children[i] as LayoutNode, cctx, 'bottom');
      const below = this.textEdge(children[i + 1] as LayoutNode, cctx, 'top');
      let gap = DEFAULT_SPACING;
      if (above && below) gap = below.leading - below.lineHeight + 1 / 3;
      else if (below) gap = 0.409 * below.size - 1.24;
      else if (above) gap = 0.6 * above.size + 1.13;
      gaps.push(Math.max(0, Math.round(gap * 3) / 3));
    }
    return gaps;
  }

  /**
   * The font of the text at a view's top or bottom edge, for the default
   * spacing of a vertical stack: a `text`, through modifiers that leave its
   * box alone (not a frame or padding), decorations (their content) and
   * vertical stacks (their first or last child). Null for any other view.
   */
  private textEdge(node: LayoutNode, ctx: Context, edge: 'top' | 'bottom', depth = 0): ResolvedFont | null {
    if (depth > 32) return null;
    switch (node.kind) {
      case 'text':
        return ctx.font;
      case 'styled': {
        const style = styleOf(node);
        const boxed = ['frame', 'padding', 'containerRelativeFrame', 'aspectRatio', 'position', 'offset'].some((key) => style[key] !== undefined && style[key] !== null);
        const child = node.children[0];
        return !boxed && node.children.length === 1 && child ? this.textEdge(child, this.childContext(node, ctx), edge, depth + 1) : null;
      }
      case 'decorated': {
        const content = node.children[0];
        return content ? this.textEdge(content, this.childContext(node, ctx), edge, depth + 1) : null;
      }
      case 'vstack':
      case 'disclosuregroup': {
        const children = this.stackChildren(node);
        const child = edge === 'top' ? children[0] : children[children.length - 1];
        return child && !isSpacer(child) ? this.textEdge(child, this.childContext(node, ctx), edge, depth + 1) : null;
      }
      case 'hstack': {
        // A row with text in it (Food Truck's flavor line: a symbol and its
        // name) is spaced as its largest text, as iOS 26 spaces it.
        const cctx = this.childContext(node, ctx);
        let font: ResolvedFont | null = null;
        for (const child of node.children) {
          const found = this.textEdge(child, cctx, edge, depth + 1);
          if (found && (!font || found.size > font.size)) font = found;
        }
        return font;
      }
      default:
        return null;
    }
  }

  /**
   * SwiftUI's stack algorithm. With an axis proposal, every child's minimum
   * and maximum along the axis (proposals 0 and Infinity, cross fixed) give
   * its flexibility; space is handed out by descending `layoutPriority`, then
   * least flexible first. A child is offered `remaining / childrenLeft`, where
   * `remaining` excludes the minimum sizes of every lower-priority child and
   * `childrenLeft` counts only the unsized children of its own priority group
   * with *finite* flexibility: infinitely flexible children (colors,
   * `maxWidth: infinity` frames, text fields) only reserve their minimum and
   * share whatever is left once everything else is sized, which is why a
   * `Spacer` never makes its sibling text wrap. Spacers come after those: they
   * reserve their minimum while the other infinitely flexible children share,
   * and take what is left (iOS gives `HStack { VStack { … }.frame(maxWidth:
   * .infinity); Spacer() }`'s stack all but the spacer's 8 points, where an
   * even split gave Ice Cubes' quoted posts half their card). With a `null`
   * axis proposal every child is proposed `null` and the stack sizes to the sum.
   */
  /** A list row's `Label` in iOS's icon column: the icon at its own size, the title in what is left past 40. */
  private labelColumnPlan(
    label: { icon: LayoutNode; title: LayoutNode },
    cctx: Context,
    p: Proposal,
  ): { size: Size; icon: Size; title: Size; iconProposal: Proposal; titleProposal: Proposal } {
    const iconProposal: Proposal = { width: null, height: p.height };
    const icon = this.measure(label.icon, cctx, iconProposal);
    const titleProposal: Proposal = { width: finite(p.width) ? Math.max(0, p.width - LIST_LABEL_TITLE_X) : p.width, height: p.height };
    const title = this.measure(label.title, cctx, titleProposal);
    return {
      size: { width: LIST_LABEL_TITLE_X + title.width, height: Math.max(icon.height, title.height) },
      icon,
      title,
      iconProposal,
      titleProposal,
    };
  }

  private layoutStack(
    children: readonly LayoutNode[],
    cctx: Context,
    p: Proposal,
    axis: Axis,
    gaps: number[],
  ): StackPlan {
    const n = children.length;
    if (n === 0) return { size: ZERO, childSizes: [], childProposals: [], gaps };
    const along = axis === 'horizontal' ? p.width : p.height;
    const cross = axis === 'horizontal' ? p.height : p.width;
    const totalSpacing = gaps.reduce((sum, gap) => sum + gap, 0);
    const childSizes: Size[] = new Array<Size>(n);
    const childProposals: Proposal[] = new Array<Proposal>(n);

    if (along === null) {
      const proposal = makeProposal(axis, null, cross);
      for (let i = 0; i < n; i++) {
        childProposals[i] = proposal;
        childSizes[i] = this.measure(children[i] as LayoutNode, cctx, proposal);
      }
    } else {
      const mins = new Array<number>(n);
      const flex = new Array<number>(n);
      const priorities = new Array<number>(n);
      const spacers = children.map(isSpacer);
      const minProposal = makeProposal(axis, 0, cross);
      const maxProposal = makeProposal(axis, Infinity, cross);
      for (let i = 0; i < n; i++) {
        const child = children[i] as LayoutNode;
        const min = axisOf(this.measure(child, cctx, minProposal), axis);
        const max = axisOf(this.measure(child, cctx, maxProposal), axis);
        mins[i] = min;
        flex[i] = Number.isFinite(max) ? Math.max(0, max - min) : Infinity;
        priorities[i] = layoutPriorityOf(child);
      }
      const order = Array.from({ length: n }, (_, i) => i).sort((a, b) => {
        const pa = priorities[a] as number;
        const pb = priorities[b] as number;
        if (pa !== pb) return pb - pa;
        const fa = flex[a] as number;
        const fb = flex[b] as number;
        if (fa !== fb) return fa < fb ? -1 : 1;
        if (spacers[a] !== spacers[b]) return spacers[a] ? 1 : -1;
        return a - b;
      });

      let remaining = Math.max(0, along - totalSpacing);
      let i = 0;
      while (i < n) {
        // One priority group at a time (sorted least flexible first, so the
        // infinitely flexible children of a group come last).
        const priority = priorities[order[i] as number] as number;
        let end = i;
        while (end < n && priorities[order[end] as number] === priority) end++;
        let lowerMin = 0;
        for (let j = end; j < n; j++) lowerMin += mins[order[j] as number] as number;
        let finiteLeft = 0;
        let infiniteLeft = 0;
        let infiniteMin = 0;
        let spacersLeft = 0;
        let spacerMin = 0;
        for (let k = i; k < end; k++) {
          const index = order[k] as number;
          if (Number.isFinite(flex[index])) finiteLeft++;
          else if (spacers[index]) {
            spacersLeft++;
            spacerMin += mins[index] as number;
          } else {
            infiniteLeft++;
            infiniteMin += mins[index] as number;
          }
        }
        for (let k = i; k < end; k++) {
          const index = order[k] as number;
          const isInfinite = !Number.isFinite(flex[index]);
          const isSpacerChild = isInfinite && spacers[index] === true;
          let offer: number;
          if (!Number.isFinite(remaining)) offer = Infinity;
          else if (isSpacerChild) offer = Math.max(0, remaining - lowerMin) / spacersLeft;
          else if (isInfinite) offer = Math.max(0, remaining - lowerMin - spacerMin) / infiniteLeft;
          else offer = Math.max(0, remaining - lowerMin - infiniteMin - spacerMin) / finiteLeft;
          const proposal = makeProposal(axis, offer, cross);
          const size = this.measure(children[index] as LayoutNode, cctx, proposal);
          childProposals[index] = proposal;
          childSizes[index] = size;
          if (Number.isFinite(remaining)) remaining = Math.max(0, remaining - axisOf(size, axis));
          if (isSpacerChild) {
            spacersLeft--;
            spacerMin -= mins[index] as number;
          } else if (isInfinite) {
            infiniteLeft--;
            infiniteMin -= mins[index] as number;
          } else finiteLeft--;
        }
        i = end;
      }
    }

    let total = totalSpacing;
    let crossSize = 0;
    for (let i = 0; i < n; i++) {
      const size = childSizes[i] as Size;
      total += axisOf(size, axis);
      crossSize = Math.max(crossSize, crossOf(size, axis));
    }
    const size: Size =
      axis === 'horizontal' ? { width: total, height: crossSize } : { width: crossSize, height: total };
    return { size, childSizes, childProposals, gaps };
  }

  private placeStack(
    children: readonly LayoutNode[],
    cctx: Context,
    plan: StackPlan,
    axis: Axis,
    alignment: string,
    x: number,
    y: number,
    size: Size,
  ): void {
    const anchors = alignmentAnchors(alignment);
    const crossAnchor = axis === 'horizontal' ? anchors[1] : anchors[0];
    const crossSize = crossOf(size, axis);
    // `.alignmentGuide`: the children's guides for the stack's alignment
    // line up. A child's guide is its explicit one, else the default (none,
    // half or all of its cross size); the line is the stack's own guide, or
    // further along when a guide reaches past it (the children move with it).
    const guideAxis = axis === 'horizontal' ? 'vertical' : 'horizontal';
    const guideName = guideNames(anchors)[axis === 'horizontal' ? 1 : 0];
    const guides = children.map((child, i) => {
      const explicit = explicitGuide(child, guideAxis, guideName);
      return explicit ? guideValue(explicit, plan.childSizes[i] as Size) : null;
    });
    const line = guides.some((g) => g !== null) && Number.isFinite(crossSize)
      ? Math.max(crossSize * crossAnchor, ...guides.filter((g): g is number => g !== null))
      : null;
    let cursor = 0;
    for (let i = 0; i < children.length; i++) {
      const child = children[i] as LayoutNode;
      const childSize = plan.childSizes[i] as Size;
      const proposal = plan.childProposals[i] as Proposal;
      const crossOffset = line === null
        ? safeOffset(crossSize, crossOf(childSize, axis), crossAnchor)
        : line - (guides[i] ?? crossOf(childSize, axis) * crossAnchor);
      if (axis === 'horizontal') this.place(child, cctx, proposal, x + cursor, y + crossOffset);
      else this.place(child, cctx, proposal, x + crossOffset, y + cursor);
      cursor += axisOf(childSize, axis) + (plan.gaps[i] ?? 0);
    }
  }

  // -------------------------------------------------------------------------
  // Image
  // -------------------------------------------------------------------------

  /**
   * An SF Symbol (`systemName`) and a catalog `symbol` are glyphs: a square
   * of the font's line height. A catalog `image` has an intrinsic point size;
   * `.resizable()` makes it fill whatever is proposed (the intrinsic size on
   * a nil axis, like SwiftUI). A name the manifest does not know measures a
   * fixed 44×44 so the placeholder is visible.
   */
  private measureImage(node: LayoutNode, ctx: Context, p: Proposal): Size {
    if (typeof node.props['url'] === 'string' || typeof node.props['bitmap'] === 'object') {
      // Phase 13: a remote image carries its own point size, as does a bitmap.
      const width = num(node.props['width']) ?? 0;
      const height = num(node.props['height']) ?? 0;
      if (node.props['resizable'] !== true) return { width, height };
      return { width: p.width ?? width, height: p.height ?? height };
    }
    const name = str(node.props['name']);
    // A symbol takes a square of the font's leading; in a Mac list row, of
    // its point size (13 in a 13-point row, measured in the Mac app).
    const symbolSide = (ctx.listCell === true ? ctx.font.size : ctx.font.leading) * ctx.imageScale;
    if (name === null) return square(symbolSide);
    const asset = this.env.assets?.images[name];
    if (!asset) return square(UNKNOWN_IMAGE_SIDE);
    if (asset.kind === 'symbol') return square(symbolSide);
    const width = num(asset.width) ?? 0;
    const height = num(asset.height) ?? 0;
    if (node.props['resizable'] !== true) return { width, height };
    return { width: p.width ?? width, height: p.height ?? height };
  }

  // -------------------------------------------------------------------------
  // Styled
  // -------------------------------------------------------------------------

  /**
   * One `styled` element may carry several layout effects; they nest as
   * frame (outermost) > padding > fixedSize > aspectRatio > child.
   */
  private styledPlan(node: LayoutNode, ctx: Context, p: Proposal): StyledPlan {
    return this.plan(node, ctx, p, () => {
      const style = styleOf(node);
      let frame = readFrame(style['frame']);
      // Phase 12 `.containerRelativeFrame`: an affine size against the nearest
      // container (screen or scroll viewport), taking the place of a fixed frame.
      const relative = readContainerRelativeFrame(style['containerRelativeFrame']);
      if (relative) {
        const base: FrameSpec = frame ?? { width: null, height: null, minWidth: null, maxWidth: null, minHeight: null, maxHeight: null, alignment: relative.alignment };
        frame = {
          ...base,
          alignment: frame?.alignment ?? relative.alignment,
          width: relative.horizontal ? Math.max(0, relative.a * ctx.container.width + relative.b) : base.width,
          height: relative.vertical ? Math.max(0, relative.a * ctx.container.height + relative.b) : base.height,
        };
      }
      const padding = readInsets(style['padding']);
      const aspect = readAspectRatio(style['aspectRatio']);
      const fixed = obj(style['fixedSize']);
      const fixedH = fixed?.['horizontal'] === true;
      const fixedV = fixed?.['vertical'] === true;
      const cctx = this.childContext(node, ctx);
      const child = this.contentOf(node);

      // Frame step: the proposal handed to the padded content.
      let innerW: number | null = p.width;
      let innerH: number | null = p.height;
      if (frame) {
        if (frame.width !== null) innerW = frame.width;
        else if (frame.minWidth !== null || frame.maxWidth !== null)
          innerW = p.width === null ? null : clamp(p.width, frame.minWidth, frame.maxWidth);
        if (frame.height !== null) innerH = frame.height;
        else if (frame.minHeight !== null || frame.maxHeight !== null)
          innerH = p.height === null ? null : clamp(p.height, frame.minHeight, frame.maxHeight);
      }
      const framedProposal: Proposal = { width: innerW, height: innerH };

      // Padding step.
      const padW = padding ? padding.leading + padding.trailing : 0;
      const padH = padding ? padding.top + padding.bottom : 0;
      let childProposal: Proposal = {
        width: fixedH ? null : shrink(framedProposal.width, padW),
        height: fixedV ? null : shrink(framedProposal.height, padH),
      };
      // Aspect ratio step: the child is proposed the fitted (or filling) size.
      if (aspect && child) childProposal = this.aspectProposal(child, cctx, childProposal, aspect);
      // Phase 6 `.position(x:y:)`: the box fills the proposal (the child's
      // ideal size on nil axes); the child is measured with a nil proposal.
      const point = readPoint(style['position']);
      let position: StyledPlan['position'] = null;
      let childSize: Size;
      if (point && child) {
        const ideal = this.measure(child, cctx, NIL_PROPOSAL);
        position = { x: point.x, y: point.y, ideal };
        childSize = {
          width: finite(childProposal.width) ? childProposal.width : ideal.width,
          height: finite(childProposal.height) ? childProposal.height : ideal.height,
        };
      } else {
        childSize = child ? this.measure(child, cctx, childProposal) : ZERO;
      }
      let padded: Size = { width: childSize.width + padW, height: childSize.height + padH };

      // Report back through the frame.
      let width = padded.width;
      let height = padded.height;
      if (frame) {
        if (frame.width !== null) width = frame.width;
        else if (frame.minWidth !== null || frame.maxWidth !== null)
          width = flexibleFrameLength(p.width, padded.width, frame.minWidth, frame.maxWidth);
        if (frame.height !== null) height = frame.height;
        else if (frame.minHeight !== null || frame.maxHeight !== null)
          height = flexibleFrameLength(p.height, padded.height, frame.minHeight, frame.maxHeight);
        // A flexible frame proposes its own resolved size to the child (so
        // `Rectangle().frame(minHeight: 100)` fills 100, as in SwiftUI). Only
        // when a nil-proposed axis was clamped up to a minimum does the child
        // see a proposal it has not seen yet.
        const grewW = frame.width === null && p.width === null && width > padded.width;
        const grewH = frame.height === null && p.height === null && height > padded.height;
        if ((grewW || grewH) && child && !position) {
          const again: Proposal = {
            width: grewW ? Math.max(0, width - padW) : childProposal.width,
            height: grewH ? Math.max(0, height - padH) : childProposal.height,
          };
          childProposal = again;
          childSize = this.measure(child, cctx, again);
          padded = { width: childSize.width + padW, height: childSize.height + padH };
        }
      }
      const contentBox: Rect = {
        x: padding ? padding.leading : 0,
        y: padding ? padding.top : 0,
        width: Math.max(0, width - padW),
        height: Math.max(0, height - padH),
      };
      return {
        size: { width, height },
        childSize,
        childProposal,
        contentBox,
        alignment: frame?.alignment ?? 'center',
        position,
      };
    });
  }

  /**
   * `.aspectRatio(_:contentMode:)`: the size with the ratio (the child's ideal
   * ratio when `ratio` is null) that fits inside the proposal, or covers it
   * for `fill`. One nil/infinite axis is derived from the other; with neither
   * axis given the child's ideal size is proposed. The child reports its own
   * size for that proposal (a resizable image or a shape takes it exactly).
   */
  private aspectProposal(child: LayoutNode, cctx: Context, p: Proposal, aspect: AspectRatioSpec): Proposal {
    const ideal = (): Size => this.measure(child, cctx, { width: null, height: null });
    let ratio = aspect.ratio;
    if (ratio === null) {
      const size = ideal();
      ratio = size.width > 0 && size.height > 0 && Number.isFinite(size.width / size.height) ? size.width / size.height : 1;
    }
    // An unbounded box on both axes: the child is unbounded too (a flexible
    // child fills it, so `Color.aspectRatio(1)` is not pinned to its ideal
    // when a custom layout probes its maximum).
    if (p.width === Infinity && p.height === Infinity) return { width: Infinity, height: Infinity };
    const pw = finite(p.width) ? p.width : null;
    const ph = finite(p.height) ? p.height : null;
    if (pw !== null && ph !== null) {
      // Fit: the larger of the two candidate sizes still inside the box;
      // fill: the smaller one that covers it. Comparing the box's ratio to
      // the target decides which axis binds.
      const boxWider = ph === 0 ? true : pw / ph > ratio;
      const byHeight = boxWider === !aspect.fill;
      return byHeight ? { width: ph * ratio, height: ph } : { width: pw, height: pw / ratio };
    }
    if (pw !== null) return { width: pw, height: pw / ratio };
    if (ph !== null) return { width: ph * ratio, height: ph };
    const size = ideal();
    return { width: size.width, height: size.height };
  }

  // -------------------------------------------------------------------------
  // ScrollView
  // -------------------------------------------------------------------------

  private scrollAxes(node: LayoutNode): { horizontal: boolean; vertical: boolean } {
    const axes = str(node.props['axes']) ?? 'vertical';
    return {
      horizontal: axes === 'horizontal' || axes === 'both',
      vertical: axes === 'vertical' || axes === 'both',
    };
  }

  private scrollPlan(node: LayoutNode, ctx: Context, p: Proposal): ScrollPlan {
    return this.plan(node, ctx, p, () => {
      const axes = this.scrollAxes(node);
      const content = this.contentOf(node, axes.horizontal && !axes.vertical ? 'horizontal' : 'vertical');
      // Phase 12: the viewport is the container `containerRelativeFrame` measures against.
      const viewport: Size = {
        width: finite(p.width) ? p.width : ctx.container.width,
        height: finite(p.height) ? p.height : ctx.container.height,
      };
      const cctx: Context = { ...this.childContext(node, ctx), container: viewport };
      // Along scroll axes the content is unconstrained; elsewhere it gets the
      // scroll view's own size (which is the proposal when one was given).
      const contentProposal: Proposal = {
        width: axes.horizontal ? null : finite(p.width) ? p.width : null,
        height: axes.vertical ? null : finite(p.height) ? p.height : null,
      };
      const contentSize = content ? this.measure(content, cctx, contentProposal) : ZERO;
      const size: Size = {
        width: p.width === null ? contentSize.width : p.width,
        height: p.height === null ? contentSize.height : p.height,
      };
      return { size, content, cctx, contentProposal, contentSize };
    });
  }

  // -------------------------------------------------------------------------
  // List
  // -------------------------------------------------------------------------

  /** A list's style: its own, but on a Mac an automatic list in a split view's sidebar is a sidebar, as SwiftUI's is. */
  private listStyleOf(node: LayoutNode, ctx: Context): string {
    const style = str(node.props['style']) ?? 'insetGrouped';
    return this.env.platform === 'macos' && ctx.splitSidebar && style === 'insetGrouped' ? 'sidebar' : style;
  }

  private listPlan(node: LayoutNode, ctx: Context, p: Proposal): ListPlan {
    return this.plan(node, ctx, p, () => {
      if (this.env.platform === 'macos' && node.props['form'] === true && node.props['formStyle'] !== 'grouped') return this.macFormPlan(node, ctx, p);
      const { screen } = this.env;
      const width = finite(p.width) ? p.width : p.width === Infinity ? Infinity : screen.width;
      const layoutWidth = Number.isFinite(width) ? width : screen.width;
      // `insetGrouped` (default) insets the cards 16; `grouped` and `plain`
      // sections are full width. `plain` also drops the top/bottom gaps and
      // the space between sections. `sidebar` (iPadOS) is laid out like
      // `insetGrouped` with 44pt rows (the page draws no cards or hairlines).
      const listStyle = this.listStyleOf(node, ctx);
      // In a compact width (an iPhone) a `sidebar` list looks like `insetGrouped`
      // (cards, chevrons, 52pt rows) and only its sections' folding remains.
      const sidebarStyle = listStyle === 'sidebar' && isRegularWidth(screen);
      // `.environment(\.defaultMinListRowHeight, _)` (the list's `minRowHeight`); 52 by default (44 in a sidebar, 24 on a Mac).
      // The Mac idiom (MAC_LIST_* above): smaller rows, margins and gaps,
      // 11-point bold headers, a sidebar's rows inset 10 and a grouped
      // form's cards 20.
      const mac = this.env.platform === 'macos';
      const macGrouped = mac && node.props['form'] === true && node.props['formStyle'] === 'grouped';
      // The list's own minimum, else a Mac grouped form's rows (a form's, not a list's), else the one set above it, else the platform's.
      const minRowHeight =
        num(node.props['minRowHeight']) ??
        (macGrouped ? MAC_GROUPED_ROW_HEIGHT : null) ??
        ctx.minListRowHeight ??
        (sidebarStyle ? (mac ? MAC_LIST_SIDEBAR_ROW_MIN_HEIGHT : LIST_SIDEBAR_ROW_MIN_HEIGHT) : mac ? MAC_LIST_ROW_MIN_HEIGHT : LIST_ROW_MIN_HEIGHT);
      // A Mac's lists have no cards: the rows run the whole width (a
      // sidebar's inset 10).
      const inset = mac
        ? macGrouped
          ? Math.max(MAC_GROUPED_MARGIN, (layoutWidth - MAC_GROUPED_MAX_WIDTH) / 2)
          : listStyle === 'sidebar'
            ? MAC_LIST_INSET_SIDEBAR
            // SwiftUI's inset list style: the list's own 16, which
            // `listRowInsets` does not touch (its rows have no padding of
            // their own); a row's separator runs to the trailing edge of it.
            : MAC_LIST_CELL_PADDING
        : listStyle === 'insetGrouped' || listStyle === 'sidebar'
          ? LIST_INSET
          : 0;
      const plain = listStyle === 'plain';
      const cellPadding = mac
        ? macGrouped ? MAC_GROUPED_CELL_PADDING : listStyle === 'sidebar' ? MAC_LIST_SIDEBAR_CELL_PADDING : 0
        : LIST_CELL_PADDING;
      const macSidebar = mac && listStyle === 'sidebar';
      const listTop = plain || macSidebar ? 0 : macGrouped ? MAC_GROUPED_TOP : mac ? MAC_LIST_TOP : LIST_TOP;
      const listBottom = plain ? 0 : macGrouped ? MAC_GROUPED_TOP : mac ? MAC_LIST_BOTTOM : LIST_BOTTOM;
      const sectionGap = plain ? 0 : macGrouped ? MAC_GROUPED_SECTION_GAP : macSidebar ? MAC_LIST_SIDEBAR_SECTION_GAP : mac ? MAC_LIST_SECTION_GAP : LIST_SECTION_GAP;
      const headerGap = macGrouped ? MAC_GROUPED_HEADER_GAP : macSidebar ? MAC_LIST_SIDEBAR_HEADER_GAP : mac ? MAC_LIST_HEADER_GAP : LIST_HEADER_GAP;
      const cardX = inset;
      const cardWidth = Math.max(0, layoutWidth - 2 * inset);
      const contentX = inset + cellPadding;
      const contentWidth = Math.max(0, layoutWidth - 2 * (inset + cellPadding));
      // iOS 26: headers in the headline size (semibold), footers in footnote.
      const headerFont = macGrouped
        ? makeFont('system', textStyleSize('body', this.env.dynamicTypeSize, 'macos'), 600)
        : mac
        ? makeFont('system', textStyleSize('subheadline', this.env.dynamicTypeSize, 'macos'), 700)
        : makeFont('system', textStyleSize('headline', this.env.dynamicTypeSize, this.env.platform), 600);
      const footerFont = makeFont(
        'system',
        textStyleSize('footnote', this.env.dynamicTypeSize, this.env.platform),
        400,
      );
      // A Mac's rows have no icon column: a Label is its icon and title, 6 apart.
      const rowCtx: Context = {
        ...this.childContext(node, ctx),
        listRow: true,
        labelColumn: !mac,
        listCell: mac,
        sidebarList: listStyle === 'sidebar',
        ...(mac && listStyle === 'sidebar' ? { lineLimit: 1 } : {}),
        ...(macGrouped ? { groupedRow: true } : {}),
      };
      // Phase 8: in edit mode a selectable or deletable `listrow` gains a 44
      // leading control (6 in from the card's edge) and its content shifts
      // right by 40; a movable one a trailing grip, which narrows it by 40.
      const editing = node.props['editing'] === true;

      // Group consecutive plain rows into an implicit section. A section's
      // view header and footer are not rows.
      type GroupSource = { section: LayoutNode | null; rows: RowSource[]; headerView: LayoutNode | null; footerView: LayoutNode | null };
      const groupsSource: GroupSource[] = [];
      for (const child of node.children) {
        if (child.kind === 'section') {
          // `Section(isExpanded:)`: collapsed, its rows are not laid out, in a sidebar-style list (iOS shows the rows in the other styles, whatever the binding).
          const collapsed = child.props['collapsible'] === true && child.props['expanded'] === false && rowCtx.sidebarList === true;
          const rows = collapsed ? [] : outlineRows(child.children.filter((row) => row.kind !== 'sectionheader' && row.kind !== 'sectionfooter'), 0, []);
          const headerView = child.children.find((row) => row.kind === 'sectionheader') ?? null;
          const footerView = child.children.find((row) => row.kind === 'sectionfooter') ?? null;
          groupsSource.push({ section: child, rows, headerView, footerView });
        } else {
          const last = groupsSource[groupsSource.length - 1];
          if (last && last.section === null) outlineRows([child], 0, last.rows);
          else groupsSource.push({ section: null, rows: outlineRows([child], 0, []), headerView: null, footerView: null });
        }
      }
      // View headers and footers: the content width, the header's and the
      // footer's font.
      const headerCtx: Context = { ...rowCtx, listRow: false, stackAxis: null, font: headerFont };
      const footerCtx: Context = { ...headerCtx, font: footerFont };
      const supplement = (view: LayoutNode | null, y: number, ctx: Context, full = false): ListSupplementPlan | null => {
        if (!view) return null;
        const proposal: Proposal = { width: contentWidth, height: null };
        const size = this.measure(view, ctx, proposal);
        // A collapsible section's header spans the content width: its chevron sits at the trailing edge.
        return { node: view, ctx, proposal, rect: { x: contentX, y, width: full ? contentWidth : size.width, height: size.height } };
      };

      const groups: ListGroupPlan[] = [];
      // A Mac grouped form's text fields: their titles lead their rows (the page draws them as the columns form's labels).
      const fieldLabels: FormLabelPlan[] = [];
      let y = listTop;
      const makeText = (text: string, font: ResolvedFont): { layout: TextLayout; size: Size } => {
        const wrapped = wrapText(text, font, contentWidth, null, this.measurer);
        return {
          layout: { font, lines: wrapped.lines, alignment: 'leading', truncated: false },
          size: { width: wrapped.width, height: textHeight(wrapped.lines.length, font) },
        };
      };
      for (let g = 0; g < groupsSource.length; g++) {
        const source = groupsSource[g] as GroupSource;
        const previous = groups[g - 1];
        if (previous) {
          const footed = previous.footer !== null || (previous.footerView !== null && previous.footerView.rect.height > 0);
          y += plain || mac ? sectionGap : footed ? LIST_SECTION_GAP_AFTER_FOOTER : sectionGap;
        }
        // A Mac list's first header sits 4 above where its first row would.
        if (mac && !macGrouped && !macSidebar && !previous && (source.headerView !== null || (source.section !== null && (str(source.section.props['header']) ?? '').length > 0))) y -= MAC_LIST_FIRST_HEADER_LIFT;
        const top = y;
        let header: ListGroupPlan['header'] = null;
        let footer: ListGroupPlan['footer'] = null;
        // A header sits in the gap above its card (from LIST_HEADER_LIFT
        // into it); the card then starts LIST_HEADER_GAP below the header.
        const headerLift = plain || mac ? 0 : LIST_HEADER_LIFT;
        const headerView = supplement(source.headerView, y - headerLift, headerCtx, source.section?.props['collapsible'] === true);
        if (headerView && headerView.rect.height > 0) y += headerView.rect.height + headerGap - headerLift;
        const headerText = source.section ? str(source.section.props['header']) : null;
        const footerText = source.section ? str(source.section.props['footer']) : null;
        if (headerText !== null && headerText.length > 0) {
          const { layout, size } = makeText(headerText, headerFont);
          if (mac && !macGrouped && previous) y += MAC_LIST_HEADER_TOP;
          else if (macSidebar) y += MAC_LIST_SIDEBAR_HEADER_TOP;
          const headerY = headerView && headerView.rect.height > 0 ? y : y - headerLift;
          const headerX = macSidebar ? inset + MAC_LIST_SIDEBAR_HEADER_INSET : contentX;
          header = { rect: { x: headerX, y: headerY, width: contentWidth, height: size.height }, layout };
          y = headerY + size.height + headerGap + (mac && !macSidebar && !macGrouped && !previous ? MAC_LIST_FIRST_ROWS_GAP : 0);
        }
        const cardTop = y;
        const rows: ListRowPlan[] = [];
        for (const rowSource of source.rows) {
          const rowNode = rowSource.node;
          if (rowNode.kind === 'lazyspacer') {
            // Lazy rows not built yet: a row of no height, without a cell.
            const rect: Rect = { x: cardX, y, width: cardWidth, height: 0 };
            rows.push({ node: rowNode, proposal: { width: cardWidth, height: 0 }, contentSize: ZERO, row: rect, content: rect, badge: null, edit: null });
            continue;
          }
          // A menu or inline picker already reports its row height (44 for
          // a menu, 44 per option inline); a toggle, stepper or segmented
          // picker has its own vertical padding, other rows 13. Phase 6
          // `listRowInsets` replaces all four insets; a `badge` reserves its
          // width (plus a gap) at the trailing edge.
          const hints = rowHintsOf(rowNode);
          const defaultVPad = macGrouped
            ? unwrapStyled(rowNode).kind === 'button' ? MAC_GROUPED_BUTTON_ROW_VPAD : MAC_GROUPED_ROW_VPAD
            : listRowVPad(unwrapStyled(rowNode), sidebarStyle, mac);
          // An outline's rows start LIST_OUTLINE_INDENT further in per level.
          const indent = rowSource.depth * LIST_OUTLINE_INDENT;
          const insets: EdgeInsets = hints.insets
            ? { ...hints.insets, leading: hints.insets.leading + indent }
            : {
                top: defaultVPad,
                leading: cellPadding + indent,
                bottom: defaultVPad,
                trailing: cellPadding,
              };
          // A group's label row keeps the chevron's width at its trailing edge, as a link's does (a Mac's rows have no chevron).
          const chevronReserve = rowSource.group && !mac ? LIST_CHEVRON_WIDTH : 0;
          // A Mac sidebar's badge is the small (11) text (macOS 26).
          const badgeFont = macSidebar ? makeFont('system', MAC_LIST_SIDEBAR_BADGE_FONT_SIZE, 400) : rowCtx.font;
          const badgeWidth = hints.badge !== null ? this.measurer.measureWidth(hints.badge, badgeFont) : 0;
          const badgeReserve = hints.badge !== null ? badgeWidth + BADGE_GAP : 0;
          // Phase 8 edit mode: a selectable or deletable `listrow` gains a
          // leading control and its content shifts right; a movable one a
          // trailing grip, which narrows its content.
          const listRow = editing ? listRowOf(rowNode) : null;
          const hasControl = listRow !== null && (listRow.props['selectable'] === true || listRow.props['deletable'] === true);
          const hasGrip = listRow !== null && listRow.props['movable'] === true;
          const editShift = hasControl ? LIST_EDIT_CONTROL_SHIFT : 0;
          const gripReserve = hasGrip ? LIST_EDIT_CONTROL_SHIFT : 0;
          const fieldNode = macGrouped ? unwrapStyled(rowNode) : null;
          const fieldLabel = fieldNode && fieldNode.kind === 'textfield' ? str(fieldNode.props['placeholder']) || null : null;
          const fieldLabelWidth = fieldLabel ? this.measurer.measureWidth(fieldLabel, rowCtx.font) : 0;
          const fieldShift = fieldLabel ? Math.ceil(fieldLabelWidth) + MAC_GROUPED_LABEL_GAP : 0;
          const rowContentX = cardX + insets.leading + editShift + fieldShift;
          const rowContentWidth = Math.max(0, cardWidth - insets.leading - insets.trailing - badgeReserve - editShift - gripReserve - chevronReserve - fieldShift);
          const proposal: Proposal = { width: rowContentWidth, height: null };
          const contentSize = this.measure(rowNode, rowCtx, proposal);
          const rowHeight = Math.max(minRowHeight, contentSize.height + insets.top + insets.bottom);
          const row: Rect = { x: cardX, y, width: cardWidth, height: rowHeight };
          rows.push({
            node: rowNode,
            proposal,
            contentSize,
            row,
            content: {
              x: rowContentX,
              y: y + insets.top + (rowHeight - insets.top - insets.bottom - contentSize.height) / 2,
              width: contentSize.width,
              height: contentSize.height,
            },
            badge:
              hints.badge !== null
                ? {
                    rect: { x: cardX + cardWidth - insets.trailing - badgeWidth, y, width: badgeWidth, height: rowHeight },
                    layout: { font: badgeFont, lines: [hints.badge], alignment: 'trailing', truncated: false },
                  }
                : null,
            edit: listRow
              ? {
                  listRow,
                  control: hasControl ? { x: cardX + LIST_EDIT_CONTROL_INSET, y, width: LIST_EDIT_CONTROL_WIDTH, height: rowHeight } : null,
                  reorder: hasGrip
                    ? { x: cardX + cardWidth - LIST_EDIT_CONTROL_INSET - LIST_EDIT_CONTROL_WIDTH, y, width: LIST_EDIT_CONTROL_WIDTH, height: rowHeight }
                    : null,
                }
              : null,
            separatorHidden: hints.separatorHidden,
            outline: rowSource.group || rowSource.depth > 0 ? { depth: rowSource.depth, group: rowSource.group } : null,
          });
          if (fieldLabel) {
            fieldLabels.push({
              rect: { x: cardX + insets.leading, y: y + (rowHeight - rowCtx.font.lineHeight) / 2, width: Math.ceil(fieldLabelWidth), height: rowCtx.font.lineHeight },
              layout: { font: rowCtx.font, lines: [fieldLabel], alignment: 'leading', truncated: false },
              node: null,
              ctx: rowCtx,
            });
          }
          y += rowHeight;
          // A Mac grouped form's separators are a point between its rows.
          if (macGrouped && rowSource !== source.rows[source.rows.length - 1]) y += MAC_GROUPED_SEPARATOR;
        }
        // Each group's rect: its label's row, then the rows under it that sit deeper, up to the next at its level or above.
        const outlines: ListGroupPlan['outlines'] = [];
        rows.forEach((row, i) => {
          const group = row.outline?.group;
          if (!group) return;
          const depth = row.outline?.depth ?? 0;
          let end = i;
          while (end + 1 < rows.length && (rows[end + 1]?.outline?.depth ?? 0) > depth) end += 1;
          const last = (rows[end] as ListRowPlan).row;
          outlines.push({ group, frame: { x: row.row.x, y: row.row.y, width: row.row.width, height: last.y + last.height - row.row.y } });
        });
        const card: Rect = { x: cardX, y: cardTop, width: cardWidth, height: y - cardTop };
        if (footerText !== null && footerText.length > 0) {
          y += macGrouped ? MAC_GROUPED_FOOTER_GAP : mac ? MAC_LIST_FOOTER_GAP : LIST_FOOTER_GAP;
          const { layout, size } = makeText(footerText, footerFont);
          footer = { rect: { x: contentX, y, width: contentWidth, height: size.height }, layout };
          y += size.height + (mac && !macGrouped ? MAC_LIST_FOOTER_BOTTOM : 0);
        }
        let footerView = supplement(source.footerView, y + LIST_FOOTER_GAP, footerCtx);
        if (footerView && footerView.rect.height > 0) y += LIST_FOOTER_GAP + footerView.rect.height;
        else if (footerView) footerView = { ...footerView, rect: { ...footerView.rect, y } };
        groups.push({
          section: source.section,
          frame: { x: cardX, y: top, width: cardWidth, height: y - top },
          header,
          footer,
          headerView,
          footerView,
          card,
          rows,
          outlines,
        });
      }
      const contentHeight = groupsSource.length === 0 ? listTop : y + listBottom;
      const size: Size = {
        width,
        height: p.height === null ? contentHeight : p.height,
      };
      return { size, contentSize: { width: layoutWidth, height: contentHeight }, groups, ...(fieldLabels.length > 0 ? { labels: fieldLabels } : {}) };
    });
  }

  // -------------------------------------------------------------------------
  // Place
  // -------------------------------------------------------------------------

  /**
   * Phase 9: placed before, same proposal, same origin, nothing in the
   * subtree invalidated since: the last run's output still holds.
   */
  private placedAlready(node: LayoutNode, ctx: Context, p: Proposal, x: number, y: number): boolean {
    const last = this.placed.get(node);
    return last !== undefined && last.key === planKey(ctx, p) && last.x === x && last.y === y;
  }

  place(node: LayoutNode, ctx: Context, p: Proposal, x: number, y: number): void {
    if (this.placedAlready(node, ctx, p, x, y)) {
      this.stats.skipped += 1;
      return;
    }
    const key = planKey(ctx, p);
    this.placed.set(node, { key, x, y });
    this.stats.placed += 1;
    if (node.id >= 0) {
      this.touched.add(node.id);
      // Chrome from an earlier run is stale (a parent's `row` / `badge` set in
      // this run just before placing the child is kept and merged into).
      if (this.chromeRun.get(node.id) !== this.runId) this.chrome.delete(node.id);
    }
    const previouslyHidden = this.hiddenBy.get(node);
    if (previouslyHidden) {
      for (const id of previouslyHidden) {
        this.hidden.delete(id);
        this.touched.add(id);
      }
      this.hiddenBy.delete(node);
    }
    const grown = this.scrollEdgeGrowth(node, ctx, p, x, y);
    if (grown) {
      p = grown.p;
      y = grown.y;
    }
    const size = this.measure(node, ctx, p);
    this.setFrame(node, x, y, size);
    const cctx = this.childContext(node, ctx);
    switch (node.kind) {
      case 'text':
        if (node.id >= 0) this.text.set(node.id, this.textPlan(node, ctx, p).layout);
        return;
      case 'image':
        if (node.id >= 0) this.fonts.set(node.id, scaledFont(ctx.font, ctx.imageScale));
        return;
      case 'textfield': {
        // A field draws its text in the context font, so it sits on the same
        // baseline as a label in that font beside it (a form row's).
        if (node.id < 0) return;
        this.fonts.set(node.id, ctx.font);
        if (node.props['axis'] === 'vertical') return;
        // Where a one-line Text centered in the field would sit: snapped with
        // the frames, it lands where a label centered beside the field does,
        // and the DOM puts the field's text there (layoutDom.ts). Keep the
        // list row chrome a list set before placing.
        const line = ctx.font.lineHeight;
        const existing = this.chrome.get(node.id);
        this.setChrome(node, { ...existing, text: { x, y: y + (size.height - line) / 2, width: size.width, height: line } });
        return;
      }
      case 'spacer':
      case 'lazyspacer':
      case 'divider':
      case 'color':
      case 'shape':
      case 'canvas':
      case 'video':
      case 'host':
      case 'registered':
        return;
      case 'vstack':
      case 'disclosuregroup':
      case 'hstack': {
        const label = node.kind === 'hstack' && ctx.labelColumn ? labelColumnOf(node) : null;
        if (label) {
          // iOS's list icon column: the icon centred at 12, the title at 40.
          const plan = this.labelColumnPlan(label, cctx, p);
          this.place(label.icon, cctx, plan.iconProposal, x + LIST_LABEL_ICON_CENTER - plan.icon.width / 2, y + (size.height - plan.icon.height) / 2);
          this.place(label.title, cctx, plan.titleProposal, x + LIST_LABEL_TITLE_X, y + (size.height - plan.title.height) / 2);
          return;
        }
        const axis: Axis = node.kind === 'hstack' ? 'horizontal' : 'vertical';
        const plan = this.stackPlan(node, ctx, p, axis);
        const alignment = str(node.props['alignment']) ?? (node.kind === 'disclosuregroup' ? 'leading' : 'center');
        const shown = this.stackChildren(node);
        this.placeStack(shown, cctx, plan, axis, alignment, x, y, size);
        // A collapsed `disclosuregroup`: its content is hidden, without frames.
        for (const child of node.children) if (!shown.includes(child)) this.hide(child, node);
        return;
      }
      case 'zstack': {
        const alignment = str(node.props['alignment']) ?? 'center';
        for (const child of node.children) {
          const childSize = this.measure(child, cctx, p);
          const { dx, dy } = alignChild(size, childSize, alignment, child);
          this.place(child, cctx, p, x + dx, y + dy);
        }
        return;
      }
      case 'styled': {
        let plan = this.styledPlan(node, ctx, p);
        const child = this.contentOf(node);
        // Phase 8 `.ignoresSafeArea`: the box grows to the screen edge on the
        // sides where it touches the safe area (the parent's layout is
        // unchanged). A flexible child (a color, gradient or resizable image)
        // is proposed the grown box and fills it; a child whose size does not
        // change stays where the parent put it.
        const extension = this.safeAreaExtension(node, ctx, x, y, size);
        if (extension) {
          const grown: Size = {
            width: size.width + extension.leading + extension.trailing,
            height: size.height + extension.top + extension.bottom,
          };
          this.setFrame(node, x - extension.leading, y - extension.top, grown);
          const grownPlan = this.styledPlan(node, ctx, { width: grown.width, height: grown.height });
          if (grownPlan.childSize.width !== plan.childSize.width || grownPlan.childSize.height !== plan.childSize.height) {
            plan = grownPlan;
            x -= extension.leading;
            y -= extension.top;
          }
        }
        if (child) {
          const box = plan.contentBox;
          if (plan.position) {
            const { ideal } = plan.position;
            this.place(
              child,
              cctx,
              NIL_PROPOSAL,
              x + box.x + plan.position.x - ideal.width / 2,
              y + box.y + plan.position.y - ideal.height / 2,
            );
            return;
          }
          const { dx, dy } = alignChild(box, plan.childSize, plan.alignment, child);
          this.place(child, cctx, plan.childProposal, x + box.x + dx, y + box.y + dy);
        }
        return;
      }
      case 'button': {
        const label = this.contentOf(node);
        if (!label) return;
        if (!this.buttonIsBordered(node, ctx)) {
          this.place(label, cctx, p, x, y);
          return;
        }
        const metrics = this.borderedMetrics(node);
        const labelCtx = this.borderedLabelContext(node, ctx);
        const inner: Proposal = {
          width: shrink(p.width, 2 * metrics.padH),
          height: shrink(p.height, 2 * metrics.padV),
        };
        const labelSize = this.measure(label, labelCtx, inner);
        const { dx, dy } = alignIn(size, labelSize, 'center');
        this.place(label, labelCtx, inner, x + dx, y + dy);
        // Keep the list row chrome (`row`, `badge`) a list set before placing.
        const existing = node.id >= 0 ? this.chrome.get(node.id) : undefined;
        this.setChrome(node, {
          ...existing,
          label: { x: x + dx, y: y + dy, width: labelSize.width, height: labelSize.height },
          // The page draws the border on an automatic button the engine bordered (the Mac idiom).
          bordered: { x, y, width: size.width, height: size.height },
        });
        return;
      }
      case 'toggle': {
        const label = this.contentOf(node);
        if (isBarToggle(node, ctx)) {
          // the label centered on its pill (the switch's element, filled while on)
          const labelSize = label ? this.measure(label, cctx, { width: null, height: null }) : ZERO;
          const { dx, dy } = alignIn(size, labelSize, 'center');
          if (label) this.place(label, cctx, { width: null, height: null }, x + dx, y + dy);
          const existing = node.id >= 0 ? this.chrome.get(node.id) : undefined;
          this.setChrome(node, {
            ...existing,
            label: { x: x + dx, y: y + dy, width: labelSize.width, height: labelSize.height },
            switch: { x, y, width: size.width, height: size.height },
          });
          return;
        }
        if (isButtonToggle(node)) {
          // the label centered in the button (its fill, while on, the toggle's frame)
          if (!label) return;
          const m = this.borderedMetrics(node);
          const inner: Proposal = ctx.bottomBarItem
            ? { width: null, height: null }
            : { width: shrink(p.width, 2 * m.padH), height: shrink(p.height, 2 * m.padV) };
          const labelSize = this.measure(label, cctx, inner);
          const { dx, dy } = alignIn(size, labelSize, 'center');
          this.place(label, cctx, inner, x + dx, y + dy);
          const existing = node.id >= 0 ? this.chrome.get(node.id) : undefined;
          this.setChrome(node, { ...existing, label: { x: x + dx, y: y + dy, width: labelSize.width, height: labelSize.height } });
          return;
        }
        const control = this.switchMetrics(ctx);
        const labelProposal = this.toggleLabelProposal(ctx, p);
        const labelSize = label ? this.measure(label, cctx, labelProposal) : ZERO;
        const labelY = y + (size.height - labelSize.height) / 2;
        // A checkbox leads its label; a switch trails it.
        const labelX = control.checkbox ? x + control.width + control.gap : x;
        if (label) this.place(label, cctx, labelProposal, labelX, labelY);
        const existing = node.id >= 0 ? this.chrome.get(node.id) : undefined;
        const switchRect: Rect = {
          x: control.checkbox ? x : x + size.width - control.width,
          y: y + (size.height - control.height) / 2,
          width: control.width,
          height: control.height,
        };
        this.setChrome(node, {
          ...existing,
          label: {
            x: labelX,
            y: labelY,
            width: Math.min(labelSize.width, Math.max(0, size.width - control.width - control.gap)),
            height: labelSize.height,
          },
          switch: switchRect,
          // The page draws a checkbox at it (the Mac idiom).
          ...(control.checkbox ? { checkbox: switchRect } : {}),
        });
        return;
      }
      case 'navlink': {
        const label = this.contentOf(node);
        const labelProposal = this.navLinkLabelProposal(ctx, p);
        const labelSize = label ? this.measure(label, cctx, labelProposal) : ZERO;
        const labelY = y + (size.height - labelSize.height) / 2;
        if (label) this.place(label, cctx, labelProposal, x, labelY);
        if (ctx.listRow) {
          const existing = node.id >= 0 ? this.chrome.get(node.id) : undefined;
          const chrome: Record<string, Rect> = {
            ...existing,
            label: { x, y: labelY, width: labelSize.width, height: labelSize.height },
          };
          // Phase 7: the selected row of a `navsplit` sidebar has no chevron
          // (its label still reserves the chevron's width so selecting a row
          // never reflows it); elsewhere `selected` keeps the chevron. No row
          // of a `sidebar`-style list has one (iPadOS), nor any row on a Mac.
          if (this.env.platform !== 'macos' && !(node.props['selected'] === true && ctx.splitSidebar) && !(ctx.sidebarList && isRegularWidth(this.env.screen))) {
            chrome['chevron'] = {
              x: x + size.width - LIST_CHEVRON_WIDTH,
              y,
              width: LIST_CHEVRON_WIDTH,
              height: size.height,
            };
          } else if (ctx.groupedRow) {
            // A grouped form's link has the small chevron at its trailing end (macOS 26).
            chrome['chevron'] = { x: x + size.width - MAC_GROUPED_CHEVRON_WIDTH, y, width: MAC_GROUPED_CHEVRON_WIDTH, height: size.height };
          }
          this.setChrome(node, chrome);
        }
        return;
      }
      case 'scrollview': {
        const plan = this.scrollPlan(node, ctx, p);
        const axes = this.scrollAxes(node);
        if (node.id >= 0) this.contentSizes.set(node.id, plan.contentSize);
        // Content sits at the scroll origin along the scroll axes and is
        // centered on the other axes when smaller than the viewport.
        const dx = axes.horizontal ? 0 : safeOffset(size.width, plan.contentSize.width, 0.5);
        const dy = axes.vertical ? 0 : safeOffset(size.height, plan.contentSize.height, 0.5);
        // Under its screen's bar (and the large title that scrolls with it),
        // the content starts below them.
        const inset = axes.vertical ? this.scrollInsets.get(node) : undefined;
        const content: Rect = {
          x: x + dx,
          y: y + dy + (inset ? inset.bar + (inset.largeTitle?.height ?? 0) + (inset.search ?? 0) : 0),
          width: plan.contentSize.width,
          height: plan.contentSize.height,
        };
        const covered = this.coveredBottom(ctx, x, y, size);
        const scrollChrome: Record<string, Rect> = { content };
        // `.scrollClipDisabled()`: a horizontal scroll view draws its content
        // past its sides, as far as the screen's (an ancestor that clips, a
        // sheet's card, still does). Its frame reaches them; the content stays
        // where it is and `viewport` keeps the rect the scroll view laid out in,
        // which the page scrolls and snaps against.
        if (node.props['clipDisabled'] === true && axes.horizontal && !axes.vertical) {
          const left = Math.max(0, x);
          const right = Math.max(0, this.env.screen.width - (x + size.width));
          if (left > 0 || right > 0) {
            scrollChrome['viewport'] = { x, y, width: size.width, height: size.height };
            this.setFrame(node, x - left, y, { width: size.width + left + right, height: size.height });
          }
        }
        if (covered) scrollChrome['bottomInset'] = covered;
        if (inset) scrollChrome['topInset'] = { x, y, width: size.width, height: inset.bar };
        if (inset?.largeTitle) scrollChrome['largeTitle'] = inset.largeTitle;
        this.setChrome(node, scrollChrome);
        // The content is placed under the context it was measured in: its container is the viewport.
        if (plan.content) this.place(plan.content, plan.cctx, plan.contentProposal, content.x, content.y);
        return;
      }
      case 'list':
        this.placeList(node, ctx, p, x, y);
        return;
      case 'navstack': {
        const screenProposal: Proposal = { width: size.width, height: size.height };
        // Each screen's back button is labelled with the previous screen's title.
        node.children.forEach((child, index) => {
          const previous = index > 0 ? node.children[index - 1] : undefined;
          this.previousTitles.set(child, previous ? (str(previous.props['title']) ?? '') : '');
        });
        // A screen below the top one is not drawn once its push has settled
        // (the page hides it), so a screen that was laid out once keeps the
        // frames it has while it is covered, however the stack resizes or
        // its content changes meanwhile: on Food Truck's order detail the
        // covered Orders list is 89% of the boxes. Its plan is left as it
        // is, so when a pop uncovers it, `placedAlready` passes only if
        // nothing changed since those frames were made, else it is laid
        // out afresh. A screen never laid out (pushed under another in one
        // pass) is placed, so it has frames to show when uncovered.
        const top = node.children[node.children.length - 1];
        for (const child of node.children) {
          if (child !== top && child.id >= 0 && this.frames.has(child.id) && !this.uncovered.has(child)) {
            this.stats.skipped += 1;
            continue;
          }
          if (child === top) this.uncovered.delete(child);
          this.place(child, cctx, screenProposal, x, y);
        }
        return;
      }
      case 'navscreen':
        this.placeNavScreen(node, cctx, x, y, size);
        return;
      case 'navsplit':
        this.placeNavSplit(node, cctx, x, y, size);
        return;
      case 'inspector':
        this.placeInspector(node, cctx, x, y, size);
        return;
      case 'inspectorpanel':
        this.placeInspectorPanel(node, cctx, x, y, size);
        return;
      case 'viewthatfits': {
        // Phase 7: only the chosen candidate is placed (at the origin, the
        // element is its size); the others have no frame and are hidden.
        const plan = this.viewThatFitsPlan(node, ctx, p);
        for (const child of node.children) {
          if (child === plan.chosen) this.place(child, cctx, p, x, y);
          else this.hide(child, node);
        }
        return;
      }
      case 'sheet':
        this.placeSheet(node, cctx, x, y, size);
        return;
      case 'tabview':
        this.placeTabView(node, cctx, x, y, size);
        return;
      case 'picker':
        this.placePicker(node, ctx, p, x, y, size);
        return;
      case 'geometry': {
        // The child is proposed the reader's size and placed top leading.
        const content = this.contentOf(node);
        if (content) this.place(content, cctx, { width: size.width, height: size.height }, x, y);
        return;
      }
      case 'layout':
        this.placeLayout(node, cctx, p, x, y, size);
        return;
      case 'grid':
        this.placeGrid(node, ctx, p, x, y);
        return;
      case 'lazyvgrid':
      case 'lazyhgrid':
        this.placeLazyGrid(node, ctx, p, x, y);
        return;
      case 'gauge':
        this.placeGauge(node, ctx, p, x, y, size);
        return;
      case 'chart':
        this.placeChart(node, ctx, p, x, y);
        return;
      case 'chartitem': {
        // Outside a chart (or measured on its own): the view at the same origin.
        const content = node.children[0];
        if (content) this.place(content, cctx, p, x, y);
        return;
      }
      case 'decorated': {
        const [content, decoration] = node.children;
        if (content) this.place(content, cctx, p, x, y);
        if (decoration) {
          // The decoration is proposed the content's size and aligned inside it.
          const proposal: Proposal = { width: size.width, height: size.height };
          const decorationSize = this.measure(decoration, cctx, proposal);
          const alignment = str(node.props['alignment']) ?? 'center';
          // A decoration in an `if` aligns by its edges, its guides unused (as on iOS 26).
          const { dx, dy } = node.props['conditional'] === true
            ? alignIn(size, decorationSize, alignment)
            : alignChild(size, decorationSize, alignment, decoration);
          this.place(decoration, cctx, proposal, x + dx, y + dy);
        }
        return;
      }
      case 'menu': {
        // Phase 6: the label is placed; the items are only shown in the
        // popover the renderer opens, so they are hidden and have no frames.
        const [label, ...items] = node.children;
        if (this.menuIsPullDown(ctx)) {
          // The Mac's pull-down button: the label 10 in, centered in the 22 row, the chevron after it.
          const inner: Proposal = { width: shrink(p.width, 2 * MAC_PULLDOWN_PAD_H + MAC_PULLDOWN_CHEVRON), height: shrink(p.height, 0) };
          const labelSize = label ? this.measure(label, cctx, inner) : ZERO;
          if (label) this.place(label, cctx, inner, x + MAC_PULLDOWN_PAD_H, y + (size.height - labelSize.height) / 2);
          const existing = node.id >= 0 ? this.chrome.get(node.id) : undefined;
          this.setChrome(node, { ...existing, pulldown: { x, y, width: size.width, height: size.height } });
        } else if (this.menuHasIndicator(ctx)) {
          // The label at the leading end, the chevron (chrome `indicator`, the page draws it) in the 13 after it.
          const labelSize = label ? this.measure(label, cctx, { width: shrink(p.width, MAC_BAR_MENU_INDICATOR), height: p.height }) : ZERO;
          if (label) this.place(label, cctx, { width: shrink(p.width, MAC_BAR_MENU_INDICATOR), height: p.height }, x, y + (size.height - labelSize.height) / 2);
          const existing = node.id >= 0 ? this.chrome.get(node.id) : undefined;
          this.setChrome(node, { ...existing, indicator: { x: x + size.width - 6, y: y + size.height / 2 - 3, width: 6, height: 6 } });
        } else if (label) {
          this.place(label, cctx, p, x, y);
        }
        for (const item of items) this.hide(item, node);
        return;
      }
      case 'searchfield':
        return;
      // Phase 8 controls.
      case 'alert':
        this.placeAlert(node, ctx, x, y, size);
        return;
      case 'stepper':
        this.placeStepper(node, ctx, p, x, y, size);
        return;
      case 'slider':
        this.placeSlider(node, ctx, p, x, y, size);
        return;
      case 'datepicker':
        this.placeDatePicker(node, ctx, p, x, y, size);
        return;
      case 'texteditor':
        // It draws in the context font, as a field does (the page's own
        // font otherwise: a serif where the page sets none); a rich one's
        // runs in theirs.
        if (node.id < 0) return;
        this.fonts.set(node.id, ctx.font);
        if (Array.isArray(node.props['runs'])) {
          // The plan a fitting editor was measured from (`measureFittingEditor`, memoized).
          this.text.set(node.id, this.editorRunsPlan(node, ctx, p, node.props['runs'], size.width).layout);
        }
        return;
      case 'swipeactions':
      case 'contextmenu': {
        // Phase 8: the content at the same origin; actions / items hidden, no frames.
        const [content, ...rest] = node.children;
        if (content) this.place(content, cctx, p, x, y);
        for (const item of rest) this.hide(item, node);
        return;
      }
      case 'gesture':
      case 'listrow':
      case 'tapgesture':
      case 'draggable':
      case 'dropdestination':
      default: {
        // `tapgesture`, `gesture`, `listrow`, `draggable`, `dropdestination` (child at the same origin), `toolbar` outside a navscreen,
        // `tab` outside a tabview, `section` outside a list and unknown kinds.
        const content = this.contentOf(node);
        if (content) this.place(content, cctx, p, x, y);
        return;
      }
    }
  }

  /**
   * The part of a scrolling view's frame that a tab bar (or its screen's
   * bottom toolbar or search field) covers (chrome `bottomInset`).
   */
  private coveredBottom(ctx: Context, x: number, y: number, size: Size): Rect | null {
    if (ctx.bottomBar <= 0) return null;
    const bottom = y + size.height;
    if (bottom < (ctx.bottomEdge ?? this.env.screen.height) - EPSILON) return null;
    const height = Math.min(size.height, ctx.bottomBar);
    return { x, y: bottom - height, width: size.width, height };
  }

  /**
   * iOS 26: a vertical list or scroll view deeper in a screen's content
   * whose top edge touches the screen's bar across the screen (`barAbove`)
   * runs up under it, and one whose bottom edge touches a bar covering the
   * bottom (`bottomBar`: a tab bar, a bottom toolbar or search field) runs
   * down under that, as the screen's own scroll view does: the proposal and
   * frame grow by the bar (the parent's layout is unchanged, as for
   * `.ignoresSafeArea`) and the content is inset by it (chrome `topInset` /
   * `bottomInset`), so it scrolls under the bar, where the scroll edge
   * effect shows (styles.css). Null when nothing grows.
   */
  private scrollEdgeGrowth(node: LayoutNode, ctx: Context, p: Proposal, x: number, y: number): { p: Proposal; y: number } | null {
    if (node.kind !== 'list' && !(node.kind === 'scrollview' && this.scrollAxes(node).vertical)) return null;
    const inset = this.scrollInsets.get(node);
    const bar = ctx.barAbove;
    // The screen's own scroll view is inset by `placeNavScreen` (no bar above
    // here); a nested one that no longer touches the bar loses its inset.
    if (inset?.nested) this.scrollInsets.delete(node);
    if (!finite(p.height)) return null;
    const size = this.measure(node, ctx, p);
    let top = 0;
    if (
      bar &&
      !(inset && !inset.nested) &&
      Math.abs(y - bar.top) <= EPSILON &&
      x <= bar.x + EPSILON &&
      x + size.width >= bar.x + bar.width - EPSILON
    ) {
      top = bar.height;
      this.scrollInsets.set(node, { bar: top, largeTitle: null, nested: true });
    }
    let bottom = 0;
    if (ctx.bottomBar > 0) {
      const edge = ctx.bottomEdge ?? this.env.screen.height;
      if (Math.abs(y + size.height - (edge - ctx.bottomBar)) <= EPSILON) bottom = ctx.bottomBar;
    }
    if (top === 0 && bottom === 0) return null;
    return { p: { width: p.width, height: size.height + top + bottom }, y: y - top };
  }

  /** The labeled control a form row's stack is shifted to (the form's alignment guide), if it has one. */
  private macFormGuide(row: LayoutNode): LayoutNode | null {
    const inner = unwrapStyled(row);
    if (inner.kind !== 'hstack') return null;
    return inner.children.find((child) => MAC_FORM_LABELED_KINDS.includes(unwrapStyled(child).kind)) ?? null;
  }

  /**
   * The Mac idiom's `Form` (MAC_FORM_* above, docs/notes/macos.md): the
   * columns style. A row whose control carries a label (a text field's
   * title, a picker's, a stepper's, a date picker's, a slider's) puts it
   * in the label column, right-aligned, and the control in the controls
   * column at the control's own height; a toggle, a button, text and other
   * rows go in the controls column whole. A stack row with such a control
   * in it is shifted so the control starts at the controls column, what is
   * before it reaching into the label column, as the form's alignment guide
   * does on a Mac. Headers and footers are 13-point text in the controls
   * column. No cards, no separators.
   */
  private macFormPlan(node: LayoutNode, ctx: Context, p: Proposal): ListPlan {
    const { screen } = this.env;
    const width = finite(p.width) ? p.width : p.width === Infinity ? Infinity : screen.width;
    const layoutWidth = Number.isFinite(width) ? width : screen.width;
    const rowCtx: Context = { ...this.childContext(node, ctx), listRow: true, labelColumn: false, listCell: true, formColumn: true, sidebarList: false };
    const font = rowCtx.font;
    const textFont = makeFont('system', textStyleSize('body', this.env.dynamicTypeSize, 'macos'), 400);
    type Row = { node: LayoutNode; label: string | null; labelNode: LayoutNode | null; guide: LayoutNode | null };
    type Source = { section: LayoutNode | null; rows: Row[]; headerView: LayoutNode | null; footerView: LayoutNode | null };
    // The rows, each with what goes in the label column.
    const classify = (row: LayoutNode): Row => {
      const inner = unwrapStyled(row);
      switch (inner.kind) {
        case 'textfield':
          return { node: row, label: str(inner.props['placeholder']) || null, labelNode: null, guide: null };
        case 'picker':
          return { node: row, label: str(inner.props['label']) || null, labelNode: null, guide: null };
        case 'stepper':
        case 'datepicker':
          return { node: row, label: null, labelNode: inner.props['labelsHidden'] === true ? null : this.contentOf(inner), guide: null };
        case 'slider': {
          const names = Array.isArray(inner.props['labels']) ? inner.props['labels'].map(str) : [];
          const at = names.indexOf('label');
          return { node: row, label: null, labelNode: at >= 0 ? (inner.children[at] ?? null) : null, guide: null };
        }
        default:
          return { node: row, label: null, labelNode: null, guide: this.macFormGuide(row) };
      }
    };
    const sources: Source[] = [];
    for (const child of node.children) {
      if (child.kind === 'section') {
        const rows = child.children.filter((row) => row.kind !== 'sectionheader' && row.kind !== 'sectionfooter').map(classify);
        sources.push({ section: child, rows, headerView: child.children.find((row) => row.kind === 'sectionheader') ?? null, footerView: child.children.find((row) => row.kind === 'sectionfooter') ?? null });
      } else {
        const last = sources[sources.length - 1];
        if (last && last.section === null) last.rows.push(classify(child));
        else sources.push({ section: null, rows: [classify(child)], headerView: null, footerView: null });
      }
    }
    // The label column: as wide as the widest label (a view label at its ideal size).
    const labelCtx: Context = { ...this.childContext(node, ctx), stackAxis: null, listRow: false, listCell: true };
    let labelWidth = 0;
    const labelSizes = new Map<LayoutNode, Size>();
    for (const source of sources) {
      for (const row of source.rows) {
        if (row.label !== null) labelWidth = Math.max(labelWidth, this.measurer.measureWidth(row.label, font));
        else if (row.labelNode) {
          const size = this.measure(row.labelNode, labelCtx, { width: null, height: null });
          labelSizes.set(row.labelNode, size);
          labelWidth = Math.max(labelWidth, size.width);
        }
      }
    }
    labelWidth = Math.ceil(labelWidth);
    const controlX = labelWidth + MAC_FORM_LABEL_GAP;
    const controlWidth = Math.max(0, layoutWidth - controlX);
    const textCtx: Context = { ...labelCtx, font: textFont };
    const makeText = (text: string): { layout: TextLayout; size: Size } => {
      const wrapped = wrapText(text, textFont, controlWidth, null, this.measurer);
      return { layout: { font: textFont, lines: wrapped.lines, alignment: 'leading', truncated: false }, size: { width: wrapped.width, height: textHeight(wrapped.lines.length, textFont) } };
    };
    const supplement = (view: LayoutNode | null, y: number): ListSupplementPlan | null => {
      if (!view) return null;
      const proposal: Proposal = { width: controlWidth, height: null };
      const size = this.measure(view, textCtx, proposal);
      return { node: view, ctx: textCtx, proposal, rect: { x: controlX, y, width: size.width, height: size.height } };
    };
    // Built from a top, then again centered in the room when the rows are shorter than it.
    const build = (startY: number): { groups: ListGroupPlan[]; labels: FormLabelPlan[]; end: number } => {
    const groups: ListGroupPlan[] = [];
    const labels: FormLabelPlan[] = [];
    let y = startY;
    for (let g = 0; g < sources.length; g++) {
      const source = sources[g] as Source;
      const previous = groups[g - 1];
      const headerText = source.section ? str(source.section.props['header']) : null;
      const footerText = source.section ? str(source.section.props['footer']) : null;
      const hasHeader = (headerText !== null && headerText.length > 0) || (source.headerView !== null && this.measure(source.headerView, textCtx, { width: controlWidth, height: null }).height > 0);
      // A header after rows is 4 under them; right under a footer.
      if (previous && hasHeader && previous.footer === null && !(previous.footerView !== null && previous.footerView.rect.height > 0)) y += MAC_FORM_HEADER_TOP;
      const top = y;
      let header: ListGroupPlan['header'] = null;
      let footer: ListGroupPlan['footer'] = null;
      const headerView = supplement(source.headerView, y);
      if (headerView && headerView.rect.height > 0) y += headerView.rect.height + MAC_FORM_HEADER_GAP;
      if (headerText !== null && headerText.length > 0) {
        const { layout, size } = makeText(headerText);
        header = { rect: { x: controlX, y, width: controlWidth, height: size.height }, layout };
        y += size.height + MAC_FORM_HEADER_GAP;
      }
      const cardTop = y;
      const rows: ListRowPlan[] = [];
      source.rows.forEach((row, i) => {
        if (i > 0) y += MAC_FORM_ROW_GAP;
        if (row.node.kind === 'lazyspacer') {
          const rect: Rect = { x: controlX, y, width: controlWidth, height: 0 };
          rows.push({ node: row.node, proposal: { width: controlWidth, height: 0 }, contentSize: ZERO, row: rect, content: rect, badge: null, edit: null });
          return;
        }
        // A shifted stack is proposed the whole row less the trailing inset; its place is set when the list is placed.
        // A shifted stack is proposed the whole row; its place and width are set when the list is placed.
        const proposal: Proposal = { width: row.guide ? layoutWidth : controlWidth, height: null };
        const contentSize = this.measure(row.node, rowCtx, proposal);
        const labelSize = row.labelNode ? labelSizes.get(row.labelNode) ?? ZERO : row.label !== null ? { width: this.measurer.measureWidth(row.label, font), height: font.lineHeight } : ZERO;
        const rowHeight = Math.max(contentSize.height, labelSize.height);
        // A text field's row is the field itself (the page draws the Mac's bezel on the row element); the others span the form.
        const field = unwrapStyled(row.node).kind === 'textfield';
        rows.push({
          node: row.node,
          proposal,
          contentSize,
          row: field ? { x: controlX, y, width: controlWidth, height: rowHeight } : { x: 0, y, width: layoutWidth, height: rowHeight },
          content: { x: row.guide ? 0 : controlX, y: y + (rowHeight - contentSize.height) / 2, width: contentSize.width, height: contentSize.height },
          badge: null,
          edit: null,
          separatorHidden: { top: true, bottom: true },
        });
        if (row.label !== null) {
          labels.push({
            rect: { x: 0, y: y + (rowHeight - labelSize.height) / 2, width: labelWidth, height: labelSize.height },
            layout: { font, lines: [row.label], alignment: 'trailing', truncated: false },
            node: null,
            ctx: labelCtx,
          });
        } else if (row.labelNode) {
          labels.push({
            rect: { x: labelWidth - labelSize.width, y: y + (rowHeight - labelSize.height) / 2, width: labelSize.width, height: labelSize.height },
            layout: null,
            node: row.labelNode,
            ctx: labelCtx,
          });
        }
        y += rowHeight;
      });
      // The rows' card spans the form: the label column is drawn in it too.
      const card: Rect = { x: 0, y: cardTop, width: layoutWidth, height: y - cardTop };
      if (footerText !== null && footerText.length > 0) {
        y += MAC_FORM_FOOTER_GAP;
        const { layout, size } = makeText(footerText);
        footer = { rect: { x: controlX, y, width: controlWidth, height: size.height }, layout };
        y += size.height;
      }
      let footerView = supplement(source.footerView, y + MAC_FORM_FOOTER_GAP);
      if (footerView && footerView.rect.height > 0) y += MAC_FORM_FOOTER_GAP + footerView.rect.height;
      else if (footerView) footerView = { ...footerView, rect: { ...footerView.rect, y } };
      groups.push({ section: source.section, frame: { x: 0, y: top, width: layoutWidth, height: y - top }, header, footer, headerView, footerView, card, rows, outlines: [] });
    }
    return { groups, labels, end: y };
    };
    let built = build(0);
    // Centered in the room under the bar when shorter than it (the Mac's columns form does not scroll).
    const scrollInset = this.scrollInsets.get(node);
    const inset = scrollInset ? scrollInset.bar + (scrollInset.largeTitle?.height ?? 0) + (scrollInset.search ?? 0) : 0;
    const room = finite(p.height) ? p.height - inset : null;
    if (room !== null && room > built.end) built = build((room - built.end) / 2);
    const contentHeight = built.end;
    return {
      size: { width, height: p.height === null ? contentHeight : p.height },
      contentSize: { width: layoutWidth, height: contentHeight },
      groups: built.groups,
      labels: built.labels,
      columns: true,
      column: { x: controlX, width: controlWidth },
    };
  }

  private placeList(node: LayoutNode, ctx: Context, p: Proposal, x: number, y: number): void {
    const plan = this.listPlan(node, ctx, p);
    const columns = plan.columns === true;
    const rowCtx: Context = columns
      ? { ...this.childContext(node, ctx), listRow: true, labelColumn: false, listCell: true, formColumn: true, sidebarList: false }
      : {
          ...this.childContext(node, ctx),
          listRow: true,
          labelColumn: this.env.platform !== 'macos',
          listCell: this.env.platform === 'macos',
          sidebarList: this.listStyleOf(node, ctx) === 'sidebar',
          ...(this.env.platform === 'macos' && this.listStyleOf(node, ctx) === 'sidebar' ? { lineLimit: 1 } : {}),
          ...(this.env.platform === 'macos' && node.props['form'] === true && node.props['formStyle'] === 'grouped' ? { groupedRow: true } : {}),
        };
    const mac = this.env.platform === 'macos';
    const cellPadding = mac ? (this.listStyleOf(node, ctx) === 'sidebar' ? MAC_LIST_SIDEBAR_CELL_PADDING : MAC_LIST_CELL_PADDING) : LIST_CELL_PADDING;
    const covered = this.coveredBottom(ctx, x, y, plan.size);
    // Under its screen's bar (and the large title that scrolls with it), the
    // rows start below them.
    const scrollInset = this.scrollInsets.get(node);
    const listChrome: Record<string, Rect> = {};
    const listTexts: Record<string, TextLayout> = {};
    if (covered) listChrome['bottomInset'] = covered;
    // The page draws a sidebar's look (`data-sb-list-look`) for a list the engine laid out as one.
    if (this.listStyleOf(node, ctx) === 'sidebar') listChrome['sidebarLook'] = { x, y, width: plan.size.width, height: plan.size.height };
    // A Mac's grouped form: the page draws its cards.
    if (this.env.platform === 'macos' && node.props['form'] === true && node.props['formStyle'] === 'grouped') listChrome['grouped'] = { x, y, width: plan.size.width, height: plan.size.height };
    if (scrollInset) listChrome['topInset'] = { x, y, width: plan.size.width, height: scrollInset.bar };
    if (scrollInset?.largeTitle) listChrome['largeTitle'] = scrollInset.largeTitle;
    const inset = scrollInset ? scrollInset.bar + (scrollInset.largeTitle?.height ?? 0) + (scrollInset.search ?? 0) : 0;
    if (node.id >= 0) this.contentSizes.set(node.id, { width: plan.contentSize.width, height: plan.contentSize.height + inset });
    y += inset;
    const offset = (r: Rect): Rect => ({ x: x + r.x, y: y + r.y, width: r.width, height: r.height });
    // The Mac idiom's columns form: the label column (chrome `formLabel<i>`
    // with its text, or a view placed there) and the flag for the page.
    if (columns) listChrome['columns'] = { x, y, width: plan.size.width, height: plan.size.height };
    {
      (plan.labels ?? []).forEach((label, i) => {
        if (label.layout) {
          listChrome[`formLabel${i}`] = offset(label.rect);
          listTexts[`formLabel${i}`] = label.layout;
        }
      });
    }
    if (Object.keys(listChrome).length > 0) this.setChrome(node, listChrome);
    if (node.id >= 0 && Object.keys(listTexts).length > 0) this.chromeText.set(node.id, listTexts);
    for (const group of plan.groups) {
      if (group.section) {
        const chrome: Record<string, Rect> = { rows: offset(group.card) };
        const texts: Record<string, TextLayout> = {};
        if (group.header) {
          chrome['header'] = offset(group.header.rect);
          texts['header'] = group.header.layout;
        }
        if (group.footer) {
          chrome['footer'] = offset(group.footer.rect);
          texts['footer'] = group.footer.layout;
        }
        this.setFrame(group.section, x + group.frame.x, y + group.frame.y, group.frame);
        this.setChrome(group.section, chrome);
        for (const view of [group.headerView, group.footerView]) {
          if (!view) continue;
          this.place(view.node, view.ctx, view.proposal, x + view.rect.x, y + view.rect.y);
          // A collapsible section's header spans the content width (its chevron is at the trailing edge); its content stays put.
          const placed = view.node.id >= 0 ? this.frames.get(view.node.id) : undefined;
          if (placed && placed.width < view.rect.width) this.setFrame(view.node, placed.x, placed.y, { width: view.rect.width, height: placed.height });
        }
        if (group.section.id >= 0) {
          this.fonts.set(group.section.id, group.header?.layout.font ?? group.footer?.layout.font ?? makeFont('system', textStyleSize('footnote', this.env.dynamicTypeSize, this.env.platform), 400));
          if (Object.keys(texts).length > 0) this.chromeText.set(group.section.id, texts);
        }
        // A collapsed `Section(isExpanded:)` (sidebar-style list): its rows are hidden, without frames.
        if (group.rows.length === 0) {
          for (const child of group.section.children) {
            if (child.kind !== 'sectionheader' && child.kind !== 'sectionfooter') this.hide(child, group.section);
          }
        }
      }
      // An outline's `disclosuregroup` is not placed through `place` (its label
      // and content are the rows): its frame is the rect its rows span, which
      // is its box too (`row`, so the page lays its rows out from it), its
      // content is hidden while collapsed, and what a pass before hid is shown.
      for (const outline of group.outlines) {
        const g = outline.group;
        const previouslyHidden = this.hiddenBy.get(g);
        if (previouslyHidden) {
          for (const id of previouslyHidden) {
            this.hidden.delete(id);
            this.touched.add(id);
          }
          this.hiddenBy.delete(g);
        }
        const frame = offset(outline.frame);
        this.setFrame(g, frame.x, frame.y, frame);
        this.setChrome(g, { row: frame });
        if (g.props['expanded'] !== true) for (const child of g.children.slice(1)) this.hide(child, g);
      }
      for (const row of group.rows) {
        // Set the row chrome before placing so a navlink can merge its own keys.
        const chrome: Record<string, Rect> = { row: offset(row.row) };
        // `.listRowSeparator(.hidden)`: flags the renderer turns into data
        // attributes on the row element (its separator line is CSS).
        if (row.separatorHidden?.top) chrome['separatorTopHidden'] = offset(row.row);
        if (row.separatorHidden?.bottom) chrome['separatorBottomHidden'] = offset(row.row);
        if (row.badge) {
          chrome['badge'] = offset(row.badge.rect);
          if (row.node.id >= 0) this.chromeText.set(row.node.id, { badge: row.badge.layout });
        }
        // Phase 8: the edit control belongs to the `listrow`, which is the
        // row itself or sits under its wrappers (its own place() keeps chrome).
        if (row.edit) {
          const controls: Record<string, Rect> = {};
          if (row.edit.control) controls['editControl'] = offset(row.edit.control);
          if (row.edit.reorder) controls['reorderControl'] = offset(row.edit.reorder);
          if (row.edit.listRow === row.node) Object.assign(chrome, controls);
          else this.setChrome(row.edit.listRow, controls);
        }
        let rowX = x + row.content.x;
        const rowY = y + row.content.y;
        // A columns form's shifted stack: placed, then moved so its labeled
        // control starts at the controls column (the form's alignment guide).
        // Placed once to find the control's offset in it, then proposed the
        // width that ends it at the trailing edge, from where the control
        // lands at the column.
        const guide = columns ? this.macFormGuide(row.node) : null;
        let proposal = row.proposal;
        if (guide && plan.column) {
          this.place(row.node, rowCtx, row.proposal, rowX, rowY);
          const placed = this.frames.get(unwrapStyled(guide).id);
          if (placed) {
            const offset = placed.x - rowX;
            rowX = x + plan.column.x - offset;
            proposal = { width: Math.max(0, plan.size.width - (plan.column.x - offset)), height: row.proposal.height };
          }
        }
        // A row that is skipped keeps the chrome it set itself when it was
        // placed (a link's chevron and label); only the list's keys are new.
        const own = this.placedAlready(row.node, rowCtx, proposal, rowX, rowY) && row.node.id >= 0
          ? this.chrome.get(row.node.id)
          : undefined;
        this.setChrome(row.node, own ? { ...own, ...chrome } : chrome);
        this.place(row.node, rowCtx, proposal, rowX, rowY);
        if (row.badge) this.badgeBeforeChevron(row.node);
        // iOS 26: the separator under a row starts where its first text
        // starts (after a Label's icon), else at the row's content.
        const placed = row.node.id >= 0 ? this.chrome.get(row.node.id) : undefined;
        if (placed) {
          const textX = this.firstTextX(row.node);
          const leading = (textX ?? rowX) - (x + row.row.x);
          placed['separator'] = { x: Math.max(0, leading), y: 0, width: 0, height: 0 };
        }
        // A group's label row: the group's toggle lies over it, its chevron at the row's trailing edge (as a link's; none on a Mac).
        const outlineGroup = row.outline?.group;
        if (outlineGroup && outlineGroup.id >= 0) {
          const rowRect = offset(row.row);
          const groupChrome: Record<string, Rect> = { ...this.chrome.get(outlineGroup.id), toggle: rowRect };
          if (!mac) groupChrome['chevron'] = { x: rowRect.x + rowRect.width - cellPadding - LIST_CHEVRON_WIDTH, y: rowRect.y, width: LIST_CHEVRON_WIDTH, height: rowRect.height };
          this.setChrome(outlineGroup, groupChrome);
        }
      }
    }
    // The label column's views (a stepper's, a slider's, a date picker's label) go where the plan put them, after the rows (which do not draw them).
    if (columns) {
      for (const label of plan.labels ?? []) {
        if (label.node) this.place(label.node, label.ctx, { width: null, height: null }, x + label.rect.x, y + label.rect.y);
      }
    }
  }

  /** The x of the first text (in tree order) placed under `node`, if any. */
  private firstTextX(node: LayoutNode): number | null {
    if (node.kind === 'text' && node.id >= 0) {
      const frame = this.frames.get(node.id);
      if (frame && frame.width > 0) return frame.x;
    }
    for (const child of node.children) {
      const x = this.firstTextX(child);
      if (x !== null) return x;
    }
    return null;
  }

  /**
   * The navigation bar is the status bar plus a 44 row (plus 62 for a large
   * title, or 10 and the height of its `.largeTitle` items); a Phase 6
   * `searchfield` is a capsule floating above the bottom (iOS 26); `bottom`
   * toolbars float their items on glass in a 48 row 28
   * above the bottom (`placeBottomToolbars`); the content is proposed the
   * rest. Toolbar items are measured at their ideal size and laid out in iOS
   * 26 glass capsules in the 44 row: adjacent `leading` (or `trailing`)
   * toolbars share one (a run, chrome `leadingGlass0` …, `trailingGlass0` …;
   * a `ToolbarSpacer` of the placement ends one), their items in 44-tall
   * slots 12 apart, the capsules 12 apart (`leading` after the back button,
   * `trailing` ending 16 from the trailing edge, in their order in the
   * content); a `principal` group is centered in place of the title. The
   * inline title (`chrome.title`) is centered and truncated to the room the
   * bar items leave.
   */
  /** The tallest `.largeTitle` toolbar item at its ideal size, or null when the screen has none (the text title shows). */
  private largeTitleItemsHeight(toolbars: readonly LayoutNode[], cctx: Context, width: number): number | null {
    let height: number | null = null;
    for (const toolbar of toolbars) {
      if (str(toolbar.props['placement']) !== 'largeTitle' || str(toolbar.props['spacer']) !== null) continue;
      const itemCtx = this.childContext(toolbar, cctx);
      const room: Proposal = { width: Math.max(0, width - 2 * NAV_ITEM_MARGIN), height: null };
      for (const item of toolbar.children) height = Math.max(height ?? 0, this.measure(item, itemCtx, room).height);
    }
    return height;
  }

  private placeNavScreen(node: LayoutNode, cctx: Context, x: number, y: number, size: Size): void {
    const { safeArea, screen, dynamicTypeSize } = this.env;
    // The horizontal safe area (a phone on its side: the notch on one side,
    // the corner on the other): the bar's background runs edge to edge, as
    // the screen does; its items, the titles, the search field, the bottom
    // toolbars and the content stay clear of it. A split view's columns are
    // inset already (`placeNavSplit`), so they do not reach the edges here.
    const barX = x;
    const barWidth = size.width;
    const insetLeading = x <= EPSILON ? safeArea.leading : 0;
    const insetTrailing = x + size.width >= screen.width - EPSILON ? safeArea.trailing : 0;
    x += insetLeading;
    size = { width: Math.max(0, size.width - insetLeading - insetTrailing), height: size.height };
    const statusBar = y <= 0 ? safeArea.top : (cctx.barInset ?? 0);
    const displayMode = str(node.props['displayMode']) ?? 'automatic';
    const depth = num(node.props['depth']) ?? 0;
    // `.toolbar(.hidden, for: .navigationBar)`: no bar, title, back button or
    // bar items; the content starts under the status bar, which stays a safe
    // area. `.bottomBar` hidden: the bottom toolbars draw nothing.
    const barHidden = node.props['navigationBarHidden'] === true;
    const bottomBarHidden = node.props['bottomBarHidden'] === true;
    const parts = this.navScreenParts(node);
    // The Mac idiom (MAC_* above): the title bar in place of the bar, unified
    // (52) when anything but the title is in it, plain (28) otherwise; no
    // large titles. The status bar portion is the window's title bar, which
    // the unified bar takes over; the row is the whole bar.
    const mac = this.env.platform === 'macos';
    const hasBarItems = parts.toolbars.some((t) => {
      const placement = this.barPlacement(t);
      return placement !== 'bottom' && placement !== 'keyboard' && placement !== 'largeTitle' && str(t.props['spacer']) === null;
    });
    // On a Mac the field is the toolbar's, or over a sidebar's list.
    const searchInSidebar = mac && parts.search !== null && !barHidden && cctx.splitSidebar;
    const searchInBar = mac && parts.search !== null && !barHidden && !searchInSidebar;
    // A Mac tab view's toolbar row (the window's top): the tab's root screen
    // draws its bar as that row, without a title (the tab picker is there).
    const tabToolbar = mac && cctx.tabToolbar !== undefined && y <= cctx.tabToolbar.y + EPSILON ? cctx.tabToolbar : undefined;
    // A Mac sheet's root screen: the sheet's 40 title bar, its text actions in the sheet's bottom bar.
    const sheetBar = mac && depth === 0 && cctx.macSheet !== undefined ? cctx.macSheet : undefined;
    const actionToolbars = sheetBar?.actions ? this.macSheetActionToolbars(node) : [];
    const unified = mac && tabToolbar === undefined && sheetBar === undefined && (depth > 0 || hasBarItems || searchInBar || (cctx.barLeading ?? 0) > 0 || cctx.splitColumn === true);
    const rowHeight = mac ? (tabToolbar ? tabToolbar.height : sheetBar ? MAC_SHEET_TITLE_BAR : unified ? MAC_TITLE_BAR_UNIFIED : MAC_TITLE_BAR_PLAIN) : NAV_BAR_HEIGHT;
    const glass = mac ? MAC_BAR_ITEM_SIZE : NAV_GLASS_SIZE;
    const glassPad = mac ? MAC_BAR_ITEM_PAD_H : NAV_GLASS_PAD_H;
    const runGap = mac ? MAC_BAR_RUN_GAP : NAV_RUN_GAP;
    /** A Mac sidebar's bar: its items plain and apart, no glass (MAC_SIDEBAR_ITEM_PAD_H). */
    const plainBar = mac && cctx.splitSidebar && tabToolbar === undefined && sheetBar === undefined;
    const groupGap = plainBar ? MAC_SIDEBAR_ITEM_GAP : mac ? MAC_BAR_GROUP_GAP : NAV_GROUP_SPACING;
    // A compact height (a phone on its side) has no large titles, whatever the
    // display mode, as UIKit: the title is the bar's inline one.
    const large = !mac && !barHidden && screen.height >= REGULAR_HEIGHT && (displayMode === 'large' || (displayMode === 'automatic' && depth === 0));
    const barHeight = barHidden ? statusBar : mac ? Math.max(statusBar, rowHeight) : statusBar + NAV_BAR_HEIGHT + (!large && y <= 0 ? NAV_BAR_INLINE_BOTTOM : 0);
    const reachesBottom = y + size.height >= screen.height - EPSILON;
    const isSpacer = (t: LayoutNode): boolean => str(t.props['spacer']) !== null;
    const placementOf = (t: LayoutNode): string | null => this.barPlacement(t);
    const inHiddenBar = (t: LayoutNode): boolean => {
      const placement = placementOf(t);
      if (placement === 'keyboard') return false;
      return placement === 'bottom' ? bottomBarHidden : barHidden;
    };
    for (const toolbar of parts.toolbars.filter(inHiddenBar)) this.hide(toolbar, node);
    // `.largeTitle` items size the large title row; the text title is 62.
    const titleItemsHeight = large ? this.largeTitleItemsHeight(parts.toolbars, cctx, size.width) : null;
    const largeHeight = !large ? 0 : titleItemsHeight !== null ? LARGE_TITLE_ITEM_TOP + titleItemsHeight : LARGE_TITLE_HEIGHT;
    const bottomToolbars = bottomBarHidden ? [] : parts.toolbars.filter((t) => placementOf(t) === 'bottom');
    // `keyboard` toolbars: a glass capsule floating above the keyboard while a
    // text input of this screen is being edited (as Notes' format bar on
    // iOS 26; SwiftUI's input accessory view); nothing otherwise.
    const keyboardToolbars = parts.toolbars.filter((t) => str(t.props['placement']) === 'keyboard');
    const focusedField = this.env.focusedField;
    const keyboardBarShown = keyboardToolbars.some((t) => !isSpacer(t)) && focusedField !== undefined && subtreeHas(node, focusedField);
    const searchMargin = reachesBottom ? SEARCH_BOTTOM_MARGIN : SEARCH_BOTTOM_MARGIN_INSET;
    const bottomBarHeight = bottomToolbars.some((t) => !isSpacer(t))
      ? Math.min(size.height, BOTTOM_GLASS_HEIGHT + searchMargin)
      : 0;
    // In a tab view (a tab bar covers the bottom) the field goes under the
    // title, as iOS 26 places it; elsewhere it floats at the bottom.
    const searchOnTop = (!mac && parts.search !== null && cctx.bottomBar > 0) || searchInSidebar;
    const searchRow = searchInSidebar ? MAC_SIDEBAR_SEARCH_ABOVE + MAC_SIDEBAR_SEARCH_HEIGHT + MAC_SIDEBAR_SEARCH_BELOW : searchOnTop ? SEARCH_TOP_ROW : 0;
    const searchBottom = parts.search && !searchOnTop && !searchInBar ? SEARCH_BOTTOM_HEIGHT + searchMargin : 0;
    // The on-screen keyboard (device mode) covers the screen's bottom. A
    // bottom search field rises above it, as iOS 26 docks it on the
    // keyboard, so what is typed into it can be seen; the bars stay put,
    // covered.
    // With a keyboard bar the keyboard is the bar and its gap taller to the
    // content; a keyboard the page did not measure (frame mode) is the bottom
    // safe area.
    const keyboardAlone = reachesBottom ? this.keyboard : 0;
    const keyboard = keyboardBarShown ? KEYBOARD_BAR_HEIGHT + KEYBOARD_BAR_GAP + (keyboardAlone > 0 ? keyboardAlone : reachesBottom ? safeArea.bottom : 0) : keyboardAlone;
    const searchLift = searchBottom > 0 ? keyboard : 0;
    const contentTop = barHeight + largeHeight + searchRow;
    const actionsBar = sheetBar?.actions ? sheetBar.actions.height : 0;
    const content: Rect = {
      x,
      y: y + contentTop,
      width: size.width,
      height: Math.max(0, size.height - contentTop - Math.max(bottomBarHeight, searchBottom) - actionsBar),
    };
    const chrome: Record<string, Rect> = { content };
    if (!barHidden) {
      chrome['bar'] = { x: barX, y, width: barWidth, height: barHeight };
      // The 44 row the bar's items sit in, under the status bar (on a Mac the
      // title bar's row, which ends at the bar's bottom).
      chrome['barRow'] = { x, y: mac ? y + barHeight - rowHeight : y + statusBar, width: size.width, height: rowHeight };
      // The page styles a unified title bar's title (leading-aligned, larger).
      if (unified) chrome['unified'] = chrome['barRow'];
      // The page styles a Mac sheet's title (leading-aligned, 13 bold).
      if (sheetBar) chrome['sheetBar'] = chrome['barRow'];
    }
    if (large) chrome['largeTitle'] = { x, y: y + barHeight, width: size.width, height: largeHeight };
    if (parts.search && !searchInBar) {
      const marginH = searchInSidebar ? MAC_SIDEBAR_SEARCH_MARGIN_H : searchOnTop ? SEARCH_FIELD_MARGIN_H : SEARCH_BOTTOM_MARGIN_H;
      chrome['searchField'] = {
        x: x + marginH,
        y: searchOnTop ? y + barHeight + largeHeight + (searchInSidebar ? MAC_SIDEBAR_SEARCH_ABOVE : SEARCH_TOP_ABOVE) : y + size.height - searchLift - searchBottom,
        width: Math.max(0, size.width - 2 * marginH),
        height: searchInSidebar ? MAC_SIDEBAR_SEARCH_HEIGHT : searchOnTop ? SEARCH_TOP_HEIGHT : SEARCH_BOTTOM_HEIGHT,
      };
      // The page draws a field under the title as the bar's (a gray capsule)
      // and moves it with the content when that scrolls under the bar.
      if (searchOnTop) chrome['searchTop'] = chrome['searchField'];
    }
    if (bottomBarHeight > 0) {
      chrome['bottomBar'] = { x, y: y + size.height - bottomBarHeight, width: size.width, height: bottomBarHeight };
    }
    if (keyboardBarShown) {
      chrome['keyboardBar'] = { x: x + KEYBOARD_BAR_MARGIN_H, y: y + size.height - keyboard, width: Math.max(0, size.width - 2 * KEYBOARD_BAR_MARGIN_H), height: KEYBOARD_BAR_HEIGHT };
    }
    const title = str(node.props['title']) ?? '';
    // `.navigationSubtitle`: a second line under the title in a Mac's unified
    // title bar (11 regular, secondary); after the title in a plain one.
    const { platform } = this.env;
    const subtitle = mac ? (str(node.props['subtitle']) ?? '') : '';
    const shownTitle = subtitle.length > 0 && !unified && !sheetBar ? `${title} — ${subtitle}` : title;
    const subtitleFont = makeFont('system', textStyleSize('subheadline', dynamicTypeSize, platform), 400);
    // A unified title bar's title is the window title (15 semibold on macOS 14); a plain bar's and iOS's is the headline.
    const titleFont = makeFont('system', textStyleSize(unified ? 'title3' : 'headline', dynamicTypeSize, platform), sheetBar ? 700 : 600);
    const largeTitleFont = makeFont('system', textStyleSize('largeTitle', dynamicTypeSize, platform), 700);
    const backFont = makeFont('system', textStyleSize('body', dynamicTypeSize, platform), 400);

    // Toolbar items at their ideal size, grouped per toolbar.
    const rowY = (chrome['barRow'] ?? { y: y + statusBar }).y;
    interface Group {
      toolbar: LayoutNode;
      placement: string;
      sizes: Size[];
      /** Leading and trailing groups: each item's glass slot width. */
      slots: number[] | null;
      total: number;
    }
    // The navigation bar's groups; spacers there only separate them (the
    // groups are 8 apart anyway) and draw nothing.
    for (const spacer of parts.toolbars.filter(isSpacer)) this.hide(spacer, node);
    const barToolbars = barHidden
      ? []
      : parts.toolbars.filter((t) => {
          const placement = placementOf(t);
          return placement !== 'bottom' && placement !== 'secondary' && placement !== 'largeTitle' && placement !== 'keyboard' && !isSpacer(t) && !actionToolbars.includes(t);
        });
    // A Mac sheet's actions: 22 push buttons at the trailing end of the
    // sheet's bottom bar, the trailing placement's (confirm) last, 8 apart.
    if (sheetBar?.actions) {
      const actions = sheetBar.actions;
      let cursor = actions.x + actions.width - MAC_SHEET_ACTIONS_INSET;
      const ordered = [...actionToolbars.filter((t) => placementOf(t) === 'trailing'), ...actionToolbars.filter((t) => placementOf(t) === 'leading')];
      for (const toolbar of ordered) {
        const itemCtx: Context = { ...this.childContext(toolbar, cctx), navBarItem: false };
        let first = cursor;
        let last = cursor;
        [...toolbar.children].reverse().forEach((item) => {
          const itemSize = this.measure(item, itemCtx, { width: null, height: null });
          this.place(item, itemCtx, { width: null, height: null }, cursor - itemSize.width, actions.y + (actions.height - itemSize.height) / 2);
          first = cursor - itemSize.width;
          cursor = first - MAC_SHEET_ACTIONS_GAP;
        });
        this.setFrame(toolbar, first, actions.y, { width: Math.max(0, last - first), height: actions.height });
      }
    }
    const secondaryToolbars = barHidden ? [] : parts.toolbars.filter((t) => placementOf(t) === 'secondary' && !isSpacer(t));
    const groups: Group[] = barToolbars.map((toolbar) => {
      const itemCtx = this.childContext(toolbar, cctx);
      const sizes = toolbar.children.map((item) => this.measure(item, itemCtx, { width: null, height: null }));
      const placement = placementOf(toolbar) ?? 'trailing';
      if (placement === 'leading' || placement === 'trailing') {
        const slots = sizes.map((s, i) => navSlot(toolbar.children[i] as LayoutNode, s.width, glass, glassPad));
        return { toolbar, placement, sizes, slots, total: slots.reduce((sum, w) => sum + w, 0) };
      }
      const total = sizes.reduce((sum, s) => sum + s.width, 0) + Math.max(0, sizes.length - 1) * NAV_ITEM_SPACING;
      return { toolbar, placement, sizes, slots: null, total };
    });
    // The leading and trailing runs: consecutive toolbars of one placement
    // share a capsule, a spacer of that placement ends one.
    interface Run {
      placement: 'leading' | 'trailing';
      groups: Group[];
      /** A segmented picker's own run: the glass is the control, inside its margin. */
      segmented?: boolean;
      /** `.sharedBackgroundVisibility(.hidden)`: a run of its own with no glass. */
      bare?: boolean;
      /** A Mac sidebar's item: no glass, its slots `groupGap` apart with no padding. */
      plain?: boolean;
      /** The capsule's width: the slots 12 apart, a point past the end slots (none for a lone item). */
      width: number;
    }
    const groupOf = new Map(groups.map((g) => [g.toolbar, g]));
    const runs: Run[] = [];
    const open: Partial<Record<'leading' | 'trailing', Run>> = {};
    const runWidth = (run: Run): number => {
      const slots = run.groups.flatMap((g) => g.slots ?? []);
      if (slots.length === 0) return 0;
      if (run.plain) return slots.reduce((sum, w) => sum + w, 0) + (slots.length - 1) * groupGap;
      const inner = slots.reduce((sum, w) => sum + w, 0) + (slots.length - 1) * runGap;
      return slots.length > 1 ? inner + 2 * (mac ? MAC_BAR_RUN_PAD : NAV_RUN_PAD) : inner;
    };
    for (const toolbar of parts.toolbars) {
      const placement = placementOf(toolbar);
      if (placement !== 'leading' && placement !== 'trailing') continue;
      if (isSpacer(toolbar)) {
        delete open[placement];
        continue;
      }
      const group = groupOf.get(toolbar);
      if (!group) continue;
      // A segmented picker is a capsule of its own: it neither joins nor is joined (iOS 26
      // draws its glass, and an item's shared glass around it would show as a second capsule).
      if (toolbar.children.some(isSegmentedItem)) {
        delete open[placement];
        runs.push({ placement, groups: [group], width: 0, segmented: true });
        continue;
      }
      // A Mac sidebar's items are plain symbols in slots of their own, no glass.
      if (plainBar && toolbar.children.every((item) => isSymbolItem(item) || unwrapStyled(item).kind === 'menu')) {
        delete open[placement];
        group.slots = group.sizes.map((s) => Math.max(glass, s.width + 2 * MAC_SIDEBAR_ITEM_PAD_H));
        runs.push({ placement, groups: [group], width: 0, bare: true, plain: true });
        continue;
      }
      // A Mac's text item is a capsule of its own (macOS 26), neither joining nor joined.
      if (mac && toolbar.children.some((item) => !isSymbolItem(item) && unwrapStyled(item).kind === 'button')) {
        delete open[placement];
        runs.push({ placement, groups: [group], width: 0 });
        continue;
      }
      // An item that hides the bar's shared glass stands alone, at its own width.
      if (str(toolbar.props['sharedBackground']) === 'hidden') {
        delete open[placement];
        group.slots = group.sizes.map((s) => s.width);
        runs.push({ placement, groups: [group], width: 0, bare: true });
        continue;
      }
      let run = open[placement];
      if (!run) {
        run = { placement, groups: [], width: 0 };
        open[placement] = run;
        runs.push(run);
      }
      run.groups.push(group);
    }
    for (const run of runs) run.width = runWidth(run);
    // A Mac sidebar too narrow for its trailing items after the window
    // buttons: they all go, hidden here, to the overflow menu (`»`) at the
    // trailing end of the detail's bar (`barOverflow`), as the Mac moves
    // them all into its toolbar's overflow.
    if (plainBar && !barHidden) {
      // The window buttons sit over a sidebar at the window's top-left (its panel is inset 8 from the edge).
      const buttons = y <= 0 && barX <= MAC_NAVSPLIT_SIDEBAR_INSET + EPSILON && safeArea.top > 0;
      const floor = (buttons ? Math.max(MAC_WINDOW_BUTTONS_ROOM, x + MAC_BAR_LEADING_MARGIN) : x + MAC_BAR_LEADING_MARGIN) + runs.filter((r) => r.placement === 'leading' && r.width > 0).reduce((sum, r) => sum + r.width + groupGap, 0);
      const edge = x + size.width - MAC_BAR_MARGIN_TRAILING - (cctx.barTrailing ?? 0);
      const trailing = runs.filter((r) => r.placement === 'trailing' && r.width > 0);
      const needed = trailing.reduce((sum, r) => sum + r.width + groupGap, 0) - groupGap;
      if (trailing.length > 0 && needed > edge - floor) {
        for (const run of trailing) {
          runs.splice(runs.indexOf(run), 1);
          for (const group of run.groups) for (const item of group.toolbar.children) this.hide(item, node);
        }
        this.sidebarOverflowed = true;
      }
    }
    const runsWidth = (placement: string): number =>
      runs.filter((r) => r.placement === placement && r.width > 0).reduce((sum, r) => sum + r.width + groupGap, 0);
    const leadingWidth = runsWidth('leading');
    const trailingWidth = runsWidth('trailing');
    const principal = groups.some((g) => g.placement === 'principal');

    // `.secondaryAction` items (placement `secondary`), as iOS treats them:
    // collapsed into the More button (chrome `more`: an ellipsis in a glass
    // circle at the trailing end, whose menu lists them). A screen with the
    // editor toolbar role in a regular width shows them inline before the
    // trailing items, as iPadOS does, clear of the title, and collapses only
    // those that do not fit.
    interface SecondaryItem {
      toolbar: LayoutNode;
      item: LayoutNode;
      size: Size;
      slot: number;
    }
    const secondaryItems: SecondaryItem[] = secondaryToolbars.flatMap((toolbar) => {
      const itemCtx = this.childContext(toolbar, cctx);
      return toolbar.children.map((item) => {
        const itemSize = this.measure(item, itemCtx, { width: null, height: null });
        return { toolbar, item, size: itemSize, slot: navSlot(item, itemSize.width, glass, glassPad) };
      });
    });
    // Items of one toolbar share its glass capsule; separate toolbars sit 12 apart, as the trailing runs do.
    const secondaryGap = (previous: SecondaryItem | undefined, entry: SecondaryItem): number =>
      previous && previous.toolbar !== entry.toolbar ? groupGap : 0;
    const secondaryWidth = (entries: readonly SecondaryItem[]): number =>
      entries.reduce((sum, entry, i) => sum + secondaryGap(entries[i - 1], entry) + entry.slot, 0);
    let inlineSecondary: SecondaryItem[] = [];
    if (secondaryItems.length > 0 && str(node.props['toolbarRole']) === 'editor' && screen.width >= REGULAR_WIDTH) {
      // The room between the title (centered, at its width up to 56% of the
      // bar) and the trailing groups; the More button takes a slot of it
      // once anything collapses.
      const titleWidth = principal ? 0 : Math.min(this.measurer.measureWidth(title, titleFont), size.width * 0.56);
      const roomStart = x + (size.width + titleWidth) / 2 + groupGap;
      const roomEnd = x + size.width - NAV_ITEM_MARGIN - trailingWidth;
      const fitting = (room: number): SecondaryItem[] => {
        const out: SecondaryItem[] = [];
        let used = 0;
        for (const entry of secondaryItems) {
          const width = secondaryGap(out[out.length - 1], entry) + entry.slot;
          if (used + width > room) break;
          used += width;
          out.push(entry);
        }
        return out;
      };
      inlineSecondary = secondaryWidth(secondaryItems) <= roomEnd - roomStart ? secondaryItems : fitting(roomEnd - roomStart - glass - groupGap);
    }
    const collapsedSecondary = secondaryItems.filter((entry) => !inlineSecondary.includes(entry));

    // Back button, iOS 26: the chevron alone in a glass circle (the previous
    // title no longer shows).
    const backLabel = '';
    // A Mac window's own title bar (the top of the screen, a title bar in the
    // safe area) starts with the window buttons; the items follow them.
    const windowButtons = mac && y <= 0 && barX <= EPSILON && safeArea.top > 0;
    const leadingStart = x + (windowButtons ? MAC_WINDOW_BUTTONS_ROOM : mac ? MAC_BAR_LEADING_MARGIN : NAV_ITEM_MARGIN) + (cctx.barLeading ?? 0);
    const glassY = rowY + (rowHeight - glass) / 2;
    if (depth > 0 && !barHidden) {
      chrome['back'] = { x: leadingStart, y: glassY, width: glass, height: glass };
    }

    // Place the toolbar groups and remember how much of the row they occupy.
    const back = chrome['back'];
    // On a Mac the back button is the first slot of the leading capsule (macOS 26).
    let leadingCursor = back ? back.x + back.width + (mac ? 0 : groupGap) : leadingStart;
    let trailingEdge = x + size.width - (mac ? MAC_BAR_MARGIN_TRAILING : NAV_ITEM_MARGIN) - (cctx.barTrailing ?? 0);
    // On a Mac the search field is the trailing-most item of the title bar
    // (chrome `searchBar` tells the page to draw it as the toolbar's field).
    if (searchInBar) {
      const width = Math.max(0, Math.min(MAC_SEARCH_FIELD_WIDTH, trailingEdge - leadingStart));
      chrome['searchField'] = { x: trailingEdge - width, y: rowY + (rowHeight - MAC_BAR_ITEM_SIZE) / 2, width, height: MAC_BAR_ITEM_SIZE };
      chrome['searchBar'] = chrome['searchField'];
      trailingEdge -= width + groupGap;
    }
    // The More button is the trailing-most item (before a Mac's search
    // field); beside a Mac sidebar too narrow for its items it is the
    // overflow menu (`»`) of those (`barOverflow`).
    if (collapsedSecondary.length > 0 || cctx.barOverflow === true) {
      chrome['more'] = { x: trailingEdge - glass, y: glassY, width: glass, height: glass };
      if (cctx.barOverflow === true) chrome['overflow'] = chrome['more'];
      trailingEdge -= glass + groupGap;
    }
    const placeGroup = (group: Group, startX: number, top: number, rowHeight: number, spacing: number): void => {
      const itemCtx = this.childContext(group.toolbar, cctx);
      let cursor = startX;
      group.toolbar.children.forEach((item, i) => {
        const itemSize = group.sizes[i] as Size;
        // In a glass slot the item is centered in it.
        const slot = group.slots?.[i];
        const itemX = slot !== undefined ? cursor + (slot - itemSize.width) / 2 : cursor;
        this.place(item, itemCtx, { width: null, height: null }, itemX, top + (rowHeight - itemSize.height) / 2);
        cursor += (slot ?? itemSize.width) + spacing;
      });
    };
    // A run's capsule (chrome `leadingGlass0` …, `trailingGlass0` …) and its
    // items, in slots 12 apart from `startX`; each toolbar's frame spans its
    // own items' slots. Runs of a placement follow their order in the
    // content, leading-to-trailing.
    // The capsules are numbered as they are drawn (the page reads them until one is missing).
    const glassCount = { leading: 0, trailing: 0 };
    const placeRun = (run: Run, startX: number): void => {
      const margin = run.segmented ? BAR_SEGMENTED_MARGIN : 0;
      if (!run.bare) {
        chrome[`${run.placement}Glass${glassCount[run.placement]++}`] = { x: startX + margin, y: glassY, width: Math.max(0, run.width - 2 * margin), height: glass };
      }
      const count = run.groups.reduce((sum, g) => sum + (g.slots?.length ?? 0), 0);
      let cursor = startX + (count > 1 && !run.plain ? (mac ? MAC_BAR_RUN_PAD : NAV_RUN_PAD) : 0);
      const slotGap = run.plain ? groupGap : runGap;
      for (const group of run.groups) {
        const itemCtx = this.childContext(group.toolbar, cctx);
        const first = cursor;
        group.toolbar.children.forEach((item, i) => {
          const itemSize = group.sizes[i] as Size;
          const slot = group.slots?.[i] ?? itemSize.width;
          this.place(item, itemCtx, { width: null, height: null }, cursor + (slot - itemSize.width) / 2, rowY + (rowHeight - itemSize.height) / 2);
          cursor += slot + slotGap;
        });
        const last = group.toolbar.children.length > 0 ? cursor - slotGap : first;
        this.setFrame(group.toolbar, first, rowY, { width: last - first, height: rowHeight });
      }
    };
    const leadingRuns = runs.filter((r) => r.placement === 'leading' && r.width > 0);
    for (const run of leadingRuns) {
      placeRun(run, leadingCursor);
      leadingCursor += run.width + groupGap;
    }
    // A Mac's back button: its capsule is the first leading one's, widened to
    // take it in, or one of its own.
    if (mac && back) {
      const first = leadingRuns[0];
      const merged = first && !first.bare && !first.segmented ? chrome['leadingGlass0'] : undefined;
      if (merged) chrome['leadingGlass0'] = { ...merged, x: back.x, width: merged.x + merged.width - back.x };
      else chrome[`leadingGlass${glassCount.leading++}`] = { ...back };
      if (leadingRuns.length === 0) leadingCursor = back.x + back.width + groupGap;
    }
    const trailingRuns = runs.filter((r) => r.placement === 'trailing' && r.width > 0);
    let trailingCursor = trailingEdge - (trailingWidth > 0 ? trailingWidth - groupGap : 0);
    const trailingStart = trailingCursor;
    for (const run of trailingRuns) {
      placeRun(run, trailingCursor);
      trailingCursor += run.width + groupGap;
    }
    if (trailingRuns.length > 0) trailingEdge = trailingStart - groupGap;
    for (const group of groups) {
      if (group.placement !== 'principal') continue;
      const startX = x + (size.width - group.total) / 2;
      this.setFrame(group.toolbar, startX, rowY, { width: group.total, height: rowHeight });
      placeGroup(group, startX, rowY, rowHeight, NAV_ITEM_SPACING);
    }
    // The inline secondary items, right-aligned before the trailing groups,
    // each toolbar framed around its own; the collapsed ones are hidden (the
    // renderer lists the items without a frame in the More menu).
    if (inlineSecondary.length > 0) {
      const total = secondaryWidth(inlineSecondary);
      let cursor = trailingEdge - total;
      const extents = new Map<LayoutNode, { start: number; end: number }>();
      for (const [i, entry] of inlineSecondary.entries()) {
        cursor += secondaryGap(inlineSecondary[i - 1], entry);
        const itemCtx = this.childContext(entry.toolbar, cctx);
        this.place(entry.item, itemCtx, { width: null, height: null }, cursor + (entry.slot - entry.size.width) / 2, rowY + (rowHeight - entry.size.height) / 2);
        const extent = extents.get(entry.toolbar);
        extents.set(entry.toolbar, { start: extent?.start ?? cursor, end: cursor + entry.slot });
        cursor += entry.slot;
      }
      for (const [toolbar, extent] of extents) this.setFrame(toolbar, extent.start, rowY, { width: extent.end - extent.start, height: rowHeight });
      trailingEdge -= total + groupGap;
    }
    for (const toolbar of secondaryToolbars) {
      const items = toolbar.children;
      if (!inlineSecondary.some((entry) => entry.toolbar === toolbar)) this.hide(toolbar, node);
      else for (const item of items) if (collapsedSecondary.some((entry) => entry.item === item)) this.hide(item, node);
    }
    // `.largeTitle` items (iOS 26): in the large title row, in place of the
    // title's text, from its leading margin and centered in the row; the
    // renderer moves the toolbar into the title element, so they scroll with
    // the content as the title does, and the bar shows the title once the row
    // has gone under it. While the title is inline there is no row: hidden.
    for (const toolbar of parts.toolbars) {
      if (str(toolbar.props['placement']) !== 'largeTitle' || isSpacer(toolbar)) continue;
      const row = chrome['largeTitle'];
      if (!row) {
        this.hide(toolbar, node);
        continue;
      }
      const itemCtx = this.childContext(toolbar, cctx);
      const room: Proposal = { width: Math.max(0, row.width - 2 * NAV_ITEM_MARGIN), height: null };
      let cursor = row.x + NAV_ITEM_MARGIN;
      // The items' band: the row below its 10 top, as tall as the tallest item.
      const band = row.height - LARGE_TITLE_ITEM_TOP;
      for (const item of toolbar.children) {
        const itemSize = this.measure(item, itemCtx, room);
        this.place(item, itemCtx, room, cursor, row.y + LARGE_TITLE_ITEM_TOP + (band - itemSize.height) / 2);
        cursor += itemSize.width + NAV_ITEM_SPACING;
      }
      this.setFrame(toolbar, row.x, row.y, { width: row.width, height: row.height });
    }
    if (bottomBarHeight > 0) {
      this.placeBottomToolbars(bottomToolbars, cctx, chrome, x, y + size.height - bottomBarHeight, size.width);
    }
    const keyboardBar = chrome['keyboardBar'];
    if (keyboardBar) this.placeKeyboardToolbars(keyboardToolbars, cctx, keyboardBar);
    else for (const toolbar of keyboardToolbars) this.hide(toolbar, node);
    if (parts.search) {
      // The field is the whole capsule (its own measure is a 36pt field).
      const field = chrome['searchField'] as Rect;
      this.place(parts.search, cctx, { width: field.width, height: field.height }, field.x, field.y);
      this.setFrame(parts.search, field.x, field.y, { width: field.width, height: field.height });
    }

    // The inline title: centered, in a compact width no wider than 56% of the
    // bar (as UIKit keeps it on a phone; a wide bar on an iPad or in a split
    // view's column gives a title all the room its items leave), and clear of
    // whatever the back button and the bar items occupy on either side. Its
    // width is rounded up to whole pixels: snapping floors both edges, which
    // takes up to a pixel off a fractional width at a fractional origin, and
    // the title truncates with an ellipsis ("Animati…") when its box is any
    // narrower than its text.
    const direct = parts.content ? unwrapStyled(parts.content) : null;
    const scroller = parts.content ? this.screenScroller(parts.content) : null;
    const titleScrolls = large && scroller !== null;
    // A large title that scrolls away collapses into the inline title, which
    // the renderer shows once it has (`data-sb-collapsed`).
    if (tabToolbar) {
      // The tab picker stands where the title would be.
    } else if (mac && cctx.splitSidebar) {
      // A Mac's sidebar shows no title.
    } else if ((unified || sheetBar) && !principal && !barHidden) {
      // The unified title bar's title (and a Mac sheet's): leading-aligned
      // after the back button and the leading items (2 past the window
      // buttons' room when nothing is before it), cut to the room before
      // the trailing ones.
      const trailingUsed = trailingWidth > 0 || chrome['more'] !== undefined || inlineSecondary.length > 0 || searchInBar;
      // A Mac toolbar's title is 12 after the window buttons' room or after the leading capsules.
      const titleX = unified
        ? (back || leadingWidth > 0 ? leadingCursor - groupGap : leadingStart) + MAC_TITLE_GAP
        : Math.max(x + MAC_SHEET_TITLE_X, leadingCursor);
      const roomEnd = trailingUsed ? trailingEdge + groupGap - 8 : x + size.width - NAV_ITEM_MARGIN;
      const titleWidth = Math.min(Math.ceil(this.measurer.measureWidth(title, titleFont) - 0.001), Math.floor(Math.max(0, roomEnd - titleX)));
      if (subtitle.length > 0) {
        // The title and the subtitle stacked, centered in the row.
        const block = titleFont.lineHeight + subtitleFont.lineHeight;
        const top = rowY + (rowHeight - block) / 2;
        chrome['title'] = { x: titleX, y: top, width: titleWidth, height: titleFont.lineHeight };
        const subtitleWidth = Math.min(Math.ceil(this.measurer.measureWidth(subtitle, subtitleFont) - 0.001), Math.floor(Math.max(0, roomEnd - titleX)));
        chrome['subtitle'] = { x: titleX, y: top + titleFont.lineHeight, width: subtitleWidth, height: subtitleFont.lineHeight };
      } else {
        chrome['title'] = { x: titleX, y: rowY, width: titleWidth, height: rowHeight };
      }
    } else if ((!large || titleScrolls) && !principal && !barHidden) {
      const leftUsed = Math.max(
        back ? back.x + back.width - x : 0,
        leadingWidth > 0 ? leadingCursor - groupGap - x : 0,
        cctx.barLeading ? leadingStart - groupGap - x : 0,
      );
      const trailingUsed = trailingWidth > 0 || chrome['more'] !== undefined || inlineSecondary.length > 0;
      const rightUsed = trailingUsed ? x + size.width - (trailingEdge + groupGap) : 0;
      // As UIKit places it: centered in the bar when it fits there; else slid
      // off-center into the room the items leave (between them, 8 clear of
      // each side), and truncated only when it does not fit even there.
      const roomStart = x + leftUsed + 8;
      const roomEnd = x + size.width - rightUsed - 8;
      const compactCap = screen.width < REGULAR_WIDTH ? Math.floor(size.width * 0.56) : Infinity;
      const titleWidth = Math.min(
        Math.ceil(this.measurer.measureWidth(shownTitle, titleFont) - 0.001),
        compactCap,
        Math.floor(Math.max(0, roomEnd - roomStart)),
      );
      const centered = x + (size.width - titleWidth) / 2;
      // A Mac's compact title bar: the title leading-aligned after the window buttons (macOS 26).
      const leading = mac ? x + (windowButtons ? MAC_COMPACT_TITLE_X : NAV_ITEM_MARGIN) : null;
      const titleX = leading !== null ? Math.max(roomStart, Math.min(leading, roomEnd - titleWidth)) : Math.max(roomStart, Math.min(centered, roomEnd - titleWidth));
      chrome['title'] = { x: titleX, y: rowY, width: titleWidth, height: rowHeight };
    }
    this.setChrome(node, chrome);

    if (node.id >= 0) {
      this.fonts.set(node.id, large ? largeTitleFont : titleFont);
      const texts: Record<string, TextLayout> = {
        title: { font: titleFont, lines: [shownTitle], alignment: unified || sheetBar || mac ? 'leading' : 'center', truncated: false },
      };
      if (chrome['subtitle']) texts['subtitle'] = { font: subtitleFont, lines: [subtitle], alignment: 'leading', truncated: false };
      if (depth > 0) texts['back'] = { font: backFont, lines: [backLabel], alignment: 'leading', truncated: false };
      if (large) {
        const wrapped = wrapText(title, largeTitleFont, size.width - 32, 1, this.measurer);
        texts['largeTitle'] = {
          font: largeTitleFont,
          lines: wrapped.lines,
          alignment: 'leading',
          truncated: wrapped.truncated,
        };
      }
      this.chromeText.set(node.id, texts);
    }

    const child = parts.content;
    if (!child) return;
    // The room a bottom toolbar or search field takes; scroll views run under
    // it (iOS 26), other content stops above it.
    const bottomCover = Math.max(bottomBarHeight, searchBottom);
    // Under a tab bar the bar (which includes the bottom safe area) is what
    // non-scrolling content must clear; a bottom toolbar already excluded it.
    const clears = bottomBarHeight === 0 && y + size.height >= screen.height ? Math.max(safeArea.bottom, cctx.bottomBar) : 0;
    // The keyboard covers the bars (but not the search field, lifted above
    // it): the content stops above it as on iOS, and a scroll view runs under
    // only the part of a bar still showing above it, or under the whole
    // search field.
    const bottomInset = Math.max(clears, keyboard - (searchLift > 0 ? 0 : bottomCover));
    // Phase 8: `.ignoresSafeArea` content grows up under the bar (and the
    // large title / search field), which is transparent over it.
    const contentCtx: Context = {
      ...cctx,
      safeTop: content.y,
      barInset: 0,
      barLeading: 0,
      barTrailing: 0,
      barOverflow: false,
      barAbove: undefined,
      ...(bottomCover > 0 ? { bottomBar: bottomCover, bottomEdge: y + size.height } : {}),
    };
    if (keyboard > 0) {
      contentCtx.bottomBar = searchLift > 0 ? bottomCover : Math.max(0, (bottomCover > 0 ? bottomCover : cctx.bottomBar) - keyboard);
      contentCtx.bottomEdge = y + size.height - keyboard;
    }
    if (scroller) {
      // iOS 26: the scroll view starts at the top of the screen and its
      // content scrolls under the bar (and the large title moves with it);
      // the content starts below them. It runs to the bottom of the screen,
      // under a bottom toolbar or search field too.
      const top = content.y - y;
      this.scrollInsets.set(scroller, {
        bar: barHeight,
        largeTitle: titleScrolls ? (chrome['largeTitle'] as Rect) : null,
        search: searchRow,
      });
      this.place(child, contentCtx, { width: content.width, height: Math.max(0, content.height + top + bottomCover - keyboard) }, content.x, y);
      return;
    }
    if (direct) this.scrollInsets.delete(direct);
    // A scroll view deeper in the content that touches the bar runs under it
    // (`scrollEdgeGrowth`); not under a large title, which stays put then.
    // One that reaches the bottom of the content runs down under all the
    // content clears there.
    if (!large) contentCtx.barAbove = { top: content.y, height: content.y - y, x, width: size.width };
    if (keyboard > 0) {
      contentCtx.bottomBar = searchLift > 0 ? bottomCover + clears : Math.max(0, (bottomCover > 0 ? bottomCover + clears : cctx.bottomBar) - keyboard);
    } else if (bottomCover > 0) {
      contentCtx.bottomBar = bottomCover + clears;
    }
    if (direct && direct.kind === 'scrollview') {
      this.place(child, contentCtx, { width: content.width, height: content.height }, content.x, content.y);
      return;
    }
    const area: Size = { width: content.width, height: Math.max(0, content.height - bottomInset) };
    const proposal: Proposal = { width: area.width, height: area.height };
    const childSize = this.measure(child, contentCtx, proposal);
    const { dx, dy } = alignIn(area, childSize, 'center');
    this.place(child, contentCtx, proposal, content.x + dx, content.y + dy);
  }

  /**
   * The list or vertical scroll view that is a screen's content: through
   * modifiers and wrappers that put their child at their own origin and size
   * (a `GeometryReader`'s or a one-child `ZStack`'s child, a decoration's
   * content), as UIKit finds a screen's
   * scroll view. Food Truck's Truck page is a `GeometryReader` around its
   * `ScrollView`, its Donuts page a `ZStack` with a background around one:
   * their large titles scroll and collapse as on iOS. Null when there is none.
   */
  private screenScroller(node: LayoutNode): LayoutNode | null {
    let current: LayoutNode | undefined = node;
    for (let depth = 0; current && depth < 32; depth++) {
      if (current.kind === 'list' || (current.kind === 'scrollview' && this.scrollAxes(current).vertical)) return current;
      if ((current.kind === 'styled' || current.kind === 'geometry' || current.kind === 'zstack') && current.children.length === 1) {
        current = current.children[0];
      } else if (current.kind === 'decorated') {
        current = current.children[0];
      } else {
        return null;
      }
    }
    return null;
  }

  /**
   * iOS 26's bottom toolbar: the items of the `bottom` toolbars, in order,
   * float on Liquid Glass in a 48 row at the top of the bottom bar's band
   * (`top`), 28 in from the sides. Consecutive items share a capsule; a
   * `ToolbarSpacer` (an empty toolbar with `spacer`) ends one. Flexible
   * spacers share the room the groups leave (with none the row is centered)
   * and fixed ones are 8 wide. An icon takes a 50 slot (a lone one is a 48
   * circle), text its width and 18 either side, 6 between slots; each item
   * is centered in its slot. The capsules are the screen's chrome
   * `bottomGlass0` … (drawn under the items); each toolbar's frame spans its
   * items.
   */
  /**
   * `keyboard` toolbars (`ToolbarItemGroup(placement: .keyboard)`): their
   * items in one row across the bar, 16 in from either edge, as SwiftUI
   * lays them out, an `HStack` of the items in the toolbars' order in which
   * a `Spacer` (a child, or a `ToolbarSpacer` of the placement) takes the
   * room left over; each toolbar's frame is the bar.
   */
  private placeKeyboardToolbars(toolbars: LayoutNode[], cctx: Context, bar: Rect): void {
    const items: LayoutNode[] = [];
    for (const toolbar of toolbars) {
      if (str(toolbar.props['spacer']) !== null) items.push({ id: -1, kind: 'spacer', props: { minLength: str(toolbar.props['spacer']) === 'fixed' ? BOTTOM_FIXED_SPACER : null }, children: [] });
      else items.push(...toolbar.children);
    }
    const row: LayoutNode = { id: -1, kind: 'hstack', props: { spacing: null, alignment: 'center' }, children: items };
    const first = toolbars.find((toolbar) => str(toolbar.props['spacer']) === null) ?? (toolbars[0] as LayoutNode);
    const itemCtx = this.childContext(first, cctx);
    const inner = Math.max(0, bar.width - 2 * NAV_ITEM_MARGIN);
    const proposal: Proposal = { width: inner, height: bar.height };
    const rowSize = this.measure(row, itemCtx, proposal);
    this.place(row, itemCtx, proposal, bar.x + NAV_ITEM_MARGIN, bar.y + (bar.height - rowSize.height) / 2);
    for (const toolbar of toolbars) this.setFrame(toolbar, bar.x, bar.y, { width: bar.width, height: bar.height });
  }

  private placeBottomToolbars(
    toolbars: LayoutNode[],
    cctx: Context,
    chrome: Record<string, Rect>,
    x: number,
    top: number,
    width: number,
  ): void {
    interface Item {
      node: LayoutNode;
      toolbar: LayoutNode;
      size: Size;
      slot: number;
    }
    type Token = { kind: 'group'; items: Item[]; width: number } | { kind: 'flexible' } | { kind: 'fixed' };
    const tokens: Token[] = [];
    let open: Item[] = [];
    const close = (): void => {
      if (open.length === 0) return;
      const icon = (item: Item): boolean => item.size.width < NAV_GLASS_SIZE;
      // a lone icon is a circle; a wider one (a symbol and a chevron) keeps its capsule
      const lone = open.length === 1 && icon(open[0] as Item) && (open[0] as Item).slot <= BOTTOM_ICON_SLOT;
      if (lone) (open[0] as Item).slot = BOTTOM_GLASS_HEIGHT;
      const slots = open.reduce((sum, item) => sum + item.slot, 0);
      tokens.push({ kind: 'group', items: open, width: slots + (open.length - 1) * BOTTOM_ITEM_GAP });
      open = [];
    };
    for (const toolbar of toolbars) {
      const spacer = str(toolbar.props['spacer']);
      if (spacer !== null) {
        close();
        tokens.push({ kind: spacer === 'fixed' ? 'fixed' : 'flexible' });
        continue;
      }
      const itemCtx = this.childContext(toolbar, cctx);
      for (const node of toolbar.children) {
        const size = this.measure(node, itemCtx, { width: null, height: null });
        const style = bottomBarStyle(node);
        if (style === 'prominent') {
          // its own circle, between the capsules of the items around it
          close();
          tokens.push({ kind: 'group', items: [{ node, toolbar, size, slot: BOTTOM_GLASS_HEIGHT }], width: BOTTOM_GLASS_HEIGHT });
          continue;
        }
        // A bar's own toggle takes its label's slot, as a plain button does.
        const slot =
          style === 'bordered'
            ? size.width + BOTTOM_BORDERED_INSET
            : style === 'toggle'
              ? size.width + BOTTOM_TOGGLE_INSET
              : style === 'barToggle'
                ? bottomSlot(node, { width: size.width - 2 * BAR_TOGGLE_PAD_H, height: size.height })
                : bottomSlot(node, size);
        open.push({ node, toolbar, size, slot });
      }
    }
    close();
    const inner = Math.max(0, width - 2 * BOTTOM_GLASS_MARGIN_H);
    // Two capsules with no spacer between them (a prominent item's circle
    // beside the rest) sit 12 apart.
    const gapBefore = (index: number): number =>
      index > 0 && tokens[index]?.kind === 'group' && tokens[index - 1]?.kind === 'group' ? BOTTOM_GROUP_GAP : 0;
    let used = 0;
    let flexible = 0;
    tokens.forEach((token, index) => {
      used += gapBefore(index);
      if (token.kind === 'group') used += token.width;
      else if (token.kind === 'fixed') used += BOTTOM_FIXED_SPACER;
      else flexible += 1;
    });
    const share = flexible > 0 ? Math.max(0, inner - used) / flexible : 0;
    let cursor = flexible > 0 ? x + BOTTOM_GLASS_MARGIN_H : x + (width - used) / 2;
    const spans = new Map<LayoutNode, { start: number; end: number }>();
    let glass = 0;
    for (const [index, token] of tokens.entries()) {
      cursor += gapBefore(index);
      if (token.kind === 'flexible') {
        cursor += share;
        continue;
      }
      if (token.kind === 'fixed') {
        cursor += BOTTOM_FIXED_SPACER;
        continue;
      }
      // A button toggle's capsule is taller, about the row's center.
      const height = token.items.some((item) => bottomBarStyle(item.node) === 'toggle') ? BOTTOM_TOGGLE_GLASS_HEIGHT : BOTTOM_GLASS_HEIGHT;
      chrome[`bottomGlass${glass}`] = { x: cursor, y: top - (height - BOTTOM_GLASS_HEIGHT) / 2, width: token.width, height };
      glass += 1;
      for (const item of token.items) {
        const itemCtx = this.childContext(item.toolbar, cctx);
        this.place(item.node, itemCtx, { width: null, height: null }, cursor + (item.slot - item.size.width) / 2, top + (BOTTOM_GLASS_HEIGHT - item.size.height) / 2);
        const span = spans.get(item.toolbar);
        spans.set(item.toolbar, { start: span ? span.start : cursor, end: cursor + item.slot });
        cursor += item.slot + BOTTOM_ITEM_GAP;
      }
      cursor -= BOTTOM_ITEM_GAP;
    }
    for (const toolbar of toolbars) {
      const span = spans.get(toolbar);
      if (span) this.setFrame(toolbar, span.start, top, { width: span.end - span.start, height: BOTTOM_GLASS_HEIGHT });
    }
  }

  /**
   * The tab bar (iOS 26) is a glass capsule floating over the bottom of the
   * content (TAB_BAR_* above), as wide as its equal-width items (one per tab)
   * up to the width less the margins, centered; what it
   * covers (the capsule and the gap under it) is the content's bottom inset.
   * Every `tab` gets the tab view's frame; only the selected one lays its
   * content out, the others are reported in `hidden`.
   */
  private placeTabView(node: LayoutNode, cctx: Context, x: number, y: number, size: Size): void {
    const { screen } = this.env;
    if (this.env.platform === 'macos') {
      this.placeMacTabView(node, cctx, x, y, size);
      return;
    }
    const marginBottom = y + size.height >= screen.height - EPSILON ? TAB_BAR_MARGIN_BOTTOM : TAB_BAR_MARGIN_BOTTOM_INSET;
    const barHeight = Math.min(size.height, TAB_BAR_HEIGHT + marginBottom);
    const capsuleHeight = Math.min(TAB_BAR_HEIGHT, barHeight);
    const tabs = node.children;
    const n = tabs.length;
    const width = Math.min(Math.max(0, size.width - 2 * TAB_BAR_MARGIN_H), n * TAB_BAR_ITEM_WIDTH + 2 * TAB_BAR_ITEM_INSET);
    const bar: Rect = {
      x: x + (size.width - width) / 2,
      y: y + size.height - barHeight,
      width,
      height: capsuleHeight,
    };
    const chrome: Record<string, Rect> = {
      bar,
      content: { x, y, width: size.width, height: size.height - barHeight },
    };
    const inner = Math.max(0, bar.width - 2 * TAB_BAR_ITEM_INSET);
    for (let i = 0; i < n; i++) {
      const itemWidth = inner / n;
      chrome[`item${i}`] = {
        x: bar.x + TAB_BAR_ITEM_INSET + i * itemWidth,
        y: bar.y + TAB_BAR_ITEM_INSET,
        width: itemWidth,
        height: Math.max(0, capsuleHeight - 2 * TAB_BAR_ITEM_INSET),
      };
    }
    this.setChrome(node, chrome);
    const selected = readSelected(node, n);
    const tabCtx: Context = { ...cctx, bottomBar: barHeight, bottomEdge: undefined, barAbove: undefined };
    tabs.forEach((tab, i) => {
      this.setFrame(tab, x, y, size);
      if (i !== selected) {
        this.hide(tab, node);
        return;
      }
      this.placeTabContent(tab, this.childContext(tab, tabCtx), x, y, size, barHeight);
    });
  }

  /**
   * The Mac idiom's tab view (MAC_TAB_* above): the bar is a glass capsule
   * of equal segments centered in a 52 row at the top: the window's toolbar
   * when the tab view is at the top of the screen, else a row at the top of
   * the content. The content is the rest, and the selected tab fills it (its
   * own `navstack` reserves no status bar there: the row is above it).
   * Chrome `top` marks the bar as the top one for the page.
   */
  private placeMacTabView(node: LayoutNode, cctx: Context, x: number, y: number, size: Size): void {
    const { safeArea } = this.env;
    const atTop = y <= 0 && safeArea.top > 0;
    const tabs = node.children;
    const n = tabs.length;
    // Each segment is the widest title (13 regular) and 11 either side.
    const font = makeFont('system', MAC_SEGMENTED_FONT_SIZE, 400);
    const widest = tabs.reduce((w, tab) => Math.max(w, this.measurer.measureWidth(str(tab.props['title']) ?? '', font)), 0);
    const itemWidth = widest + 2 * MAC_TAB_ITEM_PAD_H;
    const natural = n * itemWidth + 2 * MAC_TAB_ITEM_INSET;
    const width = Math.min(Math.max(0, size.width - 2 * MAC_TAB_BAR_MARGIN_H), natural);
    const segment = n > 0 ? (width - 2 * MAC_TAB_ITEM_INSET) / n : 0;
    const rowHeight = Math.min(MAC_TAB_ROW_HEIGHT, Math.max(0, size.height));
    const barHeight = Math.min(MAC_TAB_BAR_HEIGHT, rowHeight);
    const bar: Rect = snapRect({ x: x + (size.width - width) / 2, y: y + (rowHeight - barHeight) / 2, width, height: barHeight });
    const toolbar: Rect = { x, y, width: size.width, height: rowHeight };
    const content: Rect = { x, y: y + rowHeight, width: size.width, height: Math.max(0, size.height - rowHeight) };
    const chrome: Record<string, Rect> = { bar, content, top: bar };
    const itemHeight = Math.min(MAC_TAB_ITEM_HEIGHT, barHeight);
    for (let i = 0; i < n; i++) {
      chrome[`item${i}`] = snapRect({ x: bar.x + MAC_TAB_ITEM_INSET + i * segment, y: bar.y + (barHeight - itemHeight) / 2, width: segment, height: itemHeight });
    }
    this.setChrome(node, chrome);
    const selected = readSelected(node, n);
    const tabCtx: Context = { ...cctx, bottomEdge: undefined, barAbove: undefined };
    const contentSize: Size = { width: content.width, height: content.height };
    tabs.forEach((tab, i) => {
      if (i !== selected) {
        this.setFrame(tab, content.x, content.y, contentSize);
        this.hide(tab, node);
        return;
      }
      const child = this.contentOf(tab);
      // A navigation stack at the window's top fills the tab: its root
      // screen draws the toolbar row as its bar (`tabToolbar`), with the
      // picker over it; anything else starts under the row.
      if (atTop && child && unwrapStyled(child).kind === 'navstack') {
        this.setFrame(tab, x, y, size);
        this.placeTabContent(tab, this.childContext(tab, { ...tabCtx, tabToolbar: toolbar }), x, y, size, 0);
        return;
      }
      this.setFrame(tab, content.x, content.y, contentSize);
      this.placeTabContent(tab, this.childContext(tab, tabCtx), content.x, content.y, contentSize, 0);
    });
  }

  /**
   * The selected tab's content, like a navscreen's: a `navstack` fills the
   * whole tab (it reserves the status bar itself and its lists run under the
   * bar); a `list` or `scrollview` starts below the status bar and also runs
   * to the bottom, inset by the bar (chrome `bottomInset`); anything else is
   * centered between the status bar and the tab bar.
   */
  private placeTabContent(tab: LayoutNode, cctx: Context, x: number, y: number, size: Size, barHeight: number): void {
    const { safeArea, screen } = this.env;
    const topInset = y <= 0 ? safeArea.top : 0;
    // The keyboard (device mode) covers the tab bar: the content stops above
    // whichever is taller (a `navstack` handles the keyboard in its screens).
    const keyboard = y + size.height >= screen.height - EPSILON ? this.keyboard : 0;
    // Clear of the horizontal safe area (a phone on its side); a `navstack`
    // insets its own screens' items and content.
    const leading = x <= EPSILON ? safeArea.leading : 0;
    const trailing = x + size.width >= screen.width - EPSILON ? safeArea.trailing : 0;
    const area: Rect = {
      x: x + leading,
      y: y + topInset,
      width: Math.max(0, size.width - leading - trailing),
      height: Math.max(0, size.height - topInset - Math.max(barHeight, keyboard)),
    };
    this.setChrome(tab, { content: area });
    const child = this.contentOf(tab);
    if (!child) return;
    const kind = unwrapStyled(child).kind;
    if (kind === 'navstack') {
      this.place(child, cctx, { width: size.width, height: size.height }, x, y);
      return;
    }
    if (kind === 'list' || kind === 'scrollview') {
      const height = Math.max(0, size.height - topInset - keyboard);
      const ctx: Context = keyboard > 0 ? { ...cctx, bottomBar: Math.max(0, barHeight - keyboard), bottomEdge: y + size.height - keyboard } : cctx;
      this.place(child, ctx, { width: area.width, height }, area.x, y + topInset);
      return;
    }
    const proposal: Proposal = { width: area.width, height: area.height };
    const childSize = this.measure(child, cctx, proposal);
    const { dx, dy } = alignIn(area, childSize, 'center');
    this.place(child, cctx, proposal, area.x + dx, area.y + dy);
  }

  /**
   * Phase 7 `NavigationSplitView` (regular size class): the children are
   * `[sidebar, detail]`, each a `navstack` that fills its column. The sidebar
   * column is 320 wide (at most 40 % of the width left by the horizontal
   * safe-area insets), then a 1px separator, then the detail column. Both
   * columns run the full height, so each navstack's screens reserve the
   * status bar and the bottom safe area themselves exactly as a root
   * navstack does (`placeNavScreen` keys on `y <= 0` and the screen bottom).
   * The leading inset belongs to the sidebar column and the trailing inset
   * to the detail column (when the split view touches those screen edges):
   * the column rects (`chrome.sidebar` / `chrome.detail`) include them, the
   * navstacks start / end at them.
   *
   * iPadOS: a sidebar toggle (a 44 glass circle, chrome `toggle`) sits at the
   * leading edge of the bar row, in the sidebar's bar or, with the sidebar
   * hidden (`sidebarVisible: false`), in the detail's; that column's screens
   * start their back button and leading items after it. A hidden sidebar
   * slides off the leading edge and the detail column takes the width.
   */
  private placeNavSplit(node: LayoutNode, cctx: Context, x: number, y: number, size: Size): void {
    const { screen, safeArea } = this.env;
    const leading = x <= 0 ? safeArea.leading : 0;
    const trailing = x + size.width >= screen.width - EPSILON ? safeArea.trailing : 0;
    const usable = Math.max(0, size.width - leading - trailing);
    const mac = this.env.platform === 'macos';
    const [sidebar, detail, ...rest] = node.children;
    // The sidebar's width: the app's `.navigationSplitViewColumnWidth` (a
    // `splitColumnWidth` hint on the sidebar's content, its ideal width
    // between its min and max), else the platform's default; on a Mac the
    // user drags the column's edge (`env.sidebarWidths`) between the same
    // bounds. Never more than 40% of the width.
    const requested = sidebar ? splitColumnWidthOf(sidebar) : null;
    const fallback = mac ? MAC_NAVSPLIT_SIDEBAR_WIDTH : NAVSPLIT_SIDEBAR_WIDTH;
    const minWidth = requested?.min ?? Math.min(fallback, requested?.ideal ?? fallback);
    const maxWidth = Math.min(requested?.max ?? Infinity, usable * NAVSPLIT_SIDEBAR_MAX_FRACTION);
    const dragged = mac ? this.env.sidebarWidths?.[node.id] : undefined;
    const sidebarWidth = Math.max(0, Math.min(maxWidth, Math.max(minWidth, dragged ?? requested?.ideal ?? fallback)));
    const visible = node.props['sidebarVisible'] !== false;
    // A Mac's sidebar is a panel inset from the window's edges, with no separator.
    const panelInset = mac ? MAC_NAVSPLIT_SIDEBAR_INSET : 0;
    const separatorWidth = mac ? 0 : NAVSPLIT_SEPARATOR_WIDTH;
    // Hidden, the sidebar and its separator sit just off the leading edge.
    const sidebarX = visible ? x + leading + panelInset : x - sidebarWidth - separatorWidth;
    const separatorX = sidebarX + sidebarWidth;
    const detailX = visible ? separatorX + separatorWidth : x + leading;
    const detailWidth = Math.max(0, x + size.width - trailing - detailX);
    const statusBar = y <= 0 ? safeArea.top : 0;
    // On a Mac the toggle is a bare 36 slot centered in the toolbar's row: at
    // the sidebar panel's trailing end, or after the window buttons while
    // the sidebar is hidden.
    const toggleSize = mac ? MAC_BAR_ITEM_SIZE : NAV_GLASS_SIZE;
    const toggleX = mac && visible
      ? sidebarX + sidebarWidth - MAC_NAVSPLIT_TOGGLE_INSET - toggleSize
      : x + leading + (mac && y <= 0 && x <= EPSILON && safeArea.top > 0 ? MAC_WINDOW_BUTTONS_ROOM : NAV_ITEM_MARGIN);
    const toggleY = mac ? y + Math.max(statusBar, MAC_TITLE_BAR_UNIFIED) - (MAC_TITLE_BAR_UNIFIED + toggleSize) / 2 : y + statusBar;
    const chrome: Record<string, Rect> = {
      sidebar: { x: visible ? x : sidebarX, y, width: visible ? leading + sidebarWidth : sidebarWidth, height: size.height },
      separator: { x: separatorX, y, width: separatorWidth, height: size.height },
      detail: { x: visible ? detailX : x, y, width: Math.max(0, x + size.width - (visible ? detailX : x)), height: size.height },
      toggle: { x: toggleX, y: toggleY, width: toggleSize, height: toggleSize },
    };
    // The Mac's sidebar panel (the page draws it), inset from the window's edges.
    if (mac) chrome['panel'] = { x: sidebarX, y: y + panelInset, width: sidebarWidth, height: Math.max(0, size.height - 2 * panelInset) };
    // The Mac's column divider (the page's drag handle for the sidebar's
    // width), a strip astride the panel's trailing edge while it shows.
    if (mac && visible) chrome['divider'] = { x: separatorX - MAC_NAVSPLIT_DIVIDER_GRIP / 2, y, width: MAC_NAVSPLIT_DIVIDER_GRIP, height: size.height };
    this.setChrome(node, chrome);
    const toggleRoom = toggleSize + (mac ? MAC_BAR_GROUP_GAP : NAV_GROUP_SPACING);
    if (sidebar) {
      const proposal: Proposal = { width: sidebarWidth, height: size.height };
      // On a Mac the toggle is at the sidebar's trailing end: its screens' items start where they would,
      // and their trailing items end a group's gap before the toggle.
      const barTrailing = mac && visible ? MAC_NAVSPLIT_TOGGLE_INSET + toggleSize + MAC_SIDEBAR_TOGGLE_GAP - MAC_BAR_MARGIN_TRAILING : 0;
      this.sidebarOverflowed = false;
      this.place(sidebar, { ...cctx, splitSidebar: true, splitColumn: true, barLeading: visible && !mac ? toggleRoom : 0, barTrailing }, proposal, sidebarX, y);
    }
    // The sidebar's items it could not fit go to the detail's overflow menu.
    const barOverflow = mac && visible && sidebar !== undefined && this.sidebarOverflowed;
    this.sidebarOverflowed = false;
    if (detail) {
      const proposal: Proposal = { width: detailWidth, height: size.height };
      this.place(detail, { ...cctx, splitSidebar: false, splitColumn: true, barLeading: visible ? 0 : toggleRoom, barTrailing: 0, barOverflow }, proposal, detailX, y);
    }
    for (const extra of rest) this.hide(extra, node);
  }

  /**
   * `.inspector` (regular width): the children are the content, then the
   * `inspectorpanel`. The panel is a trailing column, 320 wide (its
   * content's `inspectorColumnWidth`, at most half the width) plus the
   * trailing safe-area inset when it touches that edge, with a 1px gap
   * before it (the panel draws the hairline there); the content is proposed the rest (each content child is
   * centered in it, as a root view is). With `presented: false` the panel
   * sits just off the trailing edge (so it slides) and the content takes the
   * whole width.
   */
  private placeInspector(node: LayoutNode, cctx: Context, x: number, y: number, size: Size): void {
    const { screen, safeArea } = this.env;
    const last = node.children[node.children.length - 1];
    const panel = last && last.kind === 'inspectorpanel' ? last : null;
    const main = panel ? node.children.slice(0, -1) : node.children;
    const presented = node.props['presented'] === true;
    const trailing = x + size.width >= screen.width - EPSILON ? safeArea.trailing : 0;
    const requested = panel?.children[0] ? num(cellStyle(panel.children[0], 'inspectorColumnWidth')) : null;
    const columnWidth = Math.min(requested ?? INSPECTOR_WIDTH, size.width * INSPECTOR_MAX_FRACTION);
    const panelWidth = columnWidth + trailing;
    const panelX = presented ? x + size.width - panelWidth : x + size.width + NAVSPLIT_SEPARATOR_WIDTH;
    const mainWidth = presented ? Math.max(0, panelX - NAVSPLIT_SEPARATOR_WIDTH - x) : size.width;
    const area: Size = { width: mainWidth, height: size.height };
    for (const child of main) {
      const childSize = this.measure(child, cctx, area);
      const { dx, dy } = alignIn(area, childSize, 'center');
      this.place(child, cctx, area, x + dx, y + dy);
    }
    if (panel) this.place(panel, cctx, { width: panelWidth, height: size.height }, panelX, y);
  }

  /**
   * The inspector column: its content below the status bar (at the top of
   * the screen) and clear of the trailing inset, top-aligned and centered
   * horizontally (a list or form fills it). A navigation stack fills the
   * column from its top instead, as the split view's columns do, so its bar
   * takes the status bar's room and is as tall as theirs (with the 10 under
   * an inline title) and its content starts level with theirs.
   */
  private placeInspectorPanel(node: LayoutNode, cctx: Context, x: number, y: number, size: Size): void {
    const { screen, safeArea } = this.env;
    const top = y <= 0 ? safeArea.top : 0;
    const trailing = x + size.width >= screen.width - EPSILON ? safeArea.trailing : 0;
    const area: Size = { width: Math.max(0, size.width - trailing), height: Math.max(0, size.height - top) };
    for (const child of node.children) {
      if (unwrapStyled(child).kind === 'navstack') {
        this.place(child, cctx, { width: area.width, height: size.height }, x, y);
        continue;
      }
      const childSize = this.measure(child, cctx, area);
      const { dx } = alignIn(area, childSize, 'center');
      this.place(child, cctx, area, x + dx, y + top);
    }
  }

  /**
   * Phase 7 `ViewThatFits`: the first child whose ideal size fits the
   * proposal on the restricted axes (a nil or infinite proposal axis always
   * fits), else the last child; the element is that child's size for the
   * proposal. Memoized per proposal like every other plan, so the choice is
   * stable within a pass.
   */
  private viewThatFitsPlan(node: LayoutNode, ctx: Context, p: Proposal): { chosen: LayoutNode | null; size: Size } {
    return this.plan(node, ctx, p, () => {
      const axes = str(node.props['axes']) ?? 'both';
      const checkWidth = axes !== 'vertical';
      const checkHeight = axes !== 'horizontal';
      const cctx = this.childContext(node, ctx);
      const fitsAxis = (proposed: number | null, ideal: number): boolean =>
        !finite(proposed) || ideal <= proposed + EPSILON;
      let chosen: LayoutNode | null = null;
      for (const child of node.children) {
        const ideal = this.measure(child, cctx, NIL_PROPOSAL);
        if ((!checkWidth || fitsAxis(p.width, ideal.width)) && (!checkHeight || fitsAxis(p.height, ideal.height))) {
          chosen = child;
          break;
        }
      }
      if (!chosen && node.children.length > 0) chosen = node.children[node.children.length - 1] as LayoutNode;
      return { chosen, size: chosen ? this.measure(chosen, cctx, p) : ZERO };
    });
  }

  // -------------------------------------------------------------------------
  // Phase 8: controls, alerts, safe-area bleed
  // -------------------------------------------------------------------------

  /**
   * `.ignoresSafeArea(edges:)`: how far a `styled` box at (x, y, size) grows
   * on each listed side to reach the screen edge. A side grows only when the
   * box touches (or is inside) that safe-area inset band; null when nothing grows.
   */
  private safeAreaExtension(node: LayoutNode, ctx: Context, x: number, y: number, size: Size): EdgeInsets | null {
    const style = styleOf(node);
    const raw = style['ignoresSafeArea'];
    const edges: string[] = raw === 'all' ? [...SAFE_AREA_EDGES] : Array.isArray(raw) ? raw.map(str).filter((e): e is string => e !== null) : [];
    // `.ignoresSafeArea(.keyboard)`: the box runs down under the keyboard
    // the content above it stopped at (device mode, while it is up).
    const keyboard = style['ignoresKeyboard'] === true ? this.keyboard : 0;
    if (edges.length === 0 && keyboard === 0) return null;
    const { screen, safeArea } = this.env;
    // Inside a `navscreen` the bar is what the content clears (`ctx.safeTop`);
    // under a tab bar, the bar (`ctx.bottomBar`, which includes the bottom
    // safe area).
    const top = ctx.safeTop;
    const bottom = Math.max(edges.includes('bottom') ? Math.max(safeArea.bottom, ctx.bottomBar) : 0, keyboard);
    const ext: EdgeInsets = { top: 0, leading: 0, bottom: 0, trailing: 0 };
    if (edges.includes('top') && top > 0 && y <= top + EPSILON) ext.top = Math.max(0, y);
    if (edges.includes('leading') && safeArea.leading > 0 && x <= safeArea.leading + EPSILON) ext.leading = Math.max(0, x);
    if (bottom > 0 && y + size.height >= screen.height - bottom - EPSILON) {
      ext.bottom = Math.max(0, screen.height - (y + size.height));
    }
    if (edges.includes('trailing') && safeArea.trailing > 0 && x + size.width >= screen.width - safeArea.trailing - EPSILON) {
      ext.trailing = Math.max(0, screen.width - (x + size.width));
    }
    return ext.top || ext.leading || ext.bottom || ext.trailing ? ext : null;
  }

  private stepperLabelProposal(p: Proposal): Proposal {
    const control = this.stepperMetrics();
    return { width: shrink(p.width, control.width + control.gap), height: p.height };
  }

  /** Like a toggle: the label leads, the 94 × 32 control trails; at least 32 high. */
  /** The Mac idiom: a menu outside a bar and a list row is a pull-down button. */
  private menuIsPullDown(ctx: Context): boolean {
    return this.env.platform === 'macos' && !ctx.listRow && !ctx.navBarItem && !ctx.bottomBarItem;
  }

  /** A menu in a Mac title bar shows its indicator, a chevron after the label (SwiftUI's default `.menuIndicator`). */
  private menuHasIndicator(ctx: Context): boolean {
    return this.env.platform === 'macos' && ctx.navBarItem === true;
  }

  /** The stepper's control: iOS's segmented −/+, or the Mac's 13 × 22 arrows. */
  private stepperMetrics(): { width: number; height: number; gap: number; mac: boolean } {
    if (this.env.platform === 'macos') return { width: MAC_STEPPER_WIDTH, height: MAC_STEPPER_HEIGHT, gap: MAC_STEPPER_GAP, mac: true };
    return { width: STEPPER_WIDTH, height: STEPPER_HEIGHT, gap: STEPPER_GAP, mac: false };
  }

  private measureStepper(node: LayoutNode, ctx: Context, p: Proposal): Size {
    const label = this.contentOf(node);
    const control = this.stepperMetrics();
    // A Mac form's row: the label is in the label column; the control alone.
    if (ctx.formColumn) return { width: control.width, height: control.height };
    const labelSize = label ? this.measure(label, this.childContext(node, ctx), this.stepperLabelProposal(p)) : ZERO;
    return {
      width: finite(p.width) ? p.width : labelSize.width + control.gap + control.width,
      height: Math.max(labelSize.height, control.height),
    };
  }

  private placeStepper(node: LayoutNode, ctx: Context, p: Proposal, x: number, y: number, size: Size): void {
    const cctx = this.childContext(node, ctx);
    const label = this.contentOf(node);
    const labelProposal = this.stepperLabelProposal(p);
    const labelSize = label && !ctx.formColumn ? this.measure(label, cctx, labelProposal) : ZERO;
    const labelY = y + (size.height - labelSize.height) / 2;
    // A Mac form's row: the label is in the label column (the form places it), the control at the leading edge.
    if (label && !ctx.formColumn) this.place(label, cctx, labelProposal, x, labelY);
    const control = this.stepperMetrics();
    const controlX = ctx.formColumn ? x : x + size.width - control.width;
    const controlY = y + (size.height - control.height) / 2;
    const existing = node.id >= 0 ? this.chrome.get(node.id) : undefined;
    // iOS: − and + side by side; a Mac: up (increment) over down.
    const half = control.mac ? control.height / 2 : control.width / 2;
    this.setChrome(node, {
      ...existing,
      label: {
        x,
        y: labelY,
        width: Math.min(labelSize.width, Math.max(0, size.width - control.width - control.gap)),
        height: labelSize.height,
      },
      control: { x: controlX, y: controlY, width: control.width, height: control.height },
      decrement: control.mac
        ? { x: controlX, y: controlY + half, width: control.width, height: half }
        : { x: controlX, y: controlY, width: half, height: control.height },
      increment: control.mac
        ? { x: controlX, y: controlY, width: control.width, height: half }
        : { x: controlX + half, y: controlY, width: half, height: control.height },
      ...(control.mac ? { arrows: { x: controlX, y: controlY, width: control.width, height: control.height } } : {}),
    });
  }

  /**
   * A slider is the proposed width (200 when none) and 44 high. The `min` /
   * `max` value labels sit at the track's ends, 8 from the track. Its
   * `label` child is not drawn, as iOS does not draw a slider's label (it
   * names the slider for VoiceOver): the page hides it and names the slider
   * from it (`aria-labelledby`). Children are named by `labels` in order.
   */
  /** The slider's row, track and knob: iOS 26's pill knob, or the Mac's round one. */
  private sliderMetrics(): { height: number; track: number; thumb: number; thumbHeight: number; mac: boolean } {
    if (this.env.platform === 'macos') return { height: MAC_SLIDER_HEIGHT, track: MAC_SLIDER_TRACK_HEIGHT, thumb: MAC_SLIDER_THUMB, thumbHeight: MAC_SLIDER_THUMB, mac: true };
    return { height: SLIDER_HEIGHT, track: SLIDER_TRACK_HEIGHT, thumb: SLIDER_THUMB, thumbHeight: SLIDER_THUMB_HEIGHT, mac: false };
  }

  private sliderPlan(node: LayoutNode, ctx: Context, p: Proposal): SliderPlan {
    return this.plan(node, ctx, p, () => {
      const width = finite(p.width) ? p.width : SLIDER_DEFAULT_WIDTH;
      const cctx = this.childContext(node, ctx);
      const names = Array.isArray(node.props['labels']) ? node.props['labels'].map(str) : [];
      let label: LayoutNode | null = null;
      let min: LayoutNode | null = null;
      let max: LayoutNode | null = null;
      names.forEach((name, i) => {
        const child = node.children[i] ?? null;
        if (name === 'label') label = child;
        else if (name === 'min') min = child;
        else if (name === 'max') max = child;
      });
      const minSize = min ? this.measure(min, cctx, NIL_PROPOSAL) : ZERO;
      const maxSize = max ? this.measure(max, cctx, NIL_PROPOSAL) : ZERO;
      return { size: { width, height: this.sliderMetrics().height }, label, min, max, minSize, maxSize, cctx };
    });
  }

  private placeSlider(node: LayoutNode, ctx: Context, p: Proposal, x: number, y: number, size: Size): void {
    const plan = this.sliderPlan(node, ctx, p);
    const { cctx } = plan;
    const m = this.sliderMetrics();
    const rowY = y;
    let trackX = x;
    let trackEnd = x + size.width;
    // In a Mac grouped form the label leads the row and the slider takes its
    // trailing 55%; elsewhere the label is not drawn (a Mac columns form
    // places it in its label column).
    if (plan.label && ctx.groupedRow) {
      const labelSize = this.measure(plan.label, cctx, NIL_PROPOSAL);
      this.place(plan.label, cctx, NIL_PROPOSAL, x, rowY + (m.height - labelSize.height) / 2);
      trackX = x + size.width * (1 - MAC_GROUPED_SLIDER_FRACTION);
    } else if (plan.label && !ctx.formColumn) {
      this.hide(plan.label, node);
    }
    if (plan.min) {
      this.place(plan.min, cctx, NIL_PROPOSAL, x, rowY + (m.height - plan.minSize.height) / 2);
      trackX += plan.minSize.width + SLIDER_LABEL_GAP;
    }
    if (plan.max) {
      this.place(plan.max, cctx, NIL_PROPOSAL, x + size.width - plan.maxSize.width, rowY + (m.height - plan.maxSize.height) / 2);
      trackEnd -= plan.maxSize.width + SLIDER_LABEL_GAP;
    }
    const trackWidth = Math.max(0, trackEnd - trackX);
    const trackY = rowY + (m.height - m.track) / 2;
    const fraction = sliderFraction(num(node.props['value']) ?? 0, num(node.props['min']) ?? 0, num(node.props['max']) ?? 1);
    // The thumb travels inside the track: its center runs from half a thumb
    // past the track's start to half a thumb before its end.
    const travel = Math.max(0, trackWidth - m.thumb);
    const thumbCenter = trackX + m.thumb / 2 + fraction * travel;
    const existing = node.id >= 0 ? this.chrome.get(node.id) : undefined;
    this.setChrome(node, {
      ...existing,
      track: { x: trackX, y: trackY, width: trackWidth, height: m.track },
      fill: { x: trackX, y: trackY, width: Math.max(0, thumbCenter - trackX), height: m.track },
      thumb: {
        x: thumbCenter - m.thumb / 2,
        y: rowY + (m.height - m.thumbHeight) / 2,
        width: m.thumb,
        height: m.thumbHeight,
      },
      // The page draws the Mac's round knob (styles.css).
      ...(m.mac ? { knob: { x: thumbCenter - m.thumb / 2, y: rowY + (m.height - m.thumbHeight) / 2, width: m.thumb, height: m.thumbHeight } } : {}),
    });
  }

  /**
   * Compact date picker: the label leads; the date and / or time capsule
   * (text in the body font plus 11 of padding each side, 34 high, 8 apart)
   * trail. The width is the proposal, or label + 8 + capsules.
   */
  private datePickerPlan(node: LayoutNode, ctx: Context, p: Proposal): DatePickerPlan {
    return this.plan(node, ctx, p, () => {
      const cctx = this.childContext(node, ctx);
      const seconds = num(node.props['seconds']) ?? 0;
      const components = str(node.props['components']) ?? 'dateAndTime';
      const timeZone = str(node.props['timeZone']) ?? undefined;
      const dateText = components === 'hourAndMinute' ? null : formatPickerDate(seconds, timeZone);
      const timeText = components === 'date' ? null : formatPickerTime(seconds, timeZone);
      const mac = this.env.platform === 'macos';
      const capsulePad = mac ? MAC_DATEPICKER_CAPSULE_PAD_H : DATEPICKER_CAPSULE_PAD_H;
      const capsuleGap = mac ? MAC_DATEPICKER_CAPSULE_GAP : DATEPICKER_CAPSULE_GAP;
      const capsule = (text: string | null): number =>
        text === null ? 0 : this.measurer.measureWidth(text, ctx.font) + 2 * capsulePad;
      const dateWidth = capsule(dateText);
      const timeWidth = capsule(timeText);
      const capsulesWidth = dateWidth + timeWidth + (dateText !== null && timeText !== null ? capsuleGap : 0);
      // A Mac form's row: the label is in the label column; the fields alone.
      const label = node.props['labelsHidden'] === true || ctx.formColumn ? null : this.contentOf(node);
      const labelProposal: Proposal = { width: shrink(p.width, capsulesWidth + DATEPICKER_GAP), height: p.height };
      const labelSize = label ? this.measure(label, cctx, labelProposal) : ZERO;
      const width = ctx.formColumn ? capsulesWidth : finite(p.width) ? p.width : labelSize.width + (label ? DATEPICKER_GAP : 0) + capsulesWidth;
      return {
        size: { width, height: Math.max(labelSize.height, mac ? MAC_DATEPICKER_HEIGHT : DATEPICKER_HEIGHT) },
        label,
        labelProposal,
        labelSize,
        dateWidth,
        timeWidth,
        capsulesWidth,
        cctx,
      };
    });
  }

  private placeDatePicker(node: LayoutNode, ctx: Context, p: Proposal, x: number, y: number, size: Size): void {
    const plan = this.datePickerPlan(node, ctx, p);
    if (!plan.label && (!ctx.formColumn || node.props['labelsHidden'] === true)) {
      // `labelsHidden`: the label child exists but is not shown (a Mac form places it in its label column).
      for (const child of node.children) this.hide(child, node);
    }
    const labelY = y + (size.height - plan.labelSize.height) / 2;
    if (plan.label) this.place(plan.label, plan.cctx, plan.labelProposal, x, labelY);
    const mac = this.env.platform === 'macos';
    const capsuleHeight = mac ? MAC_DATEPICKER_HEIGHT : DATEPICKER_HEIGHT;
    const capsuleY = y + (size.height - capsuleHeight) / 2;
    const existing = node.id >= 0 ? this.chrome.get(node.id) : undefined;
    const chrome: Record<string, Rect> = {
      ...existing,
      label: {
        x,
        y: labelY,
        width: Math.min(plan.labelSize.width, Math.max(0, size.width - plan.capsulesWidth - DATEPICKER_GAP)),
        height: plan.labelSize.height,
      },
    };
    let capsuleX = x + size.width - plan.capsulesWidth;
    if (plan.dateWidth > 0) {
      chrome['date'] = { x: capsuleX, y: capsuleY, width: plan.dateWidth, height: capsuleHeight };
      capsuleX += plan.dateWidth + (mac ? MAC_DATEPICKER_CAPSULE_GAP : DATEPICKER_CAPSULE_GAP);
    }
    if (plan.timeWidth > 0) {
      chrome['time'] = { x: capsuleX, y: capsuleY, width: plan.timeWidth, height: capsuleHeight };
    }
    // The page draws the Mac's bordered fields (styles.css).
    if (mac) chrome['fields'] = { x: x + size.width - plan.capsulesWidth, y: capsuleY, width: plan.capsulesWidth, height: capsuleHeight };
    this.setChrome(node, chrome);
  }

  /**
   * `alert` style (iOS 26): a card centered in the safe area with the title
   * (headline) and the message (subheadline), leading-aligned, and capsule
   * buttons: two side by side (a `cancel` one first), otherwise stacked; no
   * buttons → an `ok` rect the renderer draws.
   * `dialog` style: an action sheet of 57 rows in a card 8 from the screen
   * edges, above a separate card for the `cancel` button, 8 above the bottom
   * safe area; the title and message are a footnote caption on top. Button
   * children get the row as their frame with the label centered in it.
   */
  private placeAlert(node: LayoutNode, ctx: Context, x: number, y: number, size: Size): void {
    const style = str(node.props['style']) === 'dialog' ? 'dialog' : 'alert';
    const title = str(node.props['title']) ?? '';
    const message = str(node.props['message']) ?? '';
    const cctx = this.childContext(node, ctx);
    const { safeArea } = this.env;
    const chrome: Record<string, Rect> = {};
    const texts: Record<string, TextLayout> = {};
    const roleOf = (child: LayoutNode): string | null => str(unwrapStyled(child).props['role']);
    // The alert's metrics: iOS's card, or the Mac's panel (`data-sb-panel` for the page).
    const m = this.env.platform === 'macos' ? MAC_ALERT_METRICS : ALERT_METRICS;
    // `textfield` children (`.alert { TextField(...); Button(...) }`) go in
    // the card between the message and the buttons, in their order.
    const fields = node.children.filter((child) => unwrapStyled(child).kind === 'textfield');
    const buttons = node.children.filter((child) => !fields.includes(child));
    const placeFields = (top: number, left: number, width: number): void => {
      fields.forEach((field, i) => {
        const rect: Rect = { x: left, y: top + i * (m.fieldHeight + m.fieldSpacing), width, height: m.fieldHeight };
        chrome[`field${i}`] = rect;
        const proposal: Proposal = { width: rect.width, height: rect.height };
        const fieldSize = this.measure(field, cctx, proposal);
        this.place(field, cctx, proposal, rect.x, rect.y + (rect.height - fieldSize.height) / 2);
        this.setFrame(field, rect.x, rect.y, { width: rect.width, height: rect.height });
      });
    };
    const fieldsHeight = fields.length > 0 ? fields.length * m.fieldHeight + (fields.length - 1) * m.fieldSpacing : 0;

    const block = (text: string, font: ResolvedFont, width: number, alignment: 'center' | 'leading'): { layout: TextLayout; height: number } => {
      const wrapped = wrapText(text, font, width, null, this.measurer);
      return {
        layout: { font, lines: wrapped.lines, alignment, truncated: wrapped.truncated ?? false },
        height: textHeight(wrapped.lines.length, font),
      };
    };
    const centered = (text: string, font: ResolvedFont, width: number) => block(text, font, width, 'center');

    if (style === 'alert') {
      const cardWidth = Math.min(m.width, size.width - 2 * DIALOG_MARGIN);
      const textWidth = cardWidth - 2 * m.padH;
      const titleFont = resolveFont({ textStyle: 'headline' }, ctx.font, this.env);
      // The Mac's message is the body text (13), as large as the title.
      const messageFont = resolveFont({ textStyle: m === MAC_ALERT_METRICS ? 'body' : 'subheadline' }, ctx.font, this.env);
      // Heights first (the card is centered), then the rects.
      let h = m.padTop;
      let titleBlock: { layout: TextLayout; height: number; top: number } | null = null;
      let messageBlock: { layout: TextLayout; height: number; top: number } | null = null;
      if (title.length > 0) {
        const b = block(title, titleFont, textWidth, m.textAlignment);
        titleBlock = { ...b, top: h };
        h += b.height;
      }
      if (message.length > 0) {
        if (titleBlock) h += m.titleGap;
        const b = block(message, messageFont, textWidth, m.textAlignment);
        messageBlock = { ...b, top: h };
        h += b.height;
      }
      let fieldsTop: number | null = null;
      if (fields.length > 0) {
        if (titleBlock || messageBlock) h += m.fieldsGap;
        fieldsTop = h;
        h += fieldsHeight;
      }
      h += m.buttonsGap;
      const headerHeight = h;
      const rows = buttons.length === 0 ? 1 : buttons.length === 2 ? 1 : buttons.length;
      h += rows * m.buttonHeight + (rows - 1) * m.buttonSpacing + m.padBottom;
      // Centered in the safe area, as iOS does; a Mac's panel in the window.
      const { safeArea: inset } = this.env;
      const top = y + (y <= 0 && m !== MAC_ALERT_METRICS ? inset.top : 0);
      const bottom = y + size.height - (y + size.height >= this.env.screen.height - EPSILON ? inset.bottom : 0);
      const card: Rect = { x: x + (size.width - cardWidth) / 2, y: top + (bottom - top - h) / 2, width: cardWidth, height: h };
      chrome['card'] = card;
      if (m === MAC_ALERT_METRICS) chrome['panel'] = card;
      if (titleBlock) {
        chrome['title'] = { x: card.x + m.padH, y: card.y + titleBlock.top, width: textWidth, height: titleBlock.height };
        texts['title'] = titleBlock.layout;
      }
      if (messageBlock) {
        chrome['message'] = { x: card.x + m.padH, y: card.y + messageBlock.top, width: textWidth, height: messageBlock.height };
        texts['message'] = messageBlock.layout;
      }
      const buttonsTop = card.y + headerHeight;
      const buttonsX = card.x + m.buttonInset;
      const buttonsWidth = Math.max(0, cardWidth - 2 * m.buttonInset);
      if (fieldsTop !== null) placeFields(card.y + fieldsTop, buttonsX, buttonsWidth);
      if (buttons.length === 0) {
        chrome['ok'] = { x: buttonsX, y: buttonsTop, width: buttonsWidth, height: m.buttonHeight };
      } else if (buttons.length === 2) {
        // A `cancel` button goes first (leading), as iOS orders them.
        const ordered = roleOf(buttons[1] as LayoutNode) === 'cancel' ? [buttons[1], buttons[0]] : [buttons[0], buttons[1]];
        const half = (buttonsWidth - m.rowSpacing) / 2;
        ordered.forEach((button, slot) => {
          const i = buttons.indexOf(button as LayoutNode);
          const rect: Rect = { x: buttonsX + slot * (half + m.rowSpacing), y: buttonsTop, width: half, height: m.buttonHeight };
          chrome[`button${i}`] = rect;
          this.placeAlertButton(button as LayoutNode, cctx, rect);
        });
      } else {
        buttons.forEach((button, i) => {
          const rect: Rect = {
            x: buttonsX,
            y: buttonsTop + i * (m.buttonHeight + m.buttonSpacing),
            width: buttonsWidth,
            height: m.buttonHeight,
          };
          chrome[`button${i}`] = rect;
          this.placeAlertButton(button, cctx, rect);
        });
      }
    } else {
      const cardWidth = Math.max(0, size.width - 2 * DIALOG_MARGIN);
      const cardX = x + DIALOG_MARGIN;
      const textWidth = Math.max(0, cardWidth - 2 * DIALOG_HEADER_PAD_H);
      const titleFont = resolveFont({ textStyle: 'footnote', weight: 'semibold' }, ctx.font, this.env);
      const messageFont = resolveFont({ textStyle: 'footnote' }, ctx.font, this.env);
      const buttonCtx: Context = { ...cctx, font: resolveFont({ size: DIALOG_BUTTON_FONT_SIZE }, ctx.font, this.env) };
      let h = 0;
      let titleBlock: { layout: TextLayout; height: number; top: number } | null = null;
      let messageBlock: { layout: TextLayout; height: number; top: number } | null = null;
      if (title.length > 0 || message.length > 0) {
        h = DIALOG_HEADER_PAD_V;
        if (title.length > 0) {
          const block = centered(title, titleFont, textWidth);
          titleBlock = { ...block, top: h };
          h += block.height;
        }
        if (message.length > 0) {
          if (titleBlock) h += DIALOG_TITLE_GAP;
          const block = centered(message, messageFont, textWidth);
          messageBlock = { ...block, top: h };
          h += block.height;
        }
        h += DIALOG_HEADER_PAD_V;
      }
      let fieldsTop: number | null = null;
      if (fields.length > 0) {
        fieldsTop = h;
        h += fieldsHeight + DIALOG_HEADER_PAD_V;
      }
      const headerHeight = h;
      const cancel = buttons.find((child) => roleOf(child) === 'cancel') ?? null;
      const others = buttons.filter((child) => child !== cancel);
      const rows = others.length === 0 && !cancel ? 1 : others.length;
      h += rows * DIALOG_BUTTON_HEIGHT;
      const bottom = y + size.height - safeArea.bottom - DIALOG_MARGIN;
      let cardBottom = bottom;
      if (cancel) {
        const cancelCard: Rect = { x: cardX, y: bottom - DIALOG_BUTTON_HEIGHT, width: cardWidth, height: DIALOG_BUTTON_HEIGHT };
        chrome['cancelCard'] = cancelCard;
        chrome[`button${node.children.indexOf(cancel)}`] = cancelCard;
        this.placeAlertButton(cancel, buttonCtx, cancelCard, true);
        cardBottom = cancelCard.y - DIALOG_CARD_GAP;
      }
      const card: Rect = { x: cardX, y: cardBottom - h, width: cardWidth, height: h };
      chrome['card'] = card;
      if (m === MAC_ALERT_METRICS) chrome['panel'] = card;
      if (titleBlock) {
        chrome['title'] = { x: card.x + DIALOG_HEADER_PAD_H, y: card.y + titleBlock.top, width: textWidth, height: titleBlock.height };
        texts['title'] = titleBlock.layout;
      }
      if (messageBlock) {
        chrome['message'] = { x: card.x + DIALOG_HEADER_PAD_H, y: card.y + messageBlock.top, width: textWidth, height: messageBlock.height };
        texts['message'] = messageBlock.layout;
      }
      const buttonsTop = card.y + headerHeight;
      if (fieldsTop !== null) placeFields(card.y + fieldsTop, card.x + DIALOG_HEADER_PAD_H, textWidth);
      if (others.length === 0 && !cancel) {
        chrome['ok'] = { x: card.x, y: buttonsTop, width: cardWidth, height: DIALOG_BUTTON_HEIGHT };
      }
      others.forEach((button, i) => {
        const rect: Rect = { x: card.x, y: buttonsTop + i * DIALOG_BUTTON_HEIGHT, width: cardWidth, height: DIALOG_BUTTON_HEIGHT };
        chrome[`button${node.children.indexOf(button)}`] = rect;
        this.placeAlertButton(button, buttonCtx, rect, true);
      });
    }
    this.setChrome(node, chrome);
    if (node.id >= 0 && Object.keys(texts).length > 0) this.chromeText.set(node.id, texts);
  }

  /** An alert button fills its row; its label is centered in it (any other child is centered as is). */
  private placeAlertButton(child: LayoutNode, ctx: Context, rect: Rect, boldCancel = false): void {
    const proposal: Proposal = { width: Math.max(0, rect.width - 2 * DIALOG_LABEL_INSET), height: rect.height };
    // An action sheet's `cancel` button is bold: measure (and so draw) its
    // label semibold. iOS 26 alerts draw it regular.
    const cancel = boldCancel && str(unwrapStyled(child).props['role']) === 'cancel';
    const inherited = this.childContext(child, ctx);
    const cctx: Context = {
      ...inherited,
      textAlignment: 'center',
      font: cancel ? resolveFont({ weight: 'semibold' }, inherited.font, this.env) : inherited.font,
    };
    if (child.kind !== 'button') {
      const childSize = this.measure(child, ctx, proposal);
      const { dx, dy } = alignIn(rect, childSize, 'center');
      this.place(child, ctx, proposal, rect.x + dx, rect.y + dy);
      return;
    }
    const label = this.contentOf(child);
    const labelSize = label ? this.measure(label, cctx, proposal) : ZERO;
    this.setFrame(child, rect.x, rect.y, { width: rect.width, height: rect.height });
    if (label) {
      const { dx, dy } = alignIn(rect, labelSize, 'center');
      this.place(label, cctx, proposal, rect.x + dx, rect.y + dy);
    }
    const existing = child.id >= 0 ? this.chrome.get(child.id) : undefined;
    this.setChrome(child, { ...existing, label: { x: rect.x, y: rect.y, width: rect.width, height: rect.height } });
  }

  private placeSheet(node: LayoutNode, cctx: Context, x: number, y: number, size: Size): void {
    // A grabber gets its row; without one the content starts a little below
    // the sheet's top (iOS 26), except in a `full` one (Phase 8,
    // `.fullScreenCover`). A navigation stack fills the sheet from its top
    // instead, its bar taking that room (`barInset`), so its background
    // reaches the sheet's edge; a list or scroll view fills it below the
    // grabber row.
    const grabber = this.sheetHasGrabber(node);
    const floats = this.sheetFloats(node);
    const full = this.sheetIsFull(node);
    const grabberRow = grabber ? SHEET_GRABBER_ROW : full ? 0 : SHEET_CONTENT_TOP;
    const content: Rect = {
      x,
      y: y + grabberRow,
      width: size.width,
      height: Math.max(0, size.height - grabberRow),
    };
    const chrome: Record<string, Rect> = { content };
    if (floats) chrome['floating'] = { x, y, width: size.width, height: size.height };
    if (this.sheetIsForm(node)) chrome['form'] = { x, y, width: size.width, height: size.height };
    if (grabber) {
      chrome['grabber'] = {
        x: x + (size.width - SHEET_GRABBER.width) / 2,
        y: y + SHEET_GRABBER_TOP,
        width: SHEET_GRABBER.width,
        height: SHEET_GRABBER.height,
      };
    }
    this.setChrome(node, chrome);
    const child = this.contentOf(node);
    if (!child) return;
    // The keyboard (device mode) covers the sheet's bottom too: a list, a
    // scroll view or plain content stops above it (a navigation stack's
    // screens handle it themselves).
    const covered = Math.max(0, y + size.height - (this.env.screen.height - this.keyboard));
    const kind = unwrapStyled(child).kind;
    if (kind === 'navstack') {
      // A Mac sheet: its stack draws the sheet's title bar and puts its text
      // actions in the bottom bar (chrome `actions`, which the page draws).
      if (this.env.platform === 'macos' && this.sheetIsForm(node)) {
        const screen = this.macSheetScreen(node);
        const actions: Rect | null = screen && this.macSheetActionToolbars(screen).length > 0
          ? { x, y: y + size.height - MAC_SHEET_ACTIONS_BAR, width: size.width, height: MAC_SHEET_ACTIONS_BAR }
          : null;
        if (actions) this.setChrome(node, { ...chrome, actions });
        this.place(child, { ...cctx, barInset: 0, macSheet: { actions } }, { width: size.width, height: size.height }, x, y);
        return;
      }
      this.place(child, { ...cctx, barInset: grabberRow }, { width: size.width, height: size.height }, x, y);
      return;
    }
    if (kind === 'list' || kind === 'scrollview') {
      // A scrolling view fills the sheet below the grabber row, if any.
      const top = grabber ? SHEET_GRABBER_ROW : 0;
      this.place(child, cctx, { width: size.width, height: Math.max(0, size.height - top - covered) }, x, y + top);
      return;
    }
    const area: Rect = { ...content, height: Math.max(0, content.height - covered) };
    const proposal: Proposal = { width: area.width, height: area.height };
    const childSize = this.measure(child, cctx, proposal);
    const { dx, dy } = alignIn(area, childSize, 'center');
    this.place(child, cctx, proposal, area.x + dx, area.y + dy);
  }

  // -------------------------------------------------------------------------
  // Custom `Layout` (Phase 6)
  // -------------------------------------------------------------------------

  /**
   * Swift's answer (`size`) when it has one, for any proposal; before the first
   * answer the union of the children's ideal sizes, like a zstack measured
   * with a nil proposal, so the first frame is sane.
   */
  private measureLayout(node: LayoutNode, ctx: Context, p: Proposal): Size {
    const size = readSize(node.props['size']);
    // Swift's answer holds for the proposal it was computed for. Before the
    // first answer be flexible: fill what is proposed so the parent keeps
    // proposing real sizes (a rigid fallback would trap a nested layout that
    // was first proposed a tiny size at that size).
    if (size) {
      if (sameProposal(p, readProposal(node.props['proposal']))) return size;
      // Another proposal. Swift sends the layout's own min / ideal / max with
      // its answer, so the probes parents use to learn flexibility (0, nil,
      // Infinity) are answered from those, and any other proposal is clamped
      // to min / max (what `LayoutSubview.sizeThatFits` does on the Swift
      // side) until Swift answers it in a later pass. Guessing from the
      // answered size made an enclosing custom layout see this one as rigid
      // at a stale size, and the two then traded answers forever.
      const sizes = readLayoutSizes(node.props['sizes']);
      // At the answered width with no height (a grid or stack measuring the
      // row it places in): Swift's answer for exactly that.
      const answered = readProposal(node.props['proposal']);
      if (sizes?.atWidth && p.height === null && p.width !== null && answered?.width != null && Math.abs(p.width - answered.width) < 0.01) return sizes.atWidth;
      const axis = (proposed: number | null, answered: number, dim: 'width' | 'height'): number => {
        if (sizes) {
          if (proposed === null) return sizes.ideal[dim];
          const lower = Math.min(sizes.min[dim], sizes.max[dim]);
          return Math.max(lower, Math.min(proposed, sizes.max[dim]));
        }
        return proposed === 0 ? 0 : proposed === Infinity ? Infinity : answered;
      };
      return { width: axis(p.width, size.width, 'width'), height: axis(p.height, size.height, 'height') };
    }
    const cctx = this.childContext(node, ctx);
    let width = 0;
    let height = 0;
    for (const child of node.children) {
      const ideal = this.measure(child, cctx, NIL_PROPOSAL);
      width = Math.max(width, ideal.width);
      height = Math.max(height, ideal.height);
    }
    const fill = (proposed: number | null, ideal: number): number =>
      proposed === null ? ideal : proposed; // Infinity stays Infinity: flexible
    return { width: fill(p.width, width), height: fill(p.height, height) };
  }

  /**
   * With one frame per child (relative to the element's origin) every child
   * is proposed its frame's size and gets exactly that frame; otherwise the
   * children are centered like a zstack. Either way the record Swift needs
   * (the proposal placed with, the children's min / ideal / max) is kept.
   */
  private placeLayout(node: LayoutNode, cctx: Context, p: Proposal, x: number, y: number, size: Size): void {
    const children = node.children;
    const measured: LayoutChildSizes[] = children.map((child) => ({
      min: this.measure(child, cctx, MIN_PROPOSAL),
      ideal: this.measure(child, cctx, NIL_PROPOSAL),
      max: this.measure(child, cctx, MAX_PROPOSAL),
    }));
    if (node.id >= 0) {
      this.layouts.set(node.id, { proposal: { width: p.width, height: p.height }, children: measured });
    }

    const raw = node.props['frames'];
    const frames = Array.isArray(raw) ? raw.map(readRect) : null;
    if (frames && frames.length === children.length && frames.every((f) => f !== null)) {
      children.forEach((child, i) => {
        const f = frames[i] as Rect;
        this.place(child, cctx, { width: f.width, height: f.height }, x + f.x, y + f.y);
        this.setFrame(child, x + f.x, y + f.y, f);
      });
      return;
    }
    const proposal: Proposal = { width: size.width, height: size.height };
    for (const child of children) {
      const childSize = this.measure(child, cctx, proposal);
      const { dx, dy } = alignIn(size, childSize, 'center');
      this.place(child, cctx, proposal, x + dx, y + dy);
    }
  }

  // -------------------------------------------------------------------------
  // Grid (Phase 6)
  // -------------------------------------------------------------------------

  /** Context the cells of a grid row (or a full-span child) are measured with. */
  private gridCellContext(grid: LayoutNode, row: LayoutNode | null, ctx: Context): Context {
    const cctx = this.childContext(grid, ctx);
    return row ? this.childContext(row, cctx) : cctx;
  }

  /**
   * SwiftUI's `Grid`: the column count is the widest row (in spans). A column
   * whose non-spanning cells are all flexible (or that has none) is flexible;
   * the others take the largest ideal width of their non-spanning cells. The
   * flexible columns share what is left of the proposal width equally (their
   * ideal width with a nil proposal). A non-`gridrow` child is one full-span
   * cell; one wider than the columns (a `Divider` takes the whole proposal)
   * widens the grid, every column by the same amount (unless it has
   * `gridCellUnsizedAxes(.horizontal)`). Row heights come from
   * measuring every cell at its cell width. A cell's `gridColumnAlignment`
   * aligns its whole column.
   */
  private gridPlan(node: LayoutNode, ctx: Context, p: Proposal): GridPlan {
    return this.plan(node, ctx, p, () => {
      const hSpacing = num(node.props['horizontalSpacing']) ?? DEFAULT_SPACING;
      const vSpacing = num(node.props['verticalSpacing']) ?? DEFAULT_SPACING;
      interface Source {
        node: LayoutNode | null;
        cells: { node: LayoutNode; column: number; span: number }[];
        full: boolean;
      }
      const sources: Source[] = [];
      let columnCount = 1;
      for (const child of node.children) {
        if (child.kind !== 'gridrow') {
          sources.push({ node: null, cells: [{ node: child, column: 0, span: 1 }], full: true });
          continue;
        }
        let column = 0;
        const cells: Source['cells'] = [];
        for (const cell of child.children) {
          const span = gridSpanOf(cell);
          cells.push({ node: cell, column, span });
          column += span;
        }
        columnCount = Math.max(columnCount, column);
        sources.push({ node: child, cells, full: false });
      }

      // Column widths.
      const columns = new Array<number>(columnCount).fill(0);
      const flexible = new Array<boolean>(columnCount).fill(true);
      const maxProposal: Proposal = { width: finite(p.width) ? p.width : Infinity, height: null };
      const threshold = finite(p.width) ? p.width : Infinity;
      for (let j = 0; j < columnCount; j++) {
        let ideal = 0;
        let allFlexible = true;
        let any = false;
        for (const source of sources) {
          if (source.full) continue;
          const cctx = this.gridCellContext(node, source.node, ctx);
          for (const cell of source.cells) {
            if (cell.column !== j || cell.span !== 1) continue;
            any = true;
            ideal = Math.max(ideal, this.measure(cell.node, cctx, NIL_PROPOSAL).width);
            if (this.measure(cell.node, cctx, maxProposal).width < threshold - EPSILON) allFlexible = false;
          }
        }
        columns[j] = ideal;
        flexible[j] = !any || allFlexible;
      }
      if (finite(p.width)) {
        let fixed = 0;
        let flexibleCount = 0;
        for (let j = 0; j < columnCount; j++) {
          if (flexible[j]) flexibleCount++;
          else fixed += columns[j] as number;
        }
        const remaining = Math.max(0, p.width - fixed - hSpacing * (columnCount - 1));
        for (let j = 0; j < columnCount; j++) {
          if (flexible[j]) columns[j] = flexibleCount > 0 ? remaining / flexibleCount : 0;
        }
      }
      // Full-span children wider than the columns widen every column alike.
      const fullProposal: Proposal = { width: finite(p.width) ? p.width : null, height: null };
      let fullWidth = 0;
      for (const source of sources) {
        if (!source.full) continue;
        const cctx = this.gridCellContext(node, null, ctx);
        for (const cell of source.cells) {
          if (cellStyle(cell.node, 'gridCellUnsizedAxes') === true) continue;
          fullWidth = Math.max(fullWidth, this.measure(cell.node, cctx, fullProposal).width);
        }
      }
      const columnsWidth = columns.reduce((sum, w) => sum + w, 0) + hSpacing * (columnCount - 1);
      if (fullWidth > columnsWidth + EPSILON) {
        const extra = (fullWidth - columnsWidth) / columnCount;
        for (let j = 0; j < columnCount; j++) columns[j] = (columns[j] as number) + extra;
      }
      const columnAnchors = new Array<number | null>(columnCount).fill(null);
      for (const source of sources) {
        if (source.full) continue;
        for (const cell of source.cells) {
          const anchor = cell.span === 1 ? gridColumnAnchorOf(cell.node) : null;
          if (anchor !== null && columnAnchors[cell.column] === null) columnAnchors[cell.column] = anchor;
        }
      }
      const columnX = new Array<number>(columnCount).fill(0);
      for (let j = 1; j < columnCount; j++) {
        columnX[j] = (columnX[j - 1] as number) + (columns[j - 1] as number) + hSpacing;
      }
      const gridWidth = columns.reduce((sum, w) => sum + w, 0) + hSpacing * (columnCount - 1);

      // Row heights.
      const rows: GridRowPlan[] = [];
      let y = 0;
      for (const source of sources) {
        const cctx = this.gridCellContext(node, source.node, ctx);
        const cells: GridCellPlan[] = [];
        let height = 0;
        for (const cell of source.cells) {
          let width: number;
          if (source.full) width = gridWidth;
          else {
            const end = Math.min(columnCount, cell.column + cell.span);
            width = 0;
            for (let j = cell.column; j < end; j++) width += columns[j] as number;
            width += hSpacing * Math.max(0, end - cell.column - 1);
          }
          const size = this.measure(cell.node, cctx, { width, height: null });
          height = Math.max(height, size.height);
          cells.push({ node: cell.node, column: cell.column, span: cell.span, width, size, full: source.full });
        }
        rows.push({
          node: source.node,
          cells,
          y,
          height,
          alignment: source.node ? str(source.node.props['alignment']) : null,
        });
        y += height + vSpacing;
      }
      const gridHeight = rows.length > 0 ? y - vSpacing : 0;
      return { size: { width: gridWidth, height: gridHeight }, columns, columnX, columnAnchors, rows };
    });
  }

  private placeGrid(node: LayoutNode, ctx: Context, p: Proposal, x: number, y: number): void {
    const plan = this.gridPlan(node, ctx, p);
    const gridAlignment = str(node.props['alignment']) ?? 'center';
    const [gridX] = alignmentAnchors(gridAlignment);
    for (const row of plan.rows) {
      if (row.node) this.setFrame(row.node, x, y + row.y, { width: plan.size.width, height: row.height });
      const cctx = this.gridCellContext(node, row.node, ctx);
      const rowY = rowAnchor(row.alignment, gridAlignment);
      for (const cell of row.cells) {
        const box: Size = { width: cell.width, height: row.height };
        const columnX = cell.full ? null : (plan.columnAnchors[cell.column] ?? null);
        const [ax, ay] = gridAnchorOf(cell.node) ?? [columnX ?? gridX, rowY];
        const dx = safeOffset(box.width, cell.size.width, ax);
        const dy = safeOffset(box.height, cell.size.height, ay);
        const cellX = cell.full ? 0 : (plan.columnX[cell.column] ?? 0);
        this.place(cell.node, cctx, { width: cell.width, height: null }, x + cellX + dx, y + row.y + dy);
      }
    }
  }

  // -------------------------------------------------------------------------
  // LazyVGrid (Phase 6)
  // -------------------------------------------------------------------------

  /**
   * Columns are resolved for the proposal width: fixed items take their
   * value; flexible and adaptive items share the rest (after the fixed items
   * and the gaps between items) equally, flexible ones clamped to [min, max];
   * an adaptive item then fits as many columns as its share allows,
   * n = max(1, floor((W' + s) / (min + s))), each (W' − (n−1)·s) / n wide,
   * at most its maximum (its minimum only counts the columns: one column of
   * a minimum wider than the share is the share, as in SwiftUI). With no width the flexible columns take the cells' largest ideal
   * width and an adaptive item is one column. Cells fill rows left to right.
   */
  /**
   * `lazyvgrid` and `lazyhgrid`. Written for the vertical grid: its columns
   * share the proposed width and its cells fill them row by row. A horizontal
   * grid is the same along the other axis (its rows share the proposed
   * height and the cells fill them column by column), so the plan's
   * `columns` are the tracks across the grid (`x`, `width`: cross axis) and
   * its `rows` the lines along it (`y`, `height`: main axis).
   */
  private lazyGridPlan(node: LayoutNode, ctx: Context, p: Proposal): LazyGridPlan {
    return this.plan(node, ctx, p, () => {
      const horizontal = node.kind === 'lazyhgrid';
      const crossOf = (size: { width: number | null; height: number | null }): number | null => (horizontal ? size.height : size.width);
      const items = readGridItems(node.props[horizontal ? 'rows' : 'columns']);
      const lineSpacing = num(node.props['spacing']) ?? DEFAULT_SPACING;
      const cctx = this.childContext(node, ctx);
      const cells = node.children;
      const cross = crossOf(p);
      let idealCell: number | null = null;
      const ideal = (): number => {
        if (idealCell === null) {
          idealCell = 0;
          for (const cell of cells) idealCell = Math.max(idealCell, crossOf(this.measure(cell, cctx, NIL_PROPOSAL)) ?? 0);
        }
        return idealCell;
      };
      const widths = items.map((item): number[] => (item.kind === 'fixed' ? [item.value] : []));
      if (!finite(cross)) {
        items.forEach((item, i) => {
          if (item.kind !== 'fixed') widths[i] = [clamp(ideal(), item.minimum, item.maximum)];
        });
      } else {
        let gaps = 0;
        for (let i = 0; i < items.length - 1; i++) gaps += (items[i] as GridItemSpec).spacing;
        let fixed = 0;
        for (const item of items) if (item.kind === 'fixed') fixed += item.value;
        const sharing = items.filter((item) => item.kind !== 'fixed').length;
        const remaining = Math.max(0, cross - fixed - gaps);
        const share = sharing > 0 ? remaining / sharing : 0;
        let flexibleTotal = 0;
        let adaptiveCount = 0;
        items.forEach((item, i) => {
          if (item.kind === 'flexible') {
            const width = clamp(share, item.minimum, item.maximum);
            widths[i] = [width];
            flexibleTotal += width;
          } else if (item.kind === 'adaptive') adaptiveCount++;
        });
        const adaptiveShare = adaptiveCount > 0 ? Math.max(0, remaining - flexibleTotal) / adaptiveCount : 0;
        items.forEach((item, i) => {
          if (item.kind !== 'adaptive') return;
          const s = item.spacing;
          const unit = item.minimum + s;
          const n = unit > 0 ? Math.max(1, Math.floor((adaptiveShare + s) / unit + EPSILON)) : 1;
          const width = clamp((adaptiveShare - (n - 1) * s) / n, null, item.maximum);
          widths[i] = new Array<number>(n).fill(width);
        });
      }

      const columns: LazyColumnPlan[] = [];
      let cursor = 0;
      items.forEach((item, i) => {
        const itemWidths = widths[i] as number[];
        itemWidths.forEach((width, k) => {
          columns.push({ x: cursor, width, alignment: item.alignment });
          cursor += width;
          const last = i === items.length - 1 && k === itemWidths.length - 1;
          if (!last) cursor += item.spacing;
        });
      });
      const used = cursor;
      const gridCross = finite(cross) ? cross : used;
      const alignment = str(node.props['alignment']) ?? 'center';
      const offset = safeOffset(gridCross, used, alignmentAnchors(alignment)[horizontal ? 1 : 0]);
      for (const column of columns) column.x += offset;

      const rows: LazyGridPlan['rows'] = [];
      let y = 0;
      const perRow = Math.max(1, columns.length);
      for (let start = 0; start < cells.length; start += perRow) {
        const rowCells: LazyGridPlan['rows'][number]['cells'] = [];
        let height = 0;
        for (let k = 0; k < perRow && start + k < cells.length; k++) {
          const cell = cells[start + k] as LayoutNode;
          const column = columns[k] as LazyColumnPlan;
          const size = this.measure(cell, cctx, horizontal ? { width: null, height: column.width } : { width: column.width, height: null });
          height = Math.max(height, horizontal ? size.width : size.height);
          rowCells.push({ node: cell, column: k, size });
        }
        rows.push({ y, height, cells: rowCells });
        y += height + lineSpacing;
      }
      const gridMain = rows.length > 0 ? y - lineSpacing : 0;
      const size = horizontal ? { width: gridMain, height: gridCross } : { width: gridCross, height: gridMain };
      return { size, columns, rows };
    });
  }

  private placeLazyGrid(node: LayoutNode, ctx: Context, p: Proposal, x: number, y: number): void {
    const plan = this.lazyGridPlan(node, ctx, p);
    const horizontal = node.kind === 'lazyhgrid';
    const cctx = this.childContext(node, ctx);
    const gridAlignment = str(node.props['alignment']) ?? 'center';
    for (const row of plan.rows) {
      for (const cell of row.cells) {
        const column = plan.columns[cell.column] as LazyColumnPlan;
        const box: Size = horizontal ? { width: row.height, height: column.width } : { width: column.width, height: row.height };
        // Measured without a main-axis size, a cell is placed proposing its
        // row's (as on iOS: Backyard Birds' cards, a `ZStack` of artwork and
        // a `VStack` with a `Spacer`, stretch that stack to the artwork's
        // height, so the header sits at the card's top).
        const size = this.measure(cell.node, cctx, box);
        const { dx, dy } = alignIn(box, size, column.alignment ?? gridAlignment);
        if (horizontal) this.place(cell.node, cctx, box, x + row.y + dx, y + column.x + dy);
        else this.place(cell.node, cctx, box, x + column.x + dx, y + row.y + dy);
      }
    }
  }

  // -------------------------------------------------------------------------
  // Gauge (Phase 6)
  // -------------------------------------------------------------------------

  /**
   * A linear gauge: width = proposal (100 when none), a 4px bar below an
   * optional label row (label leading, current value label trailing, both in
   * the `caption` text style) with a 4px gap. A circular gauge is a 58-point
   * square, whatever the proposal, with the current value label (else the
   * label) centered in the ring, proposed `GAUGE_RING_CENTER` in a 24pt
   * semibold font; an `accessoryCircular` gauge with both labels puts the
   * label in the gap at the bottom of its arc.
   */
  private gaugePlan(node: LayoutNode, ctx: Context, p: Proposal): GaugePlan {
    return this.plan(node, ctx, p, () => {
      const width = finite(p.width) ? p.width : GAUGE_DEFAULT_WIDTH;
      const cctx = this.childContext(node, ctx);
      const labelCtx: Context = { ...cctx, font: resolveFont({ textStyle: 'caption' }, ctx.font, this.env) };
      let index = 0;
      const label = node.props['hasLabel'] === true ? (node.children[index++] ?? null) : null;
      const current = node.props['hasCurrentValueLabel'] === true ? (node.children[index++] ?? null) : null;
      const style = str(node.props['style']);
      if (style === 'accessoryCircular' || style === 'accessoryCircularCapacity') {
        const center = current ?? label;
        const ringCtx: Context = { ...cctx, font: resolveFont(GAUGE_RING_FONT, ctx.font, this.env) };
        const centerSize = center ? this.measure(center, ringCtx, GAUGE_RING_CENTER) : ZERO;
        // With both labels an `accessoryCircular` gauge shows the label too, under the value.
        const arcLabel = style === 'accessoryCircular' && current && label ? label : null;
        const arcLabelSize = arcLabel ? this.measure(arcLabel, labelCtx, GAUGE_ARC_LABEL) : ZERO;
        return {
          size: square(GAUGE_RING_SIZE), label, current, labelSize: arcLabelSize, currentSize: ZERO, labelRow: 0, labelCtx,
          ring: { center, centerSize, ctx: ringCtx, arcLabel },
        };
      }
      const proposal: Proposal = { width, height: null };
      if (style === 'progress') {
        // A ProgressView: the label in the context font above the bar, the
        // current value label (caption) under it.
        const labelSize = label ? this.measure(label, cctx, proposal) : ZERO;
        const currentSize = current ? this.measure(current, labelCtx, proposal) : ZERO;
        const labelRow = label ? labelSize.height + PROGRESS_LABEL_GAP : 0;
        const height = labelRow + GAUGE_BAR_HEIGHT + (current ? PROGRESS_VALUE_GAP + currentSize.height : 0);
        return { size: { width, height }, label, current, labelSize, currentSize, labelRow, labelCtx, labelOwnCtx: cctx };
      }
      const labelSize = label ? this.measure(label, labelCtx, proposal) : ZERO;
      const currentSize = current ? this.measure(current, labelCtx, proposal) : ZERO;
      const labelRow = label || current ? Math.max(labelSize.height, currentSize.height) : 0;
      const height = GAUGE_BAR_HEIGHT + (labelRow > 0 ? labelRow + GAUGE_LABEL_GAP : 0);
      return { size: { width, height }, label, current, labelSize, currentSize, labelRow, labelCtx };
    });
  }

  private placeGauge(node: LayoutNode, ctx: Context, p: Proposal, x: number, y: number, size: Size): void {
    const plan = this.gaugePlan(node, ctx, p);
    if (plan.ring) {
      const { center, centerSize, ctx: ringCtx, arcLabel } = plan.ring;
      if (center) {
        const { dx, dy } = alignIn(size, centerSize, 'center');
        this.place(center, ringCtx, GAUGE_RING_CENTER, x + dx, y + dy);
      }
      if (arcLabel) {
        // Centered in the arc's gap, on the gauge's bottom edge.
        const labelSize = plan.labelSize;
        this.place(arcLabel, plan.labelCtx, GAUGE_ARC_LABEL, x + (size.width - labelSize.width) / 2, y + size.height - labelSize.height);
      }
      // A capacity gauge shows one label, in the middle.
      for (const other of [plan.label, plan.current]) if (other && other !== center && other !== arcLabel) this.hide(other, node);
      this.setChrome(node, { ring: { x, y, width: size.width, height: size.height } });
      return;
    }
    const proposal: Proposal = { width: size.width, height: null };
    if (str(node.props['style']) === 'progress') {
      if (plan.label) this.place(plan.label, plan.labelOwnCtx ?? plan.labelCtx, proposal, x, y);
      const barY = y + plan.labelRow;
      if (plan.current) this.place(plan.current, plan.labelCtx, proposal, x, barY + GAUGE_BAR_HEIGHT + PROGRESS_VALUE_GAP);
      const fraction = Math.min(1, Math.max(0, num(node.props['value']) ?? 0));
      this.setChrome(node, {
        track: { x, y: barY, width: size.width, height: GAUGE_BAR_HEIGHT },
        fill: { x, y: barY, width: size.width * fraction, height: GAUGE_BAR_HEIGHT },
      });
      return;
    }
    if (plan.label) {
      this.place(plan.label, plan.labelCtx, proposal, x, y + (plan.labelRow - plan.labelSize.height) / 2);
    }
    if (plan.current) {
      this.place(
        plan.current,
        plan.labelCtx,
        proposal,
        x + size.width - plan.currentSize.width,
        y + (plan.labelRow - plan.currentSize.height) / 2,
      );
    }
    const value = Math.min(1, Math.max(0, num(node.props['value']) ?? 0));
    const barY = y + (plan.labelRow > 0 ? plan.labelRow + GAUGE_LABEL_GAP : 0);
    this.setChrome(node, {
      track: { x, y: barY, width: size.width, height: GAUGE_BAR_HEIGHT },
      fill: { x, y: barY, width: size.width * value, height: GAUGE_BAR_HEIGHT },
    });
  }

  // -------------------------------------------------------------------------
  // Chart (Phase 7)
  // -------------------------------------------------------------------------

  /**
   * A chart is the proposal on both axes (300 × 200 for nil axes). Its box is
   * split into the legend, the axis bands (as thick as their largest label,
   * measured in the `caption` font or, for `xLabel` / `yLabel` children, at
   * the view's ideal size, plus 6) and the plot; every `chartitem` child is
   * measured at its ideal size and framed against the plot (src/layout/chart.ts).
   */
  private chartPlan(node: LayoutNode, ctx: Context, p: Proposal): ChartPlan {
    return this.plan(node, ctx, p, () => {
      const size = chartSize(p.width, p.height);
      // An infinite proposal reports infinite (the chart is flexible); the
      // geometry is computed for the default size and recomputed when placed.
      const box = chartSize(finite(size.width) ? size.width : null, finite(size.height) ? size.height : null);
      const childCtx = this.childContext(node, ctx);
      const font = resolveFont({ textStyle: 'caption' }, ctx.font, this.env);
      const measureWidth = (text: string): number => this.measurer.measureWidth(text, font);

      // Children at their ideal size, by role.
      const items: ChartItemPlan[] = node.children.map((child) => {
        const role = str(child.props['role']) ?? 'annotation';
        const childSize = this.measure(child, childCtx, NIL_PROPOSAL);
        return { node: child, role, size: childSize, frame: { x: 0, y: 0, ...childSize } };
      });
      const xViews = items.filter((item) => item.role === 'xLabel');
      const yViews = items.filter((item) => item.role === 'yLabel');

      const axisInput = (axis: unknown, fallback: string, views: ChartItemPlan[], key: 'x0' | 'y0'): ChartAxisInput | null => {
        const a = obj(axis);
        if (!a) return null;
        const labels: ChartAxisInput['labels'] = [];
        const rawLabels = Array.isArray(a['labels']) ? (a['labels'] as unknown[]) : [];
        for (const raw of rawLabels) {
          const label = obj(raw);
          if (!label) continue;
          const text = str(label['text']) ?? '';
          labels.push({ at: num(label['at']) ?? 0, width: measureWidth(text), leading: label['anchor'] === 'leading' });
        }
        return {
          position: (str(a['position']) ?? fallback) as ChartAxisInput['position'],
          labels,
          labelHeight: font.lineHeight,
          views: views.map((view) => ({ at: num(view.node.props[key]) ?? 0, size: view.size })),
        };
      };
      const xAxis = axisInput(node.props['xAxis'], 'bottom', xViews, 'x0');
      const yAxis = axisInput(node.props['yAxis'], 'trailing', yViews, 'y0');

      let legend: ChartLegendInput | null = null;
      const rawLegend = obj(node.props['legend']);
      if (rawLegend) {
        const rawItems = Array.isArray(rawLegend['items']) ? (rawLegend['items'] as unknown[]) : [];
        const itemWidths = rawItems.map((raw) => {
          const item = obj(raw);
          const glyph = legendGlyphSize(item ? str(item['symbol']) : null);
          // Whole pixels: the item's rect is snapped down, which must not clip the label.
          return Math.ceil(glyph.width + CHART_LEGEND_GAP + measureWidth(item ? (str(item['label']) ?? '') : ''));
        });
        legend = {
          position: (str(rawLegend['position']) ?? 'top') as ChartLegendInput['position'],
          itemWidths,
          itemHeight: Math.max(font.lineHeight, CHART_LEGEND_GLYPH),
        };
      }

      const geometry = layoutChart({ width: box.width, height: box.height, xAxis, yAxis, legend });

      // Frames (relative to the chart origin) for every child.
      xViews.forEach((item, i) => {
        item.frame = geometry.xViews[i] ?? item.frame;
      });
      yViews.forEach((item, i) => {
        item.frame = geometry.yViews[i] ?? item.frame;
      });
      for (const item of items) {
        if (item.role === 'xLabel' || item.role === 'yLabel') continue;
        const props = item.node.props;
        const R = unitRect(
          geometry.plot,
          num(props['x0']) ?? 0,
          num(props['x1']) ?? 0,
          num(props['y0']) ?? 0,
          num(props['y1']) ?? 0,
        );
        const position = (str(props['position']) ?? 'overlay') as ChartItemPosition;
        item.frame = annotationFrame(R, item.size, position, str(props['alignment']), num(props['spacing']) ?? 0);
      }

      const labelTexts = (axis: unknown): string[] => {
        const a = obj(axis);
        const raw = a && Array.isArray(a['labels']) ? (a['labels'] as unknown[]) : [];
        return raw.filter((l) => obj(l) !== null).map((l) => str((l as Record<string, unknown>)['text']) ?? '');
      };
      return {
        size,
        geometry,
        font,
        items,
        xLabelTexts: labelTexts(node.props['xAxis']),
        yLabelTexts: labelTexts(node.props['yAxis']),
        childCtx,
      };
    });
  }

  private placeChart(node: LayoutNode, ctx: Context, p: Proposal, x: number, y: number): void {
    const plan = this.chartPlan(node, ctx, p);
    const shift = (r: Rect): Rect => ({ x: x + r.x, y: y + r.y, width: r.width, height: r.height });
    for (const item of plan.items) {
      this.place(item.node, plan.childCtx, NIL_PROPOSAL, x + item.frame.x, y + item.frame.y);
    }
    const g = plan.geometry;
    const chrome: Record<string, Rect> = { plot: shift(g.plot) };
    if (g.legend) chrome['legend'] = shift(g.legend);
    if (g.xBand) chrome['xband'] = shift(g.xBand);
    if (g.yBand) chrome['yband'] = shift(g.yBand);
    const texts: Record<string, TextLayout> = {};
    const xAlign: TextAlignment = 'center';
    const yAlign: TextAlignment = g.yBand && g.yBand.x < g.plot.x ? 'trailing' : 'leading';
    g.xLabels.forEach((rect, i) => {
      chrome[`xlabel${i}`] = shift(rect);
      texts[`xlabel${i}`] = { font: plan.font, lines: [plan.xLabelTexts[i] ?? ''], alignment: xAlign, truncated: false };
    });
    g.yLabels.forEach((rect, i) => {
      chrome[`ylabel${i}`] = shift(rect);
      texts[`ylabel${i}`] = { font: plan.font, lines: [plan.yLabelTexts[i] ?? ''], alignment: yAlign, truncated: false };
    });
    g.legendItems.forEach((rect, i) => {
      chrome[`legend${i}`] = shift(rect);
    });
    this.setChrome(node, chrome);
    if (node.id >= 0) {
      this.chromeText.set(node.id, texts);
      this.fonts.set(node.id, plan.font);
    }
  }
}

export function layoutTree(
  roots: readonly LayoutNode[],
  env: LayoutEnvironment,
  measurer: TextMeasurer,
): LayoutResult {
  return new LayoutPass(env, measurer).run(roots);
}
